import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, RefreshCcw, Loader2, AlertCircle, Clock, AlertTriangle,
  CheckCircle, DollarSign, Mail, Send, X, TrendingUp, TrendingDown,
  Users, Download, Wallet, Stethoscope, Link2, Wrench, Trash2, Activity,
  Calculator, Copy, XCircle,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { formatAdminError } from "../lib/adminError";
import { PaymentProofsPanel } from "../components/PaymentProofsPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: "claimed", label: "Claimed", icon: Clock, color: "amber" },
  { id: "unpaid", label: "Unpaid", icon: AlertCircle, color: "slate" },
  { id: "disputed", label: "Disputed", icon: AlertTriangle, color: "red" },
  { id: "partial", label: "Partial", icon: TrendingDown, color: "orange" },
  { id: "approved", label: "Approved", icon: CheckCircle, color: "emerald" },
  { id: "all", label: "All", icon: Users, color: "blue" },
];

function ageToHumanReadable(sec) {
  if (sec === null || sec === undefined) return "—";
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

function csvEscape(val) {
  if (val === null || val === undefined) return "";
  const s = String(val);
  if (s.includes('"') || s.includes(",") || s.includes("\n") || s.includes("\r")) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

// Build a compact, copy-friendly single-block note for the bank transaction
// Note field. Format is deliberately terse — bank notes have tight char limits.
//
// Single-child (with discount):
//   Raoul Edmundo Varona (1x/wk × 9wks)
//   $926.37 less DM disc = $833.73
//
// Single-child (no discount):
//   Raoul Varona (2x/wk × 9wks) = $618.00
//
// Multi-child:
//   Alice (2x/wk × 9wks) $618 - DM 10% = $556.20
//   Bob   (1x/wk × 9wks) $309 - DM 10% = $278.10
//   Total = $834.30
function buildBankNote(fb, detail) {
  if (!fb) return "";
  const rawItems = (fb.line_items || []).filter((li) => !li.kind || li.kind !== "digital_membership");
  const addons = (fb.line_items || []).filter((li) => li.kind === "digital_membership");
  const total = typeof fb.total === "number" ? fb.total : null;
  const weeks = fb.term_weeks;
  const termLabel = (detail?.term_name || detail?.programme_type || "").trim();

  // Partition per-child rows (have child_name AND either sessions_per_week
  // or a breakdown sub-dict) vs flat fee lines (e.g. camp fees, sign-ups)
  const items = rawItems.filter((li) => {
    const looksLikeChild = li.child_name
      && (li.sessions_per_week > 0
          || (li.breakdown && Object.keys(li.breakdown).length > 0)
          || (typeof li.child_total === "number" && li.child_total > 0));
    return looksLikeChild;
  });
  const flatLines = rawItems.filter((li) => !items.includes(li));

  const childLines = items.map((li) => {
    const name = li.child_name || "Child";
    const spw = li.sessions_per_week || 0;
    const sub = li.breakdown || {};
    const gross = Number(
      sub.subtotal
        ?? (typeof li.amount === "number" ? li.amount : undefined)
        ?? li.session_subtotal
        ?? 0,
    );
    const finalTotal = Number(sub.final_total ?? li.child_total ?? 0);
    const regFee = Number(li.registration_fee || 0);
    const memberAmt = Number(sub.membership_discount_amount || 0);
    const siblingAmt = Number(sub.sibling_discount_amount || 0);
    const volumeAmt = Number(sub.volume_discount_amount || 0);
    const memberPct = Number(li.discount_pct || 0);
    const siblingPct = Number(li.sibling_discount_pct || 0);
    const hasFullBreakdown = (
      "membership_discount_amount" in sub
      || "sibling_discount_amount" in sub
      || "volume_discount_amount" in sub
      || "subtotal" in sub
    );
    const impliedDiscount = (
      !hasFullBreakdown
      && gross > 0
      && finalTotal > 0
      && gross - regFee - finalTotal >= 0.05
    ) ? Math.round((gross - regFee - finalTotal) * 100) / 100 : 0;
    const impliedPct = impliedDiscount > 0 && gross > 0
      ? Math.round((impliedDiscount / gross) * 100) : 0;

    const dim = spw > 0
      ? `(${spw}x/wk × ${weeks || "?"}wks)`
      : "";

    const discParts = [];
    if (memberAmt > 0) discParts.push(`-DM${memberPct > 0 ? ` ${memberPct.toFixed(0)}%` : ""}`);
    if (siblingAmt > 0) discParts.push(`-Sib${siblingPct > 0 ? ` ${siblingPct.toFixed(0)}%` : ""}`);
    if (volumeAmt > 0) discParts.push(`-Vol`);
    if (impliedDiscount > 0) discParts.push(`-DM${impliedPct > 0 ? ` ${impliedPct}%` : ""}`);
    const regPart = regFee > 0 ? ` +$${regFee.toFixed(2)} signup` : "";

    if (discParts.length === 0 && regPart === "") {
      // No discount, no signup — one-liner
      return `${name} ${dim} = $${finalTotal.toFixed(2)}`.trim();
    }
    if (items.length === 1) {
      // Single-child with discount — 2-line format
      const line1 = `${name} ${dim}`.trim();
      const line2 = `$${gross.toFixed(2)}${regPart} ${discParts.join(" ")} = $${finalTotal.toFixed(2)}`;
      return `${line1}\n${line2}`;
    }
    // Multi-child compact
    return `${name} ${dim} $${gross.toFixed(2)}${regPart} ${discParts.join(" ")} = $${finalTotal.toFixed(2)}`;
  });

  const addonLines = addons.map((li) => {
    const amt = Number(li.amount ?? li.child_total ?? 0);
    const label = li.label || "Digital Membership";
    return `${label} = ${amt < 0 ? "-" : ""}$${Math.abs(amt).toFixed(2)}`;
  });

  const flatFeeLines = flatLines.map((li) => {
    const amt = Number(li.amount ?? li.child_total ?? 0);
    const label = li.label || li.description || li.child_name || "Fee";
    return `${label} = $${amt.toFixed(2)}`;
  });

  const parts = [...childLines, ...flatFeeLines, ...addonLines];
  if (parts.length === 0) return "";
  if ((items.length + flatLines.length + addons.length) > 1 && total !== null) {
    parts.push(`Total = $${total.toFixed(2)}`);
  }
  if (termLabel) {
    parts.push(`(${termLabel})`);
  }
  return parts.join("\n");
}

function copyToClipboardFallback(text) {
  try {
    if (navigator.clipboard?.writeText) {
      return navigator.clipboard.writeText(text);
    }
  } catch (_e) { /* fall through */ }
  // Fallback for insecure contexts
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.style.position = "fixed";
  ta.style.opacity = "0";
  document.body.appendChild(ta);
  ta.select();
  try { document.execCommand("copy"); } catch (_e) { /* noop */ }
  document.body.removeChild(ta);
  return Promise.resolve();
}


function exportRowsAsCsv(rows, tabId) {
  const headers = [
    "Reference", "Parent Name", "Parent Email", "Parent Phone",
    "SCC Member", "Member Type", "Children Count", "Children Names",
    "Amount Invoiced", "Amount Received", "Diff",
    "Base Fee", "Registration Fee", "Sibling Discount",
    "Member Discount", "Member Discount Type",
    "Digital Membership", "Credits Applied",
    "Status", "Raw Status", "SLA", "Age (hours)",
    "Claimed At", "Created At", "Bank Name Declared", "Bank Reference",
    "Fee Mismatch", "Fee Mismatch Delta",
  ];
  const lines = [headers.map(csvEscape).join(",")];
  for (const r of rows) {
    const p = r.parent || {};
    const ageHours = r.age_seconds !== null && r.age_seconds !== undefined
      ? (r.age_seconds / 3600).toFixed(1) : "";
    const childNames = (r.children || []).map((c) => c.name).filter(Boolean).join(" | ");

    // Pull fee-breakdown components — fall back to child_details aggregation
    // when top-level fields are missing (legacy txns).
    const fb = r.fee_breakdown || {};
    const childDetails = fb.child_details || [];
    const num = (v) => {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : 0;
    };
    const regFeeTop = num(fb.registration_fee);
    const regFeeAgg = childDetails.reduce((s, cd) => s + num(cd.registration_fee), 0);
    const registrationFee = regFeeTop > 0 ? regFeeTop : regFeeAgg;
    const memberDiscountTop = num(fb.member_discount_amount || fb.membership_discount_amount);
    const memberDiscountAgg = childDetails.reduce(
      (s, cd) => s + num((cd.breakdown || {}).membership_discount_amount), 0,
    );
    const memberDiscount = memberDiscountTop > 0 ? memberDiscountTop : memberDiscountAgg;
    let memberDiscountType = "";
    if (memberDiscount > 0) {
      const mt = String(p.member_type || "").toLowerCase();
      if (mt === "section" || mt === "life") memberDiscountType = "section";
      else if (p.is_digital_member && !p.scc_member_verified) memberDiscountType = "digital";
      else if (p.scc_member_verified || p.is_scc_member) memberDiscountType = "club";
      else memberDiscountType = fb.member_discount_type || "";
    }
    const dmPrice = num(fb.dm_price);
    const siblingDiscount = num(fb.sibling_discount);
    // Credits applied: sum negative line_items labelled "Credit applied" + any credit_applied/credit_consumed on row
    const creditFromLines = (fb.line_items || [])
      .filter((li) => /credit applied/i.test(li.label || ""))
      .reduce((s, li) => s + Math.abs(num(li.amount)), 0);
    const creditOnRow = num(r.credit_applied || r.credit_consumed);
    const creditsApplied = creditFromLines + creditOnRow;

    const fmt = (v) => (v && v !== 0 ? Number(v).toFixed(2) : "");

    lines.push([
      r.reference_internal, p.name || p.display_name, p.email, p.phone,
      p.is_scc_member ? "Y" : "N", p.member_type,
      r.children_count, childNames,
      (r.amount_invoiced ?? "").toString(),
      r.amount_received !== null && r.amount_received !== undefined ? r.amount_received.toString() : "",
      r.diff !== null && r.diff !== undefined ? r.diff.toString() : "",
      fmt(fb.base_fee),
      fmt(registrationFee),
      fmt(siblingDiscount),
      fmt(memberDiscount),
      memberDiscountType,
      fmt(dmPrice),
      fmt(creditsApplied),
      r.status, r.raw_status, r.sla, ageHours,
      r.claimed_at, r.created_at, r.bank_account_holder_name, r.reference_bank,
      r.fee_mismatch_detected ? "Y" : "N", r.fee_mismatch_delta,
    ].map(csvEscape).join(","));
  }
  const csv = lines.join("\n");
  const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `payments-${tabId}-${ts}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  return lines.length - 1;
}

function slaBadge(sla) {
  if (sla === "breach") return <span className="inline-flex items-center gap-1 text-xs bg-red-500/20 text-red-300 px-2 py-0.5 rounded border border-red-500/40"><AlertTriangle className="w-3 h-3" /> SLA breach</span>;
  if (sla === "warn") return <span className="inline-flex items-center gap-1 text-xs bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded border border-amber-500/40"><Clock className="w-3 h-3" /> SLA warn</span>;
  return null;
}

function statusBadge(status) {
  const cls = {
    claimed: "bg-amber-500/20 text-amber-300 border-amber-500/40",
    unpaid: "bg-slate-500/20 text-slate-300 border-slate-500/40",
    approved: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
    disputed: "bg-red-500/20 text-red-300 border-red-500/40",
    partial: "bg-orange-500/20 text-orange-300 border-orange-500/40",
    overpaid: "bg-teal-500/20 text-teal-300 border-teal-500/40",
    rejected: "bg-red-900/30 text-red-400 border-red-900/50",
    credited: "bg-lime-500/20 text-lime-300 border-lime-500/40",
  }[status] || "bg-slate-700 text-slate-300";
  return <span className={`text-xs px-2 py-0.5 rounded border ${cls}`}>{status}</span>;
}

export default function AdminPaymentsHub() {
  const [tab, setTab] = useState("claimed");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Term filter for the Breakdown CSV export (Jun 2026)
  const [csvTermId, setCsvTermId] = useState(""); // "" = All terms
  const [csvTermList, setCsvTermList] = useState([]);
  const [selected, setSelected] = useState(null);  // txn id for drawer
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditData, setAuditData] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [hygieneOpen, setHygieneOpen] = useState(false);
  const [hygieneData, setHygieneData] = useState(null);
  const [hygieneLoading, setHygieneLoading] = useState(false);
  const [doctorOpen, setDoctorOpen] = useState(false);
  const [doctorQuery, setDoctorQuery] = useState("");
  const [doctorData, setDoctorData] = useState(null);
  const [doctorLoading, setDoctorLoading] = useState(false);
  const [specialDiagOpen, setSpecialDiagOpen] = useState(false);
  const [specialDiagData, setSpecialDiagData] = useState(null);
  const [specialDiagLoading, setSpecialDiagLoading] = useState(false);
  const [leakOpen, setLeakOpen] = useState(false);
  // Mobile: the diagnostics cluster is desktop-only by default (20+ buttons)
  const [toolsOpen, setToolsOpen] = useState(false);
  const [leakLoading, setLeakLoading] = useState(false);
  const [leakData, setLeakData] = useState(null);
  const [t1Open, setT1Open] = useState(false);
  const [t1Loading, setT1Loading] = useState(false);
  const [t1Data, setT1Data] = useState(null);
  const [mismatchCount, setMismatchCount] = useState(null);  // null = not loaded, 0 = clean, N = visible
  const [testAcctOpen, setTestAcctOpen] = useState(false);
  const [testAcctData, setTestAcctData] = useState(null);
  const [testAcctLoading, setTestAcctLoading] = useState(false);
  const [testAcctSelected, setTestAcctSelected] = useState(new Set());
  // Relink-transaction-parent modal (Feb 2026 — replaces ad-hoc Mongo-shell patches)
  const [relinkOpen, setRelinkOpen] = useState(false);
  const [relinkBusy, setRelinkBusy] = useState(false);
  const [relinkForm, setRelinkForm] = useState({
    txn_id: "",
    new_parent_id: "",
    reason: "",
    update_user_id: true,
  });
  const [relinkPlan, setRelinkPlan] = useState(null);
  // Backfill missing registration_id 2-step UI (Feb 2026)
  const [backfillOpen, setBackfillOpen] = useState(false);
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillData, setBackfillData] = useState(null);    // inspect response
  const [backfillSelections, setBackfillSelections] = useState({
    ambiguousChoice: {},        // { txn_id: registration_id }
    noMatchMarkLegacy: {},      // { txn_id: true|false }
  });
  const [backfillResult, setBackfillResult] = useState(null);
  const [backfillBusy, setBackfillBusy] = useState(false);
  // Archive rejected orphan registrations modal (Feb 2026)
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [archiveData, setArchiveData] = useState(null);  // GET response
  const [archiveSelections, setArchiveSelections] = useState({}); // { reg_id: true|false }
  const [archiveResult, setArchiveResult] = useState(null);
  const [archiveBusy, setArchiveBusy] = useState(false);
  // DM Health diagnostic (Feb 2026 — revenue leakage detector)
  const [dmHealthOpen, setDmHealthOpen] = useState(false);
  const [dmHealthLoading, setDmHealthLoading] = useState(false);
  const [dmHealthData, setDmHealthData] = useState(null);
  // Orphan/Duplicate Sweeper (Aug 2026 — retires manual cleanup; approved-twin-only, reversible)
  const [sweeperOpen, setSweeperOpen] = useState(false);
  const [sweeperLoading, setSweeperLoading] = useState(false);
  const [sweeperData, setSweeperData] = useState(null);
  const [sweeperBusy, setSweeperBusy] = useState(false);
  const [sweeperArchived, setSweeperArchived] = useState([]); // [{id, ref, amount, keeper_id}]
  // Duplicate Players merger (Feb 2026 — cleans up dup rows from old admin register-for-parent bug)
  const [dupPlayersOpen, setDupPlayersOpen] = useState(false);
  const [dupPlayersLoading, setDupPlayersLoading] = useState(false);
  const [dupPlayersData, setDupPlayersData] = useState(null);
  const [dupKeepSelection, setDupKeepSelection] = useState({}); // { groupKey: keepPlayerId }
  const [dupMergeBusy, setDupMergeBusy] = useState(false);
  const [dupMergeResult, setDupMergeResult] = useState(null);
  // Find Player by Name lookup (Feb 2026 — surfaces dup children with mismatched birthdates that grouping misses)
  const [findPlayerOpen, setFindPlayerOpen] = useState(false);
  const [findPlayerLoading, setFindPlayerLoading] = useState(false);
  const [findPlayerData, setFindPlayerData] = useState(null);
  const [findPlayerGiven, setFindPlayerGiven] = useState("");
  const [findPlayerLast, setFindPlayerLast] = useState("");
  // Schema drift audit (Feb 2026 — backend live at /admin/diagnostic/schema-drift; frontend modal deferred until pro-rata audit is decided)
  // Pro-rata audit (Feb 2026 — overcharge calculator using week-based formula)
  const [prorataOpen, setProrataOpen] = useState(false);
  const [prorataLoading, setProrataLoading] = useState(false);
  const [prorataData, setProrataData] = useState(null);
  const [prorataExpanded, setProrataExpanded] = useState({});
  const [prorataTerms, setProrataTerms] = useState([]);
  const [prorataSelectedTermId, setProrataSelectedTermId] = useState("");
  const [prorataTermsLoading, setProrataTermsLoading] = useState(false);
  // Force-merge state (Feb 2026 — dry-run preview + confirm-token execution)
  const [forceMergeKeep, setForceMergeKeep] = useState(null);          // player_id of the keep
  const [forceMergeArchive, setForceMergeArchive] = useState({});      // { player_id: true|false }
  const [forceMergeReason, setForceMergeReason] = useState("");
  const [forceMergePreview, setForceMergePreview] = useState(null);    // dry-run response (with confirm_token)
  const [forceMergeBusy, setForceMergeBusy] = useState(false);
  const [forceMergeResult, setForceMergeResult] = useState(null);      // final execute response
  // Reassign-parent state (Aug 2026 — one-click fix for kids whose
  // parent_id points at an archived placeholder after a stale merge)
  const [reassignFor, setReassignFor] = useState(null);               // player row being reassigned
  const [reassignQuery, setReassignQuery] = useState("");
  const [reassignResults, setReassignResults] = useState([]);
  const [reassignBusy, setReassignBusy] = useState(false);
  // Fee-breakdown drift (Feb 2026 — find + repair registrations where parent is verified member but fb has $0 discount)
  const [driftOpen, setDriftOpen] = useState(false);
  const [driftLoading, setDriftLoading] = useState(false);
  const [driftData, setDriftData] = useState(null);
  const [driftRepairBusy, setDriftRepairBusy] = useState(false);
  const [driftRepairResult, setDriftRepairResult] = useState(null);
  // Auto-cancel stale (Feb 2026 — preview + commit, 14-day threshold, 20/run cap)
  const [autoCancelOpen, setAutoCancelOpen] = useState(false);
  const [autoCancelLoading, setAutoCancelLoading] = useState(false);
  const [autoCancelData, setAutoCancelData] = useState(null);
  const [autoCancelBusy, setAutoCancelBusy] = useState(false);
  const [autoCancelResult, setAutoCancelResult] = useState(null);
  // Bulk Auto-Match (Feb 2026 — 1-click verifier for SLA-breached claimed payments)
  const [autoMatchOpen, setAutoMatchOpen] = useState(false);
  const [autoMatchLoading, setAutoMatchLoading] = useState(false);
  const [autoMatchData, setAutoMatchData] = useState(null);  // { sla_filter, total, rows }
  const [autoMatchSla, setAutoMatchSla] = useState("breach");
  const [autoMatchBusyTxn, setAutoMatchBusyTxn] = useState(null);  // txn_id currently being approved

  // Category filter (Jun 2026) — narrows queue to camp/tournament/term/etc.
  // so admins can ringfence camp money separately from term money.
  const [categoryFilter, setCategoryFilter] = useState(""); // "" = all categories

  const load = useCallback(async (tabId) => {
    setLoading(true);
    try {
      const params = { status: tabId };
      if (categoryFilter) params.category = categoryFilter;
      const res = await axios.get(`${API}/payments-hub/queue`, {
        params,
        withCredentials: true,
      });
      setData(res.data);
    } catch (err) {
      { const __e = formatAdminError(err, "Failed to load"); toast.error(__e.message, __e.opts); };
    } finally {
      setLoading(false);
    }
  }, [categoryFilter]);

  // Bulk Auto-Match — fetches top bank suggestion per breached claim so admin
  // can 1-click approve a queue of SLA-breached payments instead of opening
  // each drawer individually.
  const loadAutoMatch = useCallback(async (sla = "breach") => {
    setAutoMatchLoading(true);
    setAutoMatchSla(sla);
    try {
      const res = await axios.get(`${API}/payments-hub/bank-suggest-bulk`, {
        params: { sla },
        withCredentials: true,
      });
      setAutoMatchData(res.data);
    } catch (err) {
      const __e = formatAdminError(err, "Auto-match failed");
      toast.error(__e.message, __e.opts);
      setAutoMatchData({ rows: [], total: 0, sla_filter: sla, error: true });
    } finally {
      setAutoMatchLoading(false);
    }
  }, []);

  const approveWithBankMatch = useCallback(async (row) => {
    const sug = row.top_suggestion;
    if (!sug) return;
    setAutoMatchBusyTxn(row.txn_id);
    try {
      const body = {
        payment_reference_number: sug.bank_transaction_id || sug.reference_raw || sug.reference || "",
        payee_bank_account_name: sug.source_name || "",
        notes: `Auto-matched via bank-import (${sug.confidence}) — ${sug.reason}`,
      };
      await axios.post(`${API}/payments/admin/approve/${row.txn_id}`, body, { withCredentials: true });
      toast.success(`Approved ${row.payment_reference}`);
      // Refresh both the auto-match panel and the main queue
      await Promise.all([loadAutoMatch(autoMatchSla), load(tab)]);
    } catch (err) {
      const __e = formatAdminError(err, "Approve failed");
      toast.error(__e.message, __e.opts);
    } finally {
      setAutoMatchBusyTxn(null);
    }
  }, [autoMatchSla, loadAutoMatch, load, tab]);

  const runSiblingAudit = useCallback(async () => {
    setAuditLoading(true);
    setAuditOpen(true);
    try {
      const res = await axios.get(`${API}/payments-hub/admin/diagnostic/sibling-discount-audit`, {
        params: { days: 90, only_suspicious: false },
        withCredentials: true,
      });
      setAuditData(res.data);
    } catch (err) {
      { const __e = formatAdminError(err, "Failed to run audit"); toast.error(__e.message, __e.opts); };
      setAuditData({ error: err.response?.data?.detail || String(err) });
    } finally {
      setAuditLoading(false);
    }
  }, []);

  const runHygieneAudit = useCallback(async () => {
    setHygieneLoading(true);
    setHygieneOpen(true);
    try {
      const res = await axios.get(`${API}/payments-hub/admin/diagnostic/data-hygiene-audit`, {
        withCredentials: true,
      });
      setHygieneData(res.data);
    } catch (err) {
      { const __e = formatAdminError(err, "Failed to run hygiene audit"); toast.error(__e.message, __e.opts); };
      setHygieneData({ error: err.response?.data?.detail || String(err) });
    } finally {
      setHygieneLoading(false);
    }
  }, []);

  const runHygieneBackfill = useCallback(async (dryRun) => {
    if (!dryRun && !window.confirm("This will:\n• Backfill amount_invoiced on all 92 affected rows\n• Add an admin note documenting the SCC-20260425-0003 underpayment waiver\n• DELETE 3 confirmed test-data orphans (parenteestt@parent.com, andy@pandy.com, gah@gah.com)\n\nReal-money orphans and the ADHOC-20260104-6183CE row will NOT be touched — they will be surfaced for review.\n\nProceed?")) {
      return;
    }
    setHygieneLoading(true);
    try {
      const res = await axios.post(
        `${API}/payments-hub/admin/run-data-hygiene-backfill?dry_run=${dryRun ? "true" : "false"}`,
        {},
        { withCredentials: true },
      );
      setHygieneData({ ...res.data, _is_backfill_result: true });
      toast.success(dryRun ? "Dry-run complete — review the plan below" : "Backfill complete");
    } catch (err) {
      { const __e = formatAdminError(err, "Backfill failed"); toast.error(__e.message, __e.opts); };
      setHygieneData({ error: err.response?.data?.detail || String(err) });
    } finally {
      setHygieneLoading(false);
    }
  }, []);

  // Bundled "Run all confirmed cleanups" — executes hygiene + Sarah cascade
  // + abandoned Stripe in sequence with a single confirm token.
  const runAllConfirmedCleanups = useCallback(async (dryRun) => {
    if (!dryRun && !window.confirm(
      "EXECUTE all three confirmed cleanups in sequence?\n\n" +
      "1. Data hygiene backfill (idempotent)\n" +
      "2. Sarah Gah cascade delete\n" +
      "3. Abandoned Stripe DM session cleanup\n\n" +
      "This is irreversible. Review the dry-run totals above before confirming."
    )) {
      return;
    }
    setHygieneLoading(true);
    try {
      const res = await axios.post(
        `${API}/payments-hub/admin/run-all-confirmed-cleanups`,
        {
          dry_run: !!dryRun,
          confirm_token: dryRun ? null : "RUN_ALL_CONFIRMED_CLEANUPS_APR2026",
        },
        { withCredentials: true },
      );
      setHygieneData({ ...res.data, _is_bundle_result: true });
      if (dryRun) {
        toast.success("Bundled dry-run complete — review combined totals below");
      } else {
        toast.success(`Bundle executed: ${res.data?.summary?.total_writes ?? 0} writes, ${res.data?.summary?.errors ?? 0} errors`);
      }
    } catch (err) {
      { const __e = formatAdminError(err, "Bundle run failed"); toast.error(__e.message, __e.opts); };
      setHygieneData({ error: err.response?.data?.detail || String(err) });
    } finally {
      setHygieneLoading(false);
    }
  }, []);


  const runDoctor = async (queryStr) => {
    const q = (queryStr || doctorQuery).trim();
    if (!q) {
      toast.error("Enter a name, email, or membership number");
      return;
    }
    setDoctorLoading(true);
    setDoctorOpen(true);
    try {
      const res = await axios.get(`${API}/payments-hub/admin/diagnostic/registration-doctor`, {
        params: { q },
        withCredentials: true,
      });
      setDoctorData(res.data);
    } catch (err) {
      { const __e = formatAdminError(err, "Doctor lookup failed"); toast.error(__e.message, __e.opts); };
      setDoctorData({ error: err.response?.data?.detail || String(err) });
    } finally {
      setDoctorLoading(false);
    }
  };

  useEffect(() => { load(tab); }, [tab, load]);

  // Load the term list once on mount for the Breakdown CSV term filter
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/academy/terms`, { withCredentials: true })
      .then((r) => {
        if (cancelled) return;
        const arr = Array.isArray(r.data) ? r.data : [];
        // Filter to terms with an id + name; preserve the server's sort
        // (most recent first by start_date).
        setCsvTermList(arr.filter(t => t?.id && (t.name || t.term_name)));
      })
      .catch(() => { if (!cancelled) setCsvTermList([]); });
    return () => { cancelled = true; };
  }, []);

  // Feb 2026 audit fix — when admin arrives at /admin/payments?q=<email>
  // (e.g. from the legacy PaymentTracking "Open in Payments Hub" link),
  // auto-open the Doctor pre-filled with that parent so they land directly
  // on the parent's records instead of having to re-search by hand.
  // This is the read-only "filter by parent" bridge requested in the audit.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const q = params.get("q");
    if (q && q.trim()) {
      setDoctorQuery(q);
      runDoctor(q);
    }
  }, []);

  // Lightweight mismatch-count check on mount — surfaces the same Term 1 desync
  // class of bug if it ever recurs in Term 2/3.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/payments-hub/admin/diagnostic/term1-paid-pending-desync`, {
      params: { include_full_drilldown: false },
      withCredentials: true,
    }).then((r) => {
      if (!cancelled) setMismatchCount(r.data?.desync_count ?? 0);
    }).catch(() => {
      if (!cancelled) setMismatchCount(0);  // silent on error — never block page load
    });
    return () => { cancelled = true; };
  }, []);

  const openT1Modal = async () => {
    setT1Loading(true);
    setT1Open(true);
    try {
      const r = await axios.get(
        `${API}/payments-hub/admin/diagnostic/term1-paid-pending-desync`,
        { params: { include_full_drilldown: true }, withCredentials: true },
      );
      setT1Data(r.data);
    } catch (err) {
      { const __e = formatAdminError(err, "Diagnostic failed"); toast.error(__e.message, __e.opts); };
      setT1Data({ error: err.response?.data?.detail || String(err) });
    } finally {
      setT1Loading(false);
    }
  };

  const rows = data?.rows || [];
  const counts = data?.counts || {};
  const sla = data?.sla || { warn_count: 0, breach_count: 0 };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-950 to-black text-slate-100 p-4 md:p-8">
      <div className="max-w-[1600px] mx-auto">
        <Link to="/admin" className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-6" data-testid="back-to-admin">
          <ArrowLeft className="w-4 h-4" /> Back to Admin Panel
        </Link>

        {/* Paid-signal/status mismatch banner — surfaces Term 1 desync class of bug */}
        {mismatchCount > 0 && (
          <button
            type="button"
            onClick={openT1Modal}
            data-testid="mismatch-banner"
            className="w-full mb-4 bg-amber-500/10 border border-amber-500/40 hover:bg-amber-500/15 transition-colors rounded-lg px-4 py-3 flex items-center justify-between gap-3 text-left"
          >
            <span className="flex items-center gap-2 text-amber-200">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm font-medium">
                ⚠ <strong>{mismatchCount}</strong> paid/status mismatch{mismatchCount === 1 ? "" : "es"} detected — click <span className="underline">Term 1 desync</span> to review.
              </span>
            </span>
            <span className="text-amber-300 text-xs font-mono opacity-70 hover:opacity-100">Open →</span>
          </button>
        )}

        <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-white flex items-center gap-3">
              <DollarSign className="w-9 h-9 text-emerald-400" />
              Payments
            </h1>
            <p className="text-slate-400 mt-1">Single source of truth — verify, dispute, credit. All in one place.</p>
          </div>
          <Button
            variant="outline"
            className="sm:hidden w-full border-slate-600 text-slate-300"
            onClick={() => setToolsOpen((o) => !o)}
            data-testid="payments-tools-toggle"
          >
            <Wrench className="w-4 h-4 mr-1" /> {toolsOpen ? "Hide tools" : "Audits & tools"}
          </Button>
          <div className={`${toolsOpen ? "flex" : "hidden"} sm:flex flex-wrap gap-2 justify-end`}>
            {sla.breach_count > 0 && (
              <Card className="px-3 py-2 bg-red-500/10 border-red-500/40">
                <span className="text-red-300 text-sm font-semibold">{sla.breach_count} breaching 48h SLA</span>
              </Card>
            )}
            {sla.warn_count > 0 && (
              <Card className="px-3 py-2 bg-amber-500/10 border-amber-500/40">
                <span className="text-amber-300 text-sm font-semibold">{sla.warn_count} over 24h</span>
              </Card>
            )}
            <Link to="/admin/credits" data-testid="open-credits-link">
              <Button variant="outline" className="border-teal-500/40 text-teal-300 hover:bg-teal-500/10">
                <Wallet className="w-4 h-4 mr-1" /> Credits
              </Button>
            </Link>
            <Button
              onClick={runSiblingAudit}
              variant="outline"
              className="border-fuchsia-500/40 text-fuchsia-300 hover:bg-fuchsia-500/10"
              data-testid="open-sibling-audit-btn"
              title="Audit sibling-discount stored on transactions (read-only)"
            >
              <Stethoscope className="w-4 h-4 mr-1" /> Audit
            </Button>
            <Button
              onClick={runHygieneAudit}
              variant="outline"
              className="border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10"
              data-testid="open-hygiene-audit-btn"
              title="Audit transaction data hygiene — missing fields, orphans (read-only)"
            >
              <Stethoscope className="w-4 h-4 mr-1" /> Hygiene
            </Button>
            <Button
              onClick={async () => {
                setTestAcctOpen(true);
                setTestAcctLoading(true);
                setTestAcctSelected(new Set());
                try {
                  const res = await axios.get(`${API}/payments-hub/admin/diagnostic/test-account-audit`, { withCredentials: true });
                  setTestAcctData(res.data);
                } catch (err) {
                  { const __e = formatAdminError(err, "Audit failed"); toast.error(__e.message, __e.opts); };
                  setTestAcctData({ error: err.response?.data?.detail || String(err) });
                } finally {
                  setTestAcctLoading(false);
                }
              }}
              variant="outline"
              className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
              data-testid="open-test-accounts-btn"
              title="Audit test/dummy accounts and preview cascade delete"
            >
              <Stethoscope className="w-4 h-4 mr-1" /> Test Accounts
            </Button>
            {/* Feb 2026 — Legacy Integrity Report. Single-click read-only
                scan that reports 4 categories of structural gaps between
                academy_parent_registrations, payment_transactions, and
                payment_documents. Used to decide what historic cleanup
                we need before any code-level fix. Output is JSON,
                downloadable as-is. */}
            <Button
              onClick={async () => {
                const t = toast.loading("Running legacy integrity scan…");
                try {
                  const res = await axios.get(
                    `${API}/payments-hub/admin/diagnostic/legacy-integrity-report`,
                    { withCredentials: true, params: { examples_per_category: 5 } }
                  );
                  // Auto-download as JSON
                  const blob = new Blob([JSON.stringify(res.data, null, 2)], {
                    type: "application/json;charset=utf-8;",
                  });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement("a");
                  link.href = url;
                  const stamp = new Date().toISOString().split("T")[0];
                  link.download = `legacy-integrity-report-${stamp}.json`;
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  URL.revokeObjectURL(url);
                  const s = res.data.summary || {};
                  toast.success(
                    `Report ready · regs-without-txn: ${s.registrations_without_txn} · ` +
                    `txns-without-reg-id: ${s.academy_txns_without_registration_id} · ` +
                    `paid-txn-unpaid-reg: ${s.paid_txn_unpaid_registration} · ` +
                    `paid-no-receipt: ${s.paid_registrations_without_receipt}`,
                    { id: t, duration: 12000 }
                  );
                } catch (err) {
                  const __e = formatAdminError(err, "Integrity scan failed");
                  toast.error(__e.message, { id: t, ...__e.opts });
                }
              }}
              variant="outline"
              className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
              data-testid="open-legacy-integrity-report-btn"
              title="Read-only scan: 4 categories of registration ↔ transaction ↔ receipt gaps. Downloads JSON."
            >
              <Stethoscope className="w-4 h-4 mr-1" /> Legacy Integrity
            </Button>
            <Button
              onClick={async () => {
                setSpecialDiagOpen(true);
                setSpecialDiagLoading(true);
                try {
                  const [sg, ue, sm] = await Promise.all([
                    axios.get(`${API}/payments-hub/admin/diagnostic/sarah-gah-orphan`, { withCredentials: true }),
                    axios.get(`${API}/payments-hub/admin/diagnostic/unbackfillable-emails`, { withCredentials: true }),
                    axios.get(`${API}/payments-hub/admin/diagnostic/scc-member-flag-audit`, { withCredentials: true }),
                  ]);
                  setSpecialDiagData({ sarah: sg.data, emails: ue.data, scc: sm.data });
                } catch (err) {
                  { const __e = formatAdminError(err, "Diagnostics failed"); toast.error(__e.message, __e.opts); };
                  setSpecialDiagData({ error: err.response?.data?.detail || String(err) });
                } finally {
                  setSpecialDiagLoading(false);
                }
              }}
              variant="outline"
              className="border-violet-500/40 text-violet-300 hover:bg-violet-500/10"
              data-testid="open-special-diag-btn"
              title="Sarah Gah / unbackfillable emails / SCC flag desync (read-only)"
            >
              <Stethoscope className="w-4 h-4 mr-1" /> Triage
            </Button>
            <Button
              onClick={async () => {
                setLeakLoading(true);
                setLeakOpen(true);
                try {
                  const r = await axios.get(
                    `${API}/payments-hub/admin/diagnostic/non-member-discount-leak`,
                    { withCredentials: true },
                  );
                  setLeakData(r.data);
                } catch (err) {
                  { const __e = formatAdminError(err, "Discount leak audit failed"); toast.error(__e.message, __e.opts); };
                  setLeakData({ error: err.response?.data?.detail || String(err) });
                } finally {
                  setLeakLoading(false);
                }
              }}
              variant="outline"
              className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
              data-testid="open-discount-leak-btn"
              title="Diagnose non-member parents who got a member discount (read-only)"
            >
              <AlertTriangle className="w-4 h-4 mr-1" /> Discount leak
            </Button>
            <Button
              onClick={() => {
                setRelinkForm({ txn_id: "", new_parent_id: "", reason: "", update_user_id: true });
                setRelinkPlan(null);
                setRelinkOpen(true);
              }}
              variant="outline"
              className="border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10"
              data-testid="open-relink-txn-btn"
              title="Re-point a payment_transactions row to a different parent user (audited)"
            >
              <Link2 className="w-4 h-4 mr-1" /> Relink txn
            </Button>
            <Button
              onClick={async () => {
                setBackfillResult(null);
                setBackfillSelections({ ambiguousChoice: {}, noMatchMarkLegacy: {} });
                setBackfillOpen(true);
                setBackfillLoading(true);
                try {
                  const res = await axios.get(
                    `${API}/payments-hub/admin/diagnostic/missing-registration-id-txns`,
                    { withCredentials: true },
                  );
                  setBackfillData(res.data);
                  // Pre-select defaults: earliest candidate for ambiguous rows;
                  // mark-legacy=true for all no-match rows.
                  const ambig = {};
                  const noMatch = {};
                  for (const row of res.data.rows || []) {
                    const highConf = (row.candidates || []).filter((c) => c.confidence >= 5);
                    if (row.is_auto_fixable) continue;
                    if (highConf.length >= 2) {
                      // Earliest by reg_created_at
                      const earliest = [...highConf].sort(
                        (a, b) => (a.reg_created_at || "").localeCompare(b.reg_created_at || "")
                      )[0];
                      if (earliest) ambig[row.txn_id] = earliest.registration_id;
                    } else if ((row.candidates || []).length === 0) {
                      noMatch[row.txn_id] = true;
                    }
                  }
                  setBackfillSelections({ ambiguousChoice: ambig, noMatchMarkLegacy: noMatch });
                } catch (err) {
                  const __e = formatAdminError(err, "Inspect failed");
                  toast.error(__e.message, __e.opts);
                  setBackfillData({ error: err.response?.data?.detail || String(err) });
                } finally {
                  setBackfillLoading(false);
                }
              }}
              variant="outline"
              className="border-fuchsia-500/40 text-fuchsia-300 hover:bg-fuchsia-500/10"
              data-testid="open-backfill-regid-btn"
              title="Inspect + backfill payment_transactions rows missing registration_id"
            >
              <Wrench className="w-4 h-4 mr-1" /> Backfill txns
            </Button>
            <Button
              onClick={async () => {
                setArchiveResult(null);
                setArchiveSelections({});
                setArchiveOpen(true);
                setArchiveLoading(true);
                try {
                  const res = await axios.get(
                    `${API}/payments-hub/admin/diagnostic/rejected-orphan-registrations`,
                    { withCredentials: true },
                  );
                  setArchiveData(res.data);
                  // Default-tick every orphan
                  const ticks = {};
                  for (const o of res.data.orphans || []) {
                    if (o.id) ticks[o.id] = true;
                  }
                  setArchiveSelections(ticks);
                } catch (err) {
                  const __e = formatAdminError(err, "Inspect failed");
                  toast.error(__e.message, __e.opts);
                  setArchiveData({ error: err.response?.data?.detail || String(err) });
                } finally {
                  setArchiveLoading(false);
                }
              }}
              variant="outline"
              className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
              data-testid="open-archive-orphans-btn"
              title="Archive rejected orphan registrations (audited)"
            >
              <Trash2 className="w-4 h-4 mr-1" /> Archive orphans
            </Button>
            <Button
              onClick={async () => {
                setSweeperOpen(true);
                setSweeperLoading(true);
                setSweeperData(null);
                try {
                  const res = await axios.get(
                    `${API}/payments-hub/admin/duplicate-identity-scan`,
                    { withCredentials: true },
                  );
                  setSweeperData(res.data);
                } catch (err) {
                  const __e = formatAdminError(err, "Orphan scan failed");
                  toast.error(__e.message, __e.opts);
                  setSweeperData({ error: err.response?.data?.detail || String(err) });
                } finally {
                  setSweeperLoading(false);
                }
              }}
              variant="outline"
              className="border-orange-500/40 text-orange-300 hover:bg-orange-500/10"
              data-testid="open-orphan-sweeper-btn"
              title="Find & archive orphaned unpaid placeholders that have an approved twin (dry-run, reversible)"
            >
              <Trash2 className="w-4 h-4 mr-1" /> Orphan Sweeper
            </Button>
            <Button
              onClick={async () => {
                setDmHealthOpen(true);
                setDmHealthLoading(true);
                setDmHealthData(null);
                try {
                  const res = await axios.get(
                    `${API}/payments-hub/admin/diagnostic/dm-health`,
                    { withCredentials: true },
                  );
                  setDmHealthData(res.data);
                } catch (err) {
                  const __e = formatAdminError(err, "DM Health load failed");
                  toast.error(__e.message, __e.opts);
                  setDmHealthData({ error: err.response?.data?.detail || String(err) });
                } finally {
                  setDmHealthLoading(false);
                }
              }}
              variant="outline"
              className="border-teal-500/40 text-teal-300 hover:bg-teal-500/10"
              data-testid="open-dm-health-btn"
              title="Find paid registrations where parent's DM is no longer active"
            >
              <Activity className="w-4 h-4 mr-1" /> DM Health
            </Button>
            <Button
              onClick={async () => {
                setDupPlayersOpen(true);
                setDupPlayersLoading(true);
                setDupPlayersData(null);
                setDupKeepSelection({});
                setDupMergeResult(null);
                try {
                  const res = await axios.get(
                    `${API}/payments-hub/admin/diagnostic/duplicate-players`,
                    { withCredentials: true },
                  );
                  setDupPlayersData(res.data);
                  // Pre-select suggested keep for each group
                  const presel = {};
                  (res.data.groups || []).forEach((g, idx) => {
                    if (g.suggested_keep_player_id) presel[idx] = g.suggested_keep_player_id;
                  });
                  setDupKeepSelection(presel);
                } catch (err) {
                  const __e = formatAdminError(err, "Duplicate scan failed");
                  toast.error(__e.message, __e.opts);
                  setDupPlayersData({ error: err.response?.data?.detail || String(err) });
                } finally {
                  setDupPlayersLoading(false);
                }
              }}
              variant="outline"
              className="border-fuchsia-500/40 text-fuchsia-300 hover:bg-fuchsia-500/10"
              data-testid="open-duplicate-players-btn"
              title="Find & merge duplicate academy_players rows (same parent + name + birthdate)"
            >
              <Users className="w-4 h-4 mr-1" /> Duplicate players
            </Button>
            <Button
              onClick={() => {
                setFindPlayerOpen(true);
                setFindPlayerData(null);
                setFindPlayerGiven("");
                setFindPlayerLast("");
              }}
              variant="outline"
              className="border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10"
              data-testid="open-find-player-btn"
              title="Look up every academy_players row by name — surfaces duplicates that the grouper misses (e.g. mismatched birthdates)"
            >
              <Stethoscope className="w-4 h-4 mr-1" /> Find player
            </Button>
            <Button
              onClick={async () => {
                setProrataOpen(true);
                setProrataData(null);
                setProrataExpanded({});
                setProrataSelectedTermId("");
                setProrataTermsLoading(true);
                try {
                  const res = await axios.get(
                    `${API}/academy/terms`,
                    { withCredentials: true },
                  );
                  const raw = Array.isArray(res.data) ? res.data : (res.data?.terms || []);
                  // Filter to terms whose end_date is within the last 12 months
                  // OR whose start_date is in the future (active + upcoming + recent).
                  const now = new Date();
                  const twelveMonthsAgo = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
                  const filtered = raw
                    .filter((t) => {
                      if (!t || !t.id) return false;
                      const endStr = t.end_date || t.start_date;
                      if (!endStr) return true; // keep ambiguous rows visible
                      const endDt = new Date(endStr);
                      return !isNaN(endDt.getTime()) && endDt >= twelveMonthsAgo;
                    })
                    .map((t) => {
                      // Mark the active term — is_finalized AND end_date >= now AND start_date <= now
                      const startDt = t.start_date ? new Date(t.start_date) : null;
                      const endDt = t.end_date ? new Date(t.end_date) : null;
                      const isActive = Boolean(
                        t.is_finalized
                        && startDt && !isNaN(startDt.getTime()) && startDt <= now
                        && endDt && !isNaN(endDt.getTime()) && endDt >= now
                      );
                      return { ...t, __is_active: isActive };
                    });
                  // Sort: active first, then newest start_date
                  filtered.sort((a, b) => {
                    if (a.__is_active && !b.__is_active) return -1;
                    if (!a.__is_active && b.__is_active) return 1;
                    return (b.start_date || "").localeCompare(a.start_date || "");
                  });
                  setProrataTerms(filtered);
                  // Default to the active term if present, else newest
                  const active = filtered.find((t) => t.__is_active);
                  if (active) {
                    setProrataSelectedTermId(active.id);
                  } else if (filtered.length > 0) {
                    setProrataSelectedTermId(filtered[0].id);
                  }
                } catch (err) {
                  const __e = formatAdminError(err, "Could not load terms");
                  toast.error(__e.message, __e.opts);
                } finally {
                  setProrataTermsLoading(false);
                }
              }}
              variant="outline"
              className="border-orange-500/40 text-orange-300 hover:bg-orange-500/10"
              data-testid="open-prorata-audit-btn"
              title="Audit pro-rata billing accuracy for a selected term. Read-only."
            >
              <Calculator className="w-4 h-4 mr-1" /> Pro-rata audit
            </Button>
            <Button
              onClick={async () => {
                setDriftOpen(true);
                setDriftData(null);
                setDriftRepairResult(null);
                setDriftLoading(true);
                try {
                  const res = await axios.get(`${API}/payments-hub/admin/diagnostic/fee-breakdown-drift`, { withCredentials: true });
                  setDriftData(res.data);
                } catch (err) {
                  const __e = formatAdminError(err, "Fee-breakdown drift preview failed");
                  toast.error(__e.message, __e.opts);
                  setDriftData({ error: __e.message });
                } finally {
                  setDriftLoading(false);
                }
              }}
              variant="outline"
              className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
              data-testid="open-fb-drift-btn"
              title="Fee-breakdown drift — find registrations where parent is verified-member but stored breakdown has $0 discount. Read-only preview + confirm-token repair."
            >
              <AlertTriangle className="w-4 h-4 mr-1" /> Fee-breakdown drift
            </Button>
            <Button
              onClick={async () => {
                setAutoCancelOpen(true);
                setAutoCancelData(null);
                setAutoCancelResult(null);
                setAutoCancelLoading(true);
                try {
                  const res = await axios.get(`${API}/payments-hub/admin/diagnostic/auto-cancel-stale`, { withCredentials: true });
                  setAutoCancelData(res.data);
                } catch (err) {
                  const __e = formatAdminError(err, "Auto-cancel preview failed");
                  toast.error(__e.message, __e.opts);
                  setAutoCancelData({ error: __e.message });
                } finally {
                  setAutoCancelLoading(false);
                }
              }}
              variant="outline"
              className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
              data-testid="open-autocancel-stale-btn"
              title="Auto-cancel pending registrations older than 14 days. Skips paid-in-same-term and proof-attached rows. 20/run cap. Courtesy email per cancel."
            >
              <X className="w-4 h-4 mr-1" /> Auto-cancel stale
            </Button>
            <Button
              onClick={openT1Modal}
              variant="outline"
              className="border-rose-500/50 text-rose-300 hover:bg-rose-500/10"
              data-testid="open-term1-desync-btn"
              title="Term 1 paid-but-pending status desync (read-only)"
            >
              <AlertCircle className="w-4 h-4 mr-1" /> Term 1 desync
            </Button>
            <Button
              onClick={async () => {
                try {
                  const r = await axios.get(
                    `${API}/payments-hub/admin/diagnostic/term1-deep-investigation`,
                    { withCredentials: true },
                  );
                  const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `term1-deep-investigation-${new Date().toISOString().slice(0, 10)}.json`;
                  a.click();
                  URL.revokeObjectURL(url);
                  toast.success(`Deep investigation downloaded — academy_reg total: ${r.data.academy_registration_total}, Term1 string-cutoff: ${r.data.term1_count_string_cutoff}, datetime-cutoff: ${r.data.term1_count_datetime_cutoff}`);
                } catch (err) {
                  { const __e = formatAdminError(err, "Investigation failed"); toast.error(__e.message, __e.opts); };
                }
              }}
              variant="outline"
              className="border-orange-500/50 text-orange-300 hover:bg-orange-500/10"
              data-testid="open-term1-deep-btn"
              title="Deep investigation: counts, distinct values, Mark Reeve full dump"
            >
              <AlertCircle className="w-4 h-4 mr-1" /> Term 1 deep
            </Button>
          </div>
          {/* Everyday controls stay visible on mobile */}
          <div className="flex flex-wrap gap-2 justify-end w-full sm:w-auto">
            <div className="flex items-center gap-1">
              <input
                type="text"
                value={doctorQuery}
                onChange={(e) => setDoctorQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") runDoctor(); }}
                placeholder="Look up parent…"
                className="h-9 w-44 px-2 text-sm bg-slate-900 border border-rose-500/30 text-rose-200 placeholder:text-rose-300/40 rounded focus:outline-none focus:border-rose-400"
                data-testid="doctor-query-input"
              />
              <Button
                onClick={() => runDoctor()}
                variant="outline"
                size="sm"
                className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
                data-testid="run-doctor-btn"
                title="Diagnose a registration / payment problem for a specific parent"
              >
                Doctor
              </Button>
            </div>
            <Button
              onClick={() => {
                const n = exportRowsAsCsv(rows, tab);
                toast.success(`Exported ${n} row${n === 1 ? "" : "s"} — ${tab}.csv`);
              }}
              disabled={!rows.length}
              variant="outline"
              className="border-slate-600"
              data-testid="export-csv-btn"
              title="Download current filter as CSV"
            >
              <Download className="w-4 h-4 mr-1" /> CSV
            </Button>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="bg-slate-900 border border-slate-700 text-slate-200 text-xs rounded px-2 py-1.5"
              data-testid="hub-category-filter"
              title="Filter queue by payment category"
            >
              <option value="">All categories</option>
              <option value="academy_term">Academy Term</option>
              <option value="event">All events</option>
              <option value="camp">Camps only</option>
              <option value="tournament">Tournaments only</option>
              <option value="digital_membership">Digital Membership</option>
              <option value="manual_invoice">Manual invoice</option>
            </select>
            <select
              value={csvTermId}
              onChange={(e) => setCsvTermId(e.target.value)}
              className="bg-slate-900 border border-slate-700 text-slate-200 text-xs rounded px-2 py-1.5 max-w-[200px]"
              data-testid="breakdown-csv-term-select"
              title="Filter the Breakdown CSV by term"
            >
              <option value="">All terms</option>
              {csvTermList.map((t) => (
                <option key={t.id} value={t.id}>{t.name || t.term_name}</option>
              ))}
            </select>
            <Button
              onClick={async () => {
                try {
                  // Streams server-side from canonical fields (post-6-step DB consolidation).
                  // Filter by current tab's payment_status when meaningful + optional term_id.
                  const params = new URLSearchParams();
                  const tabToStatus = {
                    paid: "paid",
                    unpaid: "unpaid,awaiting_payment,pending",
                    pending_verification: "pending_verification,claimed",
                    disputed: "disputed",
                    cancelled: "cancelled",
                  };
                  if (tabToStatus[tab]) params.set("payment_status", tabToStatus[tab]);
                  if (csvTermId) params.set("term_id", csvTermId);
                  const url = `${API}/payments-hub/export/breakdown-csv${params.toString() ? `?${params.toString()}` : ""}`;
                  const res = await fetch(url, { credentials: "include" });
                  if (!res.ok) throw new Error(`HTTP ${res.status}`);
                  const blob = await res.blob();
                  const dlUrl = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = dlUrl;
                  const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
                  const termSlug = csvTermId
                    ? `-${(csvTermList.find(t => t.id === csvTermId)?.name || csvTermId).replace(/[^A-Za-z0-9]+/g, "_")}`
                    : "";
                  a.download = `payment-breakdown-${tab}${termSlug}-${ts}.csv`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(dlUrl);
                  toast.success(`Breakdown CSV exported — ${tab}${csvTermId ? ` · ${csvTermList.find(t => t.id === csvTermId)?.name || csvTermId}` : ""}`);
                } catch (err) {
                  toast.error(`Breakdown CSV failed: ${err.message}`);
                }
              }}
              variant="outline"
              className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
              data-testid="export-breakdown-csv-btn"
              title="Full per-line-item breakdown (server-side, from canonical fields)"
            >
              <Download className="w-4 h-4 mr-1" /> Breakdown CSV
            </Button>
            <Button onClick={() => load(tab)} variant="outline" className="border-slate-600" data-testid="refresh-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-4" data-testid="status-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            const count = counts[t.id];
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-md border transition text-sm ${
                  active ? "bg-slate-800 border-slate-600 text-white" : "bg-slate-900/40 border-slate-800 text-slate-400 hover:text-white"
                }`}
                data-testid={`tab-${t.id}`}
              >
                <Icon className="w-4 h-4" /> {t.label}
                {count !== undefined && (
                  <span className={`text-xs px-1.5 py-0.5 rounded ${active ? "bg-slate-700" : "bg-slate-800"}`}>{count}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Auto-Match Helper — surfaces SLA-breached claimed payments with
            their top bank_upload_entries candidate so admin can clear the
            queue in 1 click each. Only visible on the Claimed tab while
            there is at least one breach (the worst-case rows). */}
        {tab === "claimed" && sla.breach_count > 0 && (
          <Card
            className="mb-4 bg-red-500/5 border-red-500/30 p-4"
            data-testid="auto-match-helper-card"
          >
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex items-start gap-3">
                <Wrench className="w-5 h-5 text-red-300 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-red-200">
                    Bank-match auto-suggest
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {sla.breach_count} payment{sla.breach_count === 1 ? "" : "s"} past 48h SLA.
                    Match each against imported bank entries and approve in a single click.
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                {!autoMatchOpen && (
                  <Button
                    onClick={() => { setAutoMatchOpen(true); loadAutoMatch("breach"); }}
                    size="sm"
                    className="bg-red-500/20 border border-red-500/40 text-red-200 hover:bg-red-500/30"
                    data-testid="open-auto-match-btn"
                  >
                    <Activity className="w-4 h-4 mr-1" /> Run auto-match
                  </Button>
                )}
                {autoMatchOpen && (
                  <Button
                    onClick={() => setAutoMatchOpen(false)}
                    size="sm"
                    variant="outline"
                    className="border-slate-600"
                    data-testid="close-auto-match-btn"
                  >
                    <X className="w-4 h-4 mr-1" /> Hide
                  </Button>
                )}
              </div>
            </div>

            {autoMatchOpen && (
              <div className="mt-4" data-testid="auto-match-panel">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs uppercase text-slate-500">Show:</span>
                  {["breach", "warn", "all"].map((s) => (
                    <button
                      key={s}
                      onClick={() => loadAutoMatch(s)}
                      className={`text-xs px-2 py-1 rounded border ${
                        autoMatchSla === s
                          ? "bg-slate-800 border-slate-500 text-white"
                          : "bg-slate-900/40 border-slate-800 text-slate-400 hover:text-white"
                      }`}
                      data-testid={`auto-match-sla-${s}`}
                    >
                      {s === "breach" ? "Breached (>48h)" : s === "warn" ? "Warn + breach (>24h)" : "All claimed"}
                    </button>
                  ))}
                  {autoMatchLoading && <Loader2 className="w-4 h-4 animate-spin text-red-300 ml-2" />}
                </div>

                {!autoMatchLoading && autoMatchData && autoMatchData.rows.length === 0 && (
                  <p className="text-sm text-slate-500 italic py-4 text-center">
                    No claimed payments match this filter.
                  </p>
                )}

                {!autoMatchLoading && autoMatchData && autoMatchData.rows.length > 0 && (
                  <div className="space-y-2" data-testid="auto-match-rows">
                    {autoMatchData.rows.map((r) => {
                      const sug = r.top_suggestion;
                      const conf = sug?.confidence || "none";
                      const confColor = {
                        exact: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
                        high: "bg-blue-500/20 text-blue-300 border-blue-500/40",
                        medium: "bg-amber-500/20 text-amber-300 border-amber-500/40",
                        low: "bg-slate-700/30 text-slate-300 border-slate-600",
                        none: "bg-slate-800 text-slate-500 border-slate-700",
                      }[conf];
                      const hours = r.age_seconds ? Math.floor(r.age_seconds / 3600) : 0;
                      const busy = autoMatchBusyTxn === r.txn_id;
                      const canApprove = sug && !busy;
                      return (
                        <div
                          key={r.txn_id}
                          className="bg-slate-900/40 border border-slate-800 rounded-md p-3 flex items-start justify-between gap-3 flex-wrap"
                          data-testid={`auto-match-row-${r.txn_id}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap text-sm">
                              <button
                                onClick={() => setSelected(r.txn_id)}
                                className="font-mono text-emerald-300 hover:underline"
                                data-testid={`auto-match-open-drawer-${r.txn_id}`}
                                title="Open drawer"
                              >
                                {r.payment_reference}
                              </button>
                              <span className="text-slate-300">·</span>
                              <span className="text-slate-200 truncate">{r.parent_name || r.parent_email || "—"}</span>
                              <span className="text-slate-300">·</span>
                              <span className="font-mono text-white">${r.amount.toFixed(2)}</span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded border ${r.sla === "breach" ? "bg-red-500/15 text-red-300 border-red-500/40" : "bg-amber-500/15 text-amber-300 border-amber-500/40"}`}>
                                {r.sla.toUpperCase()} · {hours}h
                              </span>
                            </div>
                            {sug ? (
                              <div className="mt-1 text-xs text-slate-400">
                                <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold border ${confColor} mr-2`}>
                                  {conf.toUpperCase()}
                                </span>
                                <span className="text-slate-300">{sug.bank_transaction_id || sug.reference_raw || sug.reference || "(no ref)"}</span>
                                <span className="text-slate-500"> · </span>
                                <span className="font-mono text-emerald-300">${(sug.source_amount || 0).toFixed(2)}</span>
                                <span className="text-slate-500"> · </span>
                                <span className="truncate">{sug.source_name || "—"}</span>
                                {sug.finished_on && (
                                  <span className="text-slate-500"> · {String(sug.finished_on).split(" ")[0]}</span>
                                )}
                                {sug.days_from_claim !== null && sug.days_from_claim !== undefined && (
                                  <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded border ${sug.days_from_claim <= 3 ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" : sug.days_from_claim <= 14 ? "bg-amber-500/10 text-amber-300 border-amber-500/30" : "bg-red-500/10 text-red-300 border-red-500/30"}`}>
                                    {sug.days_from_claim === 0 ? "same day" : `${sug.days_from_claim}d from claim`}
                                  </span>
                                )}
                                <div className="text-[10px] text-slate-500 italic mt-0.5">{sug.reason}</div>
                              </div>
                            ) : (
                              <p className="mt-1 text-xs text-slate-500 italic">
                                No bank-import match found — open drawer to verify manually.
                              </p>
                            )}
                          </div>
                          <div className="flex-shrink-0">
                            <Button
                              onClick={() => approveWithBankMatch(r)}
                              size="sm"
                              disabled={!canApprove}
                              className="bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/30 disabled:opacity-40"
                              data-testid={`auto-match-approve-${r.txn_id}`}
                            >
                              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />}
                              {busy ? "Approving…" : "Approve with this match"}
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <p className="mt-3 text-[10px] text-slate-500 italic">
                  Each Approve commits the canonical approval flow exactly as the drawer does — bank reference and payee are stamped from the matched entry, and the same email/receipt is sent.
                </p>
              </div>
            )}
          </Card>
        )}

        {/* Queue */}
        <Card className="bg-slate-900/40 border-slate-800 overflow-hidden">
          {loading && (
            <div className="py-12 text-center">
              <Loader2 className="w-6 h-6 animate-spin text-emerald-400 mx-auto" />
            </div>
          )}
          {!loading && rows.length === 0 && (
            <div className="py-12 text-center text-slate-500">
              <CheckCircle className="w-10 h-10 mx-auto text-emerald-400/60 mb-2" />
              <p>No payments in this tab.</p>
            </div>
          )}
          {!loading && rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-900/80 border-b border-slate-800">
                  <tr className="text-left text-xs uppercase text-slate-400">
                    <th className="p-3">Parent</th>
                    <th className="p-3 text-center">Kids</th>
                    <th className="p-3 text-right">Invoiced</th>
                    <th className="p-3 text-right">Received</th>
                    <th className="p-3 text-center">Status</th>
                    <th className="p-3">Waiting / Open</th>
                    <th className="p-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <QueueRow key={r.id} row={r} onOpen={() => setSelected(r.id)} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {/* Right drawer */}
      {selected && (
        <DetailDrawer
          txnId={selected}
          onClose={() => setSelected(null)}
          onActionComplete={() => { load(tab); setSelected(null); }}
        />
      )}

      {/* Sibling-discount diagnostic modal */}
      {auditOpen && (
        <SiblingAuditModal
          loading={auditLoading}
          data={auditData}
          onClose={() => { setAuditOpen(false); setAuditData(null); }}
          onRerun={runSiblingAudit}
        />
      )}

      {/* Data hygiene diagnostic modal */}
      {hygieneOpen && (
        <HygieneAuditModal
          loading={hygieneLoading}
          data={hygieneData}
          onClose={() => { setHygieneOpen(false); setHygieneData(null); }}
          onRerun={runHygieneAudit}
          onBackfill={runHygieneBackfill}
          onRunAllConfirmed={runAllConfirmedCleanups}
        />
      )}

      {/* Registration Doctor modal */}
      {doctorOpen && (
        <DoctorModal
          loading={doctorLoading}
          data={doctorData}
          query={doctorQuery}
          onQueryChange={setDoctorQuery}
          onSearch={runDoctor}
          onClose={() => { setDoctorOpen(false); setDoctorData(null); }}
        />
      )}

      {/* Triage modal — Sarah Gah / unbackfillable emails / SCC flag desync */}
      {specialDiagOpen && (
        <TriageModal
          loading={specialDiagLoading}
          data={specialDiagData}
          onClose={() => { setSpecialDiagOpen(false); setSpecialDiagData(null); }}
        />
      )}

      {/* Discount leak audit modal */}
      {leakOpen && (
        <DiscountLeakModal
          loading={leakLoading}
          data={leakData}
          onClose={() => { setLeakOpen(false); setLeakData(null); }}
        />
      )}

      {/* Term 1 paid-but-pending desync modal */}
      {t1Open && (
        <Term1DesyncModal
          loading={t1Loading}
          data={t1Data}
          onRefresh={async () => {
            setT1Loading(true);
            try {
              const r = await axios.get(
                `${API}/payments-hub/admin/diagnostic/term1-paid-pending-desync`,
                { params: { include_full_drilldown: true }, withCredentials: true },
              );
              setT1Data(r.data);
            } finally { setT1Loading(false); }
          }}
          onClose={() => { setT1Open(false); setT1Data(null); }}
        />
      )}

      {/* Test Accounts cleanup modal */}
      {testAcctOpen && (
        <TestAccountsModal
          loading={testAcctLoading}
          data={testAcctData}
          selected={testAcctSelected}
          setSelected={setTestAcctSelected}
          onClose={() => { setTestAcctOpen(false); setTestAcctData(null); setTestAcctSelected(new Set()); }}
          onRerun={async () => {
            setTestAcctLoading(true);
            try {
              const res = await axios.get(`${API}/payments-hub/admin/diagnostic/test-account-audit`, { withCredentials: true });
              setTestAcctData(res.data);
            } catch (err) {
              { const __e = formatAdminError(err, "Audit failed"); toast.error(__e.message, __e.opts); };
            } finally {
              setTestAcctLoading(false);
            }
          }}
          onCleanup={async (dryRun, force) => {
            const ids = Array.from(testAcctSelected);
            if (ids.length === 0) {
              toast.error("Select at least one account first");
              return;
            }
            if (!dryRun && !window.confirm(
              `This will PERMANENTLY DELETE ${ids.length} user account(s) and all related records ` +
              `(transactions, players, enrollments, registrations, documents, credits).\n\n` +
              `Force flag: ${force ? "ON (overrides safety guards)" : "off"}.\n\n` +
              `Type-confirm by clicking OK only after re-checking the list. This action is logged but not reversible.`
            )) return;
            setTestAcctLoading(true);
            try {
              const res = await axios.post(
                `${API}/payments-hub/admin/cleanup-test-accounts`,
                { user_ids: ids, dry_run: dryRun, force, reason: "Test-account cleanup via Admin Hub" },
                { withCredentials: true },
              );
              toast.success(
                dryRun
                  ? `Dry-run complete — ${res.data.executed} would be deleted, ${res.data.skipped} skipped`
                  : `Deleted ${res.data.executed} account(s). ${res.data.skipped} skipped.`
              );
              setTestAcctData({ ...res.data, _is_cleanup_result: true });
            } catch (err) {
              { const __e = formatAdminError(err, "Cleanup failed"); toast.error(__e.message, __e.opts); };
            } finally {
              setTestAcctLoading(false);
            }
          }}
        />
      )}

      {relinkOpen && (
        <RelinkTxnModal
          form={relinkForm}
          setForm={setRelinkForm}
          plan={relinkPlan}
          busy={relinkBusy}
          onClose={() => { setRelinkOpen(false); setRelinkPlan(null); }}
          onPreview={async () => {
            if (!relinkForm.txn_id || !relinkForm.new_parent_id) {
              toast.error("Transaction ID and new parent user ID are required");
              return;
            }
            setRelinkBusy(true);
            try {
              const res = await axios.post(
                `${API}/payments-hub/admin/diagnostic/relink-transaction-parent`,
                {
                  txn_id: relinkForm.txn_id.trim(),
                  new_parent_id: relinkForm.new_parent_id.trim(),
                  update_user_id: relinkForm.update_user_id,
                  dry_run: true,
                },
                { withCredentials: true },
              );
              setRelinkPlan(res.data);
              toast.success("Dry-run preview ready — review and click Execute to commit");
            } catch (err) {
              const __e = formatAdminError(err, "Preview failed");
              toast.error(__e.message, __e.opts);
            } finally {
              setRelinkBusy(false);
            }
          }}
          onExecute={async () => {
            if (!relinkForm.reason || relinkForm.reason.trim().length < 10) {
              toast.error("Reason (>=10 chars) is required to execute");
              return;
            }
            if (!window.confirm(
              `This will re-point txn ${relinkForm.txn_id} to parent ${relinkForm.new_parent_id} and write an audit log entry. Continue?`
            )) return;
            setRelinkBusy(true);
            try {
              const res = await axios.post(
                `${API}/payments-hub/admin/diagnostic/relink-transaction-parent`,
                {
                  txn_id: relinkForm.txn_id.trim(),
                  new_parent_id: relinkForm.new_parent_id.trim(),
                  update_user_id: relinkForm.update_user_id,
                  reason: relinkForm.reason.trim(),
                  dry_run: false,
                  confirm_token: "RELINK_TXN_PARENT_FEB2026",
                },
                { withCredentials: true },
              );
              setRelinkPlan(res.data);
              toast.success(res.data.note || "Transaction relinked successfully");
              load(tab);
            } catch (err) {
              const __e = formatAdminError(err, "Relink failed");
              toast.error(__e.message, __e.opts);
            } finally {
              setRelinkBusy(false);
            }
          }}
        />
      )}

      {backfillOpen && (
        <BackfillRegIdModal
          loading={backfillLoading}
          data={backfillData}
          selections={backfillSelections}
          setSelections={setBackfillSelections}
          busy={backfillBusy}
          result={backfillResult}
          onClose={() => {
            setBackfillOpen(false);
            setBackfillData(null);
            setBackfillResult(null);
          }}
          onExecute={async () => {
            const rows = backfillData?.rows || [];
            const ambig = backfillSelections.ambiguousChoice || {};
            const noMatch = backfillSelections.noMatchMarkLegacy || {};

            // Build txn_ids to act on. Anything with a forced selection OR
            // auto-fixable OR marked-legacy. Rows where admin chose to skip
            // (unticked legacy on no-match) are NOT included.
            const autoFix = rows.filter((r) => r.is_auto_fixable).map((r) => r.txn_id);
            const forcedTxnIds = Object.keys(ambig).filter((k) => !!ambig[k]);
            const legacyTxnIds = Object.keys(noMatch).filter((k) => noMatch[k]);
            const txnIds = Array.from(new Set([...autoFix, ...forcedTxnIds, ...legacyTxnIds]));

            const summary = `Will fix ${autoFix.length} auto-match + ${forcedTxnIds.length} admin-resolved ambiguous, and mark ${legacyTxnIds.length} as legacy_unlinked. Proceed?`;
            if (!window.confirm(summary)) return;

            setBackfillBusy(true);
            try {
              const res = await axios.post(
                `${API}/payments-hub/admin/diagnostic/backfill-missing-registration-ids`,
                {
                  dry_run: false,
                  confirm_token: "BACKFILL_REGISTRATION_IDS_MAY2026",
                  txn_ids: txnIds.length ? txnIds : undefined,
                  forced_assignments: ambig,
                  stamp_legacy_unlinked: legacyTxnIds.length > 0,
                },
                { withCredentials: true },
              );
              setBackfillResult(res.data);
              toast.success(
                `Backfill complete — ${res.data.patched_count} auto, ${res.data.forced_patched_count} resolved, ${res.data.legacy_stamped_count} legacy.`,
                { duration: 8000 },
              );
              load(tab);
            } catch (err) {
              const __e = formatAdminError(err, "Backfill failed");
              toast.error(__e.message, __e.opts);
              setBackfillResult({ error: err.response?.data?.detail || String(err) });
            } finally {
              setBackfillBusy(false);
            }
          }}
        />
      )}

      {archiveOpen && (
        <ArchiveOrphansModal
          loading={archiveLoading}
          data={archiveData}
          selections={archiveSelections}
          setSelections={setArchiveSelections}
          busy={archiveBusy}
          result={archiveResult}
          onClose={() => {
            setArchiveOpen(false);
            setArchiveData(null);
            setArchiveResult(null);
          }}
          onExecute={async () => {
            const checkedIds = Object.entries(archiveSelections)
              .filter(([_, on]) => on)
              .map(([id]) => id);
            if (checkedIds.length === 0) {
              toast.error("No rows selected for archive");
              return;
            }
            if (!window.confirm(
              `Will archive ${checkedIds.length} registration${checkedIds.length === 1 ? "" : "s"}. This moves them to academy_parent_registrations_archive. Proceed?`
            )) return;
            setArchiveBusy(true);
            try {
              const res = await axios.post(
                `${API}/payments-hub/admin/diagnostic/archive-rejected-orphans`,
                {
                  dry_run: false,
                  confirm_token: "ARCHIVE_REJECTED_ORPHANS_FEB2026",
                  ids: checkedIds,
                },
                { withCredentials: true },
              );
              setArchiveResult(res.data);
              toast.success(
                `Archived ${res.data.archived_count} of ${res.data.target_count} orphan registration${res.data.target_count === 1 ? "" : "s"}.`,
                { duration: 8000 },
              );
              load(tab);
            } catch (err) {
              const __e = formatAdminError(err, "Archive failed");
              toast.error(__e.message, __e.opts);
              setArchiveResult({ error: err.response?.data?.detail || String(err) });
            } finally {
              setArchiveBusy(false);
            }
          }}
        />
      )}

      {prorataOpen && (
        <ProrataAuditModal
          loading={prorataLoading}
          data={prorataData}
          expanded={prorataExpanded}
          setExpanded={setProrataExpanded}
          terms={prorataTerms}
          termsLoading={prorataTermsLoading}
          selectedTermId={prorataSelectedTermId}
          setSelectedTermId={setProrataSelectedTermId}
          onRun={async () => {
            if (!prorataSelectedTermId) {
              toast.error("Pick a term first");
              return;
            }
            setProrataLoading(true);
            setProrataData(null);
            setProrataExpanded({});
            try {
              const res = await axios.get(
                `${API}/payments-hub/admin/diagnostic/prorata-audit`,
                { params: { term_id: prorataSelectedTermId }, withCredentials: true },
              );
              setProrataData(res.data);
            } catch (err) {
              const __e = formatAdminError(err, "Pro-rata audit failed");
              toast.error(__e.message, __e.opts);
              setProrataData({ error: err.response?.data?.detail || String(err) });
            } finally {
              setProrataLoading(false);
            }
          }}
          onClose={() => { setProrataOpen(false); setProrataData(null); setProrataExpanded({}); }}
          onDownload={() => {
            if (!prorataData || prorataData.error) return;
            const blob = new Blob([JSON.stringify(prorataData, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `prorata-audit-${new Date().toISOString().slice(0, 10)}.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          }}
        />
      )}

      {dmHealthOpen && (
        <DmHealthModal
          loading={dmHealthLoading}
          data={dmHealthData}
          onClose={() => { setDmHealthOpen(false); setDmHealthData(null); }}
          onDownload={() => {
            if (!dmHealthData || dmHealthData.error) return;
            const blob = new Blob([JSON.stringify(dmHealthData, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `dm-health-${new Date().toISOString().slice(0, 10)}.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          }}
        />
      )}

      {sweeperOpen && (
        <Dialog open={sweeperOpen} onOpenChange={(o) => { if (!o) { setSweeperOpen(false); setSweeperData(null); setSweeperArchived([]); } }}>
          <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="orphan-sweeper-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Trash2 className="w-5 h-5 text-orange-400" /> Orphan / Duplicate Sweeper
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-400">
              Dry-run, <strong>approved-twin-only</strong>: archives an unpaid placeholder only when the same
              parent + children + term already has ONE approved payment (the keeper). Soft-archive — fully reversible.
              Paid transactions are never touched.
            </p>

            {sweeperLoading ? (
              <p className="text-slate-400 py-6" data-testid="orphan-sweeper-loading">Scanning transactions…</p>
            ) : sweeperData?.error ? (
              <p className="text-red-400 py-6">{String(sweeperData.error)}</p>
            ) : sweeperData ? (
              <div className="space-y-4">
                <div className="flex flex-wrap gap-3 text-sm" data-testid="orphan-sweeper-summary">
                  <span className="px-2 py-1 rounded bg-orange-500/15 text-orange-300 border border-orange-500/30">
                    {(sweeperData.summary?.redundant_count) || 0} redundant (safe to archive)
                  </span>
                  <span className="px-2 py-1 rounded bg-yellow-500/15 text-yellow-300 border border-yellow-500/30">
                    {(sweeperData.summary?.needs_review_count) || 0} needs review
                  </span>
                  <span className="px-2 py-1 rounded bg-slate-700/40 text-slate-300 border border-slate-600">
                    ${((sweeperData.summary?.total_cancel_candidate_amount) || 0).toFixed(2)} archivable
                  </span>
                </div>

                {sweeperArchived.length > 0 && (
                  <div className="rounded border border-emerald-500/30 bg-emerald-500/10 p-3" data-testid="orphan-sweeper-archived">
                    <p className="text-sm text-emerald-300 font-semibold mb-2">Archived this session ({sweeperArchived.length}) — reversible</p>
                    <div className="space-y-1">
                      {sweeperArchived.map((a) => (
                        <div key={a.id} className="flex items-center justify-between text-xs text-slate-300">
                          <span>{a.ref || a.id} · ${Number(a.amount || 0).toFixed(2)}</span>
                          <Button
                            size="sm" variant="outline"
                            className="h-6 border-emerald-500/40 text-emerald-300"
                            data-testid={`orphan-restore-${a.id}`}
                            disabled={sweeperBusy}
                            onClick={async () => {
                              setSweeperBusy(true);
                              try {
                                await axios.post(`${API}/payments-hub/admin/restore-duplicate-transactions`,
                                  { ids: [a.id] }, { withCredentials: true });
                                toast.success("Restored");
                                setSweeperArchived((prev) => prev.filter((x) => x.id !== a.id));
                                const res = await axios.get(`${API}/payments-hub/admin/duplicate-identity-scan`, { withCredentials: true });
                                setSweeperData(res.data);
                              } catch (err) {
                                const __e = formatAdminError(err, "Restore failed"); toast.error(__e.message, __e.opts);
                              } finally { setSweeperBusy(false); }
                            }}
                          >Restore</Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {(sweeperData.groups || []).filter((g) => g.category === "redundant").length === 0 ? (
                  <p className="text-slate-400 text-sm py-2" data-testid="orphan-sweeper-empty">No redundant orphans found. Nothing to clean up. 🎉</p>
                ) : (
                  (sweeperData.groups || []).filter((g) => g.category === "redundant").map((g, gi) => (
                    <div key={gi} className="rounded border border-slate-700 bg-slate-900/50 p-3" data-testid={`orphan-group-${gi}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 text-sm">
                          <p className="text-white font-semibold truncate">{g.parent_name || g.parent_email}</p>
                          <p className="text-slate-400 text-xs">{g.children} · {g.term_name}</p>
                          <div className="mt-2 space-y-0.5 text-xs">
                            {(g.transactions || []).map((t) => (
                              <div key={t.id} className={`flex justify-between ${t.is_keeper ? "text-blue-300" : t.is_cancel_candidate ? "text-orange-300" : "text-slate-400"}`}>
                                <span>{t.is_keeper ? "✓ KEEP" : t.is_cancel_candidate ? "✕ archive" : ""} {t.payment_reference || t.id}</span>
                                <span>{t.status} · ${Number(t.amount || 0).toFixed(2)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          className="bg-orange-600 hover:bg-orange-500 text-white shrink-0"
                          data-testid={`orphan-archive-${gi}`}
                          disabled={sweeperBusy || !g.keeper_id || !(g.loser_ids || []).length}
                          onClick={async () => {
                            if (!window.confirm(`Archive ${g.loser_ids.length} unpaid orphan(s) for ${g.parent_name || g.parent_email}? The approved payment is kept. This is reversible.`)) return;
                            setSweeperBusy(true);
                            try {
                              const losers = (g.transactions || []).filter((t) => t.is_cancel_candidate);
                              await axios.post(`${API}/payments-hub/admin/archive-duplicate-transactions`,
                                { keeper_id: g.keeper_id, loser_ids: g.loser_ids, reason: "orphan_sweeper" },
                                { withCredentials: true });
                              toast.success(`Archived ${g.loser_ids.length} orphan(s)`);
                              setSweeperArchived((prev) => [...prev, ...losers.map((t) => ({ id: t.id, ref: t.payment_reference, amount: t.amount, keeper_id: g.keeper_id }))]);
                              const res = await axios.get(`${API}/payments-hub/admin/duplicate-identity-scan`, { withCredentials: true });
                              setSweeperData(res.data);
                            } catch (err) {
                              const __e = formatAdminError(err, "Archive failed"); toast.error(__e.message, __e.opts);
                            } finally { setSweeperBusy(false); }
                          }}
                        >
                          Archive orphan{(g.loser_ids || []).length > 1 ? "s" : ""}
                        </Button>
                      </div>
                    </div>
                  ))
                )}

                {(sweeperData.groups || []).filter((g) => g.category === "needs_review").length > 0 && (
                  <div className="pt-2">
                    <p className="text-sm text-yellow-300 font-semibold mb-1">Needs review — never auto-archived</p>
                    <p className="text-xs text-slate-400 mb-2">Multiple paid rows (possible double payment) or all-unpaid (which is real?). Handle manually.</p>
                    {(sweeperData.groups || []).filter((g) => g.category === "needs_review").map((g, gi) => (
                      <div key={gi} className="rounded border border-yellow-600/30 bg-yellow-500/5 p-2 mb-2 text-xs" data-testid={`orphan-review-${gi}`}>
                        <p className="text-white">{g.parent_name || g.parent_email} · {g.children} · {g.term_name}</p>
                        {(g.transactions || []).map((t) => (
                          <div key={t.id} className="flex justify-between text-slate-400">
                            <span>{t.payment_reference || t.id}</span><span>{t.status} · ${Number(t.amount || 0).toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : null}
          </DialogContent>
        </Dialog>
      )}

      {driftOpen && (
        <FeeBreakdownDriftModal
          loading={driftLoading}
          data={driftData}
          repairBusy={driftRepairBusy}
          repairResult={driftRepairResult}
          onRepair={async (selectedIds) => {
            if (!driftData?.confirm_token) return;
            if (!Array.isArray(selectedIds) || selectedIds.length === 0) {
              toast.error("Select at least one row to repair.");
              return;
            }
            if (!window.confirm(`This will rewrite fee_breakdown on ${selectedIds.length} selected registration(s). total_amount values are preserved. Continue?`)) return;
            setDriftRepairBusy(true);
            try {
              const res = await axios.post(
                `${API}/payments-hub/admin/diagnostic/fee-breakdown-drift/repair`,
                { confirm_token: driftData.confirm_token, registration_ids: selectedIds },
                { withCredentials: true },
              );
              setDriftRepairResult(res.data);
              toast.success(`Repaired ${res.data.repaired} of ${selectedIds.length} selected`);
            } catch (err) {
              const __e = formatAdminError(err, "Repair failed");
              toast.error(__e.message, __e.opts);
              setDriftRepairResult({ error: err.response?.data?.detail || String(err) });
            } finally {
              setDriftRepairBusy(false);
            }
          }}
          onClose={() => { setDriftOpen(false); setDriftData(null); setDriftRepairResult(null); }}
        />
      )}

      {autoCancelOpen && (
        <AutoCancelStaleModal
          loading={autoCancelLoading}
          data={autoCancelData}
          busy={autoCancelBusy}
          result={autoCancelResult}
          onRun={async (forceOverCap) => {
            if (!autoCancelData?.confirm_token) return;
            const cap = forceOverCap ? autoCancelData.eligible_count : Math.min(autoCancelData.eligible_count, autoCancelData.per_run_cap);
            if (!window.confirm(`Cancel ${cap} stale registration(s) and send courtesy emails? This cannot be undone.`)) return;
            setAutoCancelBusy(true);
            try {
              const res = await axios.post(
                `${API}/payments-hub/admin/diagnostic/auto-cancel-stale/run`,
                { confirm_token: autoCancelData.confirm_token, force_over_cap: !!forceOverCap, send_email: true },
                { withCredentials: true },
              );
              setAutoCancelResult(res.data);
              toast.success(`Cancelled ${res.data.cancelled} of ${res.data.eligible_total} stale registration(s)`);
            } catch (err) {
              const __e = formatAdminError(err, "Auto-cancel failed");
              toast.error(__e.message, __e.opts);
              setAutoCancelResult({ error: err.response?.data?.detail || String(err) });
            } finally {
              setAutoCancelBusy(false);
            }
          }}
          onClose={() => { setAutoCancelOpen(false); setAutoCancelData(null); setAutoCancelResult(null); }}
          onDownload={() => {
            if (!autoCancelData || autoCancelData.error) return;
            const blob = new Blob([JSON.stringify(autoCancelData, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `auto-cancel-stale-${new Date().toISOString().slice(0, 10)}.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          }}
        />
      )}



      {dupPlayersOpen && (
        <DuplicatePlayersModal
          loading={dupPlayersLoading}
          data={dupPlayersData}
          keepSelection={dupKeepSelection}
          setKeepSelection={setDupKeepSelection}
          busy={dupMergeBusy}
          result={dupMergeResult}
          onClose={() => {
            setDupPlayersOpen(false);
            setDupPlayersData(null);
            setDupKeepSelection({});
            setDupMergeResult(null);
          }}
          onMerge={async (groupIdx, group) => {
            const keepId = dupKeepSelection[groupIdx];
            if (!keepId) {
              toast.error("Pick which row to keep first");
              return;
            }
            const archiveIds = (group.rows || [])
              .map((r) => r.player_id)
              .filter((pid) => pid && pid !== keepId);
            if (archiveIds.length === 0) {
              toast.error("Nothing to archive in this group");
              return;
            }
            const ok = window.confirm(
              `Merge ${group.given_name} ${group.last_name} (${group.birthdate})?\n\n` +
              `KEEP: ${keepId}\nARCHIVE: ${archiveIds.join(", ")}\n\n` +
              `Membership #, team_id, registration_id and other empty fields on the KEEP row will be filled from the archived rows. ` +
              `Archived rows are soft-deleted (archived_at + archived_reason) — never hard-deleted. Proceed?`
            );
            if (!ok) return;
            setDupMergeBusy(true);
            try {
              const res = await axios.post(
                `${API}/payments-hub/admin/diagnostic/merge-duplicate-players`,
                { keep_id: keepId, archive_ids: archiveIds },
                { withCredentials: true },
              );
              setDupMergeResult({ groupIdx, ...res.data });
              toast.success(
                `Merged. Backfilled: ${(res.data.backfilled_fields || []).join(", ") || "(none)"}. Repointed: ${Object.entries(res.data.repoint_stats || {}).filter(([, n]) => n > 0).map(([k, n]) => `${k}=${n}`).join(", ") || "(none)"}.`
              );
              // Refresh the scan to reflect the merge
              const refreshed = await axios.get(
                `${API}/payments-hub/admin/diagnostic/duplicate-players`,
                { withCredentials: true },
              );
              setDupPlayersData(refreshed.data);
              const presel = {};
              (refreshed.data.groups || []).forEach((g, idx) => {
                if (g.suggested_keep_player_id) presel[idx] = g.suggested_keep_player_id;
              });
              setDupKeepSelection(presel);
            } catch (err) {
              const __e = formatAdminError(err, "Merge failed");
              toast.error(__e.message, __e.opts);
            } finally {
              setDupMergeBusy(false);
            }
          }}
        />
      )}

      {findPlayerOpen && (
        <FindPlayerModal
          loading={findPlayerLoading}
          data={findPlayerData}
          given={findPlayerGiven}
          last={findPlayerLast}
          setGiven={setFindPlayerGiven}
          setLast={setFindPlayerLast}
          forceMergeKeep={forceMergeKeep}
          setForceMergeKeep={setForceMergeKeep}
          forceMergeArchive={forceMergeArchive}
          setForceMergeArchive={setForceMergeArchive}
          forceMergeReason={forceMergeReason}
          setForceMergeReason={setForceMergeReason}
          forceMergePreview={forceMergePreview}
          forceMergeBusy={forceMergeBusy}
          forceMergeResult={forceMergeResult}
          onReassignParent={(row) => {
            setReassignFor(row);
            setReassignQuery("");
            setReassignResults([]);
          }}
          onClose={() => {
            setFindPlayerOpen(false);
            setFindPlayerData(null);
            setForceMergeKeep(null);
            setForceMergeArchive({});
            setForceMergeReason("");
            setForceMergePreview(null);
            setForceMergeResult(null);
          }}
          onSearch={async () => {
            const g = (findPlayerGiven || "").trim();
            const l = (findPlayerLast || "").trim();
            if (!g && !l) {
              toast.error("Enter at least a given name or a last name");
              return;
            }
            setFindPlayerLoading(true);
            setFindPlayerData(null);
            setForceMergeKeep(null);
            setForceMergeArchive({});
            setForceMergePreview(null);
            setForceMergeResult(null);
            try {
              const res = await axios.get(
                `${API}/payments-hub/admin/diagnostic/find-players-by-name`,
                { params: { given_name: g, last_name: l }, withCredentials: true },
              );
              setFindPlayerData(res.data);
            } catch (err) {
              const __e = formatAdminError(err, "Lookup failed");
              toast.error(__e.message, __e.opts);
              setFindPlayerData({ error: err.response?.data?.detail || String(err) });
            } finally {
              setFindPlayerLoading(false);
            }
          }}
          onForceMergePreview={async () => {
            const archiveIds = Object.entries(forceMergeArchive)
              .filter(([, on]) => on)
              .map(([pid]) => pid)
              .filter((pid) => pid !== forceMergeKeep);
            if (!forceMergeKeep) {
              toast.error("Pick a Keep row first");
              return;
            }
            if (archiveIds.length === 0) {
              toast.error("Pick at least one Archive row");
              return;
            }
            setForceMergeBusy(true);
            setForceMergePreview(null);
            try {
              const res = await axios.post(
                `${API}/payments-hub/admin/diagnostic/force-merge-players`,
                {
                  keep_id: forceMergeKeep,
                  archive_ids: archiveIds,
                  dry_run: true,
                },
                { withCredentials: true },
              );
              setForceMergePreview(res.data);
            } catch (err) {
              const __e = formatAdminError(err, "Force-merge preview failed");
              toast.error(__e.message, __e.opts);
            } finally {
              setForceMergeBusy(false);
            }
          }}
          onForceMergeExecute={async () => {
            if (!forceMergePreview?.confirm_token) {
              toast.error("Run preview first to obtain a confirm token");
              return;
            }
            if (!forceMergeReason.trim()) {
              toast.error("Enter a reason — it's logged to the audit trail");
              return;
            }
            const archiveIds = forceMergePreview.archived_player_ids || [];
            const ok = window.confirm(
              `FORCE MERGE — bypasses birthdate / name match guards.\n\n` +
              `KEEP: ${forceMergePreview.kept_player_id}\n` +
              `ARCHIVE: ${archiveIds.join(", ")}\n\n` +
              `Archive reason will be tagged with "_force_merged" suffix.\n` +
              `Will be written to admin_audit_log.\n\nProceed?`
            );
            if (!ok) return;
            setForceMergeBusy(true);
            try {
              const res = await axios.post(
                `${API}/payments-hub/admin/diagnostic/force-merge-players`,
                {
                  keep_id: forceMergePreview.kept_player_id,
                  archive_ids: archiveIds,
                  dry_run: false,
                  confirm_token: forceMergePreview.confirm_token,
                  reason: forceMergeReason.trim(),
                },
                { withCredentials: true },
              );
              setForceMergeResult(res.data);
              toast.success(
                `Force merge complete. Archived ${archiveIds.length} row${archiveIds.length === 1 ? "" : "s"}. ` +
                `Repointed: ${Object.entries(res.data.repoint_stats || {}).filter(([, n]) => n > 0).map(([k, n]) => `${k}=${n}`).join(", ") || "(none)"}.`
              );
              // Refresh the find-by-name view so archived rows drop off
              const refreshed = await axios.get(
                `${API}/payments-hub/admin/diagnostic/find-players-by-name`,
                { params: { given_name: findPlayerGiven, last_name: findPlayerLast }, withCredentials: true },
              );
              setFindPlayerData(refreshed.data);
              setForceMergeKeep(null);
              setForceMergeArchive({});
              setForceMergePreview(null);
            } catch (err) {
              const __e = formatAdminError(err, "Force merge failed");
              toast.error(__e.message, __e.opts);
            } finally {
              setForceMergeBusy(false);
            }
          }}
        />
      )}

      {/* Reassign-parent modal (Aug 2026) — one-click fix for kids whose
          parent_id points at an archived placeholder. Search real parents
          by name, click one, confirm — Portal rewrites parent_id across
          every academy collection for this child. */}
      {reassignFor && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" data-testid="reassign-parent-modal">
          <div className="bg-slate-950 border border-cyan-500/30 rounded-xl w-full max-w-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-white font-semibold">Reassign child to a different parent</h3>
                <p className="text-xs text-slate-400 mt-1">
                  <span className="text-cyan-300 font-mono">{reassignFor.membership_number || reassignFor.player_id}</span> —
                  <span className="text-white ml-1">{reassignFor.child_name || reassignFor.name || "(no name)"}</span>
                </p>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Currently under: <span className="text-amber-300">{reassignFor.parent_name || "(placeholder / archived)"}</span>
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setReassignFor(null)} data-testid="reassign-close-btn">
                <X className="w-4 h-4" />
              </Button>
            </div>

            <label className="block text-xs text-slate-400 mb-1">Search real parent (name)</label>
            <input
              type="text"
              value={reassignQuery}
              onChange={async (e) => {
                const q = e.target.value;
                setReassignQuery(q);
                if (q.length < 2) {
                  setReassignResults([]);
                  return;
                }
                try {
                  const r = await axios.get(`${API}/users/search`, {
                    params: { q },
                    withCredentials: true,
                  });
                  setReassignResults(r.data || []);
                } catch (err) {
                  // silent — dropdown just shows empty
                }
              }}
              placeholder="e.g. Widya"
              className="w-full bg-slate-900 border border-slate-700 text-white rounded px-2 py-1.5 text-sm"
              data-testid="reassign-parent-search-input"
              autoFocus
            />

            <div className="mt-2 max-h-48 overflow-y-auto border border-slate-800 rounded bg-slate-900/40" data-testid="reassign-parent-results">
              {reassignResults.length === 0 && reassignQuery.length >= 2 && (
                <div className="p-3 text-xs text-slate-500 italic">No matching parents found.</div>
              )}
              {reassignResults.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  onClick={async () => {
                    if (!window.confirm(
                      `Reassign ${reassignFor.child_name || reassignFor.name} (${reassignFor.membership_number}) `
                      + `to parent ${u.name} (${u.email})?\n\n`
                      + "This will rewrite parent_id on: academy_players, academy_parent_registrations, "
                      + "academy_registrations, academy_enrollments, kit_entitlements, payments.\n\n"
                      + "Idempotent and reversible via a follow-up reassign."
                    )) return;
                    setReassignBusy(true);
                    try {
                      const r = await axios.post(
                        `${API}/admin/reassign-orphaned-child`,
                        {
                          child_player_id: reassignFor.player_id,
                          target_parent_id: u.id,
                        },
                        { withCredentials: true },
                      );
                      const counts = r.data?.reassigned || {};
                      const summary = Object.entries(counts)
                        .filter(([, n]) => n > 0)
                        .map(([k, n]) => `${k}=${n}`).join(", ") || "0 rows moved";
                      toast.success(`Reassigned. ${summary}.`);
                      // refresh the find-by-name list so the row now shows the correct parent
                      try {
                        const refreshed = await axios.get(
                          `${API}/payments-hub/admin/diagnostic/find-players-by-name`,
                          { params: { given_name: findPlayerGiven, last_name: findPlayerLast }, withCredentials: true },
                        );
                        setFindPlayerData(refreshed.data);
                      } catch (_) { /* silent — main action succeeded */ }
                      setReassignFor(null);
                    } catch (err) {
                      const __e = formatAdminError(err, "Reassign failed");
                      toast.error(__e.message, __e.opts);
                    } finally {
                      setReassignBusy(false);
                    }
                  }}
                  disabled={reassignBusy}
                  className="w-full text-left px-3 py-2 text-sm text-white hover:bg-cyan-500/10 border-b border-slate-800 last:border-b-0 disabled:opacity-50"
                  data-testid={`reassign-parent-result-${u.id}`}
                >
                  <div className="font-medium">{u.name}</div>
                  <div className="text-[10px] text-slate-500">{u.email}</div>
                </button>
              ))}
            </div>

            <p className="mt-3 text-[10px] text-slate-500 italic">
              After reassign, use Force Merge in the Find Player screen to consolidate the two Peter-style duplicate rows
              onto the older membership number.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function ArchiveOrphansModal({ loading, data, selections, setSelections, busy, result, onClose, onExecute }) {
  const orphans = data?.orphans || [];
  const checkedCount = Object.values(selections || {}).filter(Boolean).length;

  const setRow = (id, on) => setSelections({ ...selections, [id]: on });
  const setAll = (on) => {
    const next = {};
    for (const o of orphans) {
      if (o.id) next[o.id] = on;
    }
    setSelections(next);
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      data-testid="archive-orphans-modal"
      onClick={onClose}
    >
      <Card
        className="bg-slate-900 border-rose-500/30 w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <Trash2 className="w-5 h-5 text-rose-400" />
            <h2 className="text-lg font-semibold text-white">Archive Rejected Orphan Registrations</h2>
            {data && !data.error && (
              <span className="text-xs text-slate-400 ml-2">
                {orphans.length} found &middot; {checkedCount} ticked
              </span>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="archive-close-btn">
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {loading && (
            <div className="py-12 text-center">
              <Loader2 className="w-6 h-6 animate-spin text-rose-400 mx-auto" />
              <p className="text-slate-400 mt-2 text-sm">Loading orphan registrations...</p>
            </div>
          )}

          {!loading && data?.error && (
            <Card className="bg-red-900/20 border-red-500/40 p-4 text-red-300 text-sm">
              {data.error}
            </Card>
          )}

          {!loading && data && !data.error && (
            <>
              <p className="text-sm text-slate-400">
                These registrations have status <code className="text-rose-300">rejected</code> and have NO matching <code>payment_transactions</code> entry.
                Archiving moves them to <code className="text-rose-300">academy_parent_registrations_archive</code> with
                <code className="text-rose-300"> _archive_reason=rejected_orphan_no_payment_transaction</code>.
              </p>

              {orphans.length === 0 ? (
                <div className="py-8 text-center text-emerald-400">
                  <CheckCircle className="w-8 h-8 mx-auto mb-2" />
                  <p className="text-sm">No rejected orphan registrations found.</p>
                </div>
              ) : (
                <>
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => setAll(true)} className="text-xs border-slate-600" data-testid="archive-mark-all">Mark all</Button>
                    <Button size="sm" variant="outline" onClick={() => setAll(false)} className="text-xs border-slate-600" data-testid="archive-clear-all">Clear all</Button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-950/60 text-slate-400 uppercase">
                        <tr>
                          <th className="p-2 text-center w-12">Archive</th>
                          <th className="p-2 text-left">Registration ID</th>
                          <th className="p-2 text-left">Parent</th>
                          <th className="p-2 text-right">Amount</th>
                          <th className="p-2 text-left">Rejection Reason</th>
                          <th className="p-2 text-left">Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orphans.map((o) => (
                          <tr key={o.id} className="border-t border-slate-800" data-testid={`archive-row-${o.id}`}>
                            <td className="p-2 text-center">
                              <input
                                type="checkbox"
                                checked={!!selections[o.id]}
                                onChange={(e) => setRow(o.id, e.target.checked)}
                                className="rounded border-slate-600 bg-slate-800"
                                data-testid={`archive-tick-${o.id}`}
                              />
                            </td>
                            <td className="p-2 font-mono text-slate-300 text-[11px]">{o.id?.slice(0, 14)}...</td>
                            <td className="p-2 text-slate-300">{o.parent_email || "-"}</td>
                            <td className="p-2 text-right text-slate-300">${Number(o.total_amount || 0).toFixed(2)}</td>
                            <td className="p-2 text-slate-400 truncate max-w-xs">{o.rejection_reason || "-"}</td>
                            <td className="p-2 text-slate-500">{(o.created_at || "").slice(0, 10)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {result && (
                <Card className="bg-slate-950/60 border-rose-500/30 p-3" data-testid="archive-result-card">
                  {result.error ? (
                    <div className="text-red-300 text-xs">{result.error}</div>
                  ) : (
                    <div className="text-xs space-y-1 font-mono text-slate-300">
                      <div className="text-rose-300 font-semibold mb-1">EXECUTED</div>
                      <div>archived: <span className="text-emerald-300">{result.archived_count}</span> of {result.target_count}</div>
                      {result.archived_count !== result.target_count && (
                        <div className="text-amber-300">⚠ {result.target_count - result.archived_count} row(s) failed - check backend logs</div>
                      )}
                    </div>
                  )}
                </Card>
              )}
            </>
          )}
        </div>

        <div className="border-t border-slate-800 p-4 flex items-center justify-between bg-slate-950/40">
          <div className="text-xs text-slate-400">
            Will archive <span className="text-rose-300 font-semibold">{checkedCount}</span> of {orphans.length} orphan registration{orphans.length === 1 ? "" : "s"}
          </div>
          <div className="flex gap-2">
            <Button onClick={onClose} variant="ghost" className="text-slate-400" data-testid="archive-cancel-btn">
              Close
            </Button>
            <Button
              onClick={onExecute}
              disabled={busy || loading || !data || data.error || checkedCount === 0 || !!result}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="archive-execute-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Execute & Audit
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

function DmHealthModal({ loading, data, onClose, onDownload }) {
  const rows = (data && !data.error) ? (data.rows || []) : [];
  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      data-testid="dm-health-modal"
      onClick={onClose}
    >
      <Card
        className="bg-slate-900 border-teal-500/30 w-full max-w-6xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-teal-400" />
            <h2 className="text-lg font-semibold text-white">DM Health - Revenue Leakage</h2>
            {data && !data.error && (
              <span className="text-xs text-slate-400 ml-2">
                scanned {data.scanned} &middot; flagged <span className="text-teal-300 font-semibold">{data.leakage_count}</span> &middot; total at risk: <span className="text-teal-300 font-semibold">${Number(data.total_leaked_amount || 0).toFixed(2)}</span>
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm" variant="outline" onClick={onDownload}
              disabled={!data || data.error || loading}
              className="text-xs border-teal-500/40 text-teal-300 hover:bg-teal-500/10"
              data-testid="dm-health-download-btn"
            >
              <Download className="w-3 h-3 mr-1" /> JSON
            </Button>
            <Button variant="ghost" size="sm" onClick={onClose} data-testid="dm-health-close-btn">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {loading && (
            <div className="py-12 text-center">
              <Loader2 className="w-6 h-6 animate-spin text-teal-400 mx-auto" />
              <p className="text-slate-400 mt-2 text-sm">Scanning paid registrations...</p>
            </div>
          )}
          {!loading && data?.error && (
            <Card className="bg-red-900/20 border-red-500/40 p-4 text-red-300 text-sm">{data.error}</Card>
          )}
          {!loading && data && !data.error && (
            <>
              <p className="text-sm text-slate-400 mb-3">
                Paid registrations where <code className="text-teal-300">fee_breakdown.dm_origin == "pre_existing"</code> but the parent's DM has since expired or been cleared.
                These households received the 10% academy discount on the back of a DM that is no longer being paid for.
              </p>
              {rows.length === 0 ? (
                <div className="py-8 text-center text-emerald-400">
                  <CheckCircle className="w-8 h-8 mx-auto mb-2" />
                  <p className="text-sm">No DM revenue leakage detected.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-950/60 text-slate-400 uppercase">
                      <tr>
                        <th className="p-2 text-left">Parent</th>
                        <th className="p-2 text-left">Email</th>
                        <th className="p-2 text-left">Ref</th>
                        <th className="p-2 text-right">Amount</th>
                        <th className="p-2 text-left">Paid</th>
                        <th className="p-2 text-left">DM Expired</th>
                        <th className="p-2 text-left">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.txn_id} className="border-t border-slate-800" data-testid={`dm-health-row-${r.txn_id}`}>
                          <td className="p-2 text-slate-200">{r.parent_name || "-"}</td>
                          <td className="p-2 text-slate-300">{r.parent_email || "-"}</td>
                          <td className="p-2 font-mono text-slate-300">{r.payment_reference || r.txn_id?.slice(0, 8)}</td>
                          <td className="p-2 text-right text-slate-200">${Number(r.amount_invoiced || 0).toFixed(2)}</td>
                          <td className="p-2 text-slate-500">{(r.paid_at || "").slice(0, 10)}</td>
                          <td className="p-2 text-slate-500">{r.dm_expired_at ? r.dm_expired_at.slice(0, 10) : "-"}</td>
                          <td className="p-2">
                            <Badge className={r.reason === "dm_expired" ? "bg-amber-500/20 text-amber-300" : "bg-rose-500/20 text-rose-300"}>
                              {r.reason}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </Card>
    </div>
  );
}


function RelinkTxnModal({ form, setForm, plan, busy, onClose, onPreview, onExecute }) {
  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      data-testid="relink-txn-modal"
      onClick={onClose}
    >
      <Card
        className="bg-slate-900 border-cyan-500/30 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <Link2 className="w-5 h-5 text-cyan-400" />
            <h2 className="text-lg font-semibold text-white">Relink Transaction Parent</h2>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="relink-close-btn">
            <X className="w-4 h-4" />
          </Button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-slate-400">
            Re-points a payment_transactions row from its current parent (typically a deleted user)
            to an active parent user. Audit-logged with full before/after snapshot.
          </p>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Transaction ID</label>
            <Input
              value={form.txn_id}
              onChange={(e) => setForm({ ...form, txn_id: e.target.value })}
              placeholder="ed47184a-d203-4dd0-b7a8-1ba1c3f897a8"
              className="bg-slate-800 border-slate-700 text-white font-mono text-xs"
              data-testid="relink-txn-id-input"
            />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">New Parent User ID (active account)</label>
            <Input
              value={form.new_parent_id}
              onChange={(e) => setForm({ ...form, new_parent_id: e.target.value })}
              placeholder="07518b02-b05e-4168-b92f-869564bbfabd"
              className="bg-slate-800 border-slate-700 text-white font-mono text-xs"
              data-testid="relink-new-parent-id-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="relink-update-user-id"
              checked={form.update_user_id}
              onChange={(e) => setForm({ ...form, update_user_id: e.target.checked })}
              className="rounded border-slate-600 bg-slate-800"
              data-testid="relink-update-user-id-checkbox"
            />
            <label htmlFor="relink-update-user-id" className="text-sm text-slate-300">
              Also update user_id (controls who sees this txn in their dashboard)
            </label>
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Reason (&gt;=10 chars, required for execute)</label>
            <Textarea
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              placeholder="Re-linked to active parent account - original account deleted, same person confirmed."
              className="bg-slate-800 border-slate-700 text-white text-sm"
              rows={3}
              data-testid="relink-reason-input"
            />
          </div>
          {plan && (
            <Card className="bg-slate-950/60 border-cyan-500/20 p-3">
              <div className="text-xs text-cyan-300 font-semibold mb-2">
                {plan.dry_run ? "DRY RUN - Preview" : "EXECUTED"}
              </div>
              <div className="text-xs text-slate-300 space-y-1 font-mono">
                <div>txn: <span className="text-white">{plan.txn_id}</span> &middot; ref: {plan.payment_reference || "-"} &middot; ${plan.amount?.toFixed?.(2) ?? plan.amount}</div>
                <div>status: <Badge variant="outline" className="text-xs">{plan.payment_status}</Badge></div>
                <div className="pt-1 border-t border-slate-800 mt-1">
                  <div className="text-rose-300">BEFORE: parent_id={plan.before?.parent_id || "-"} &middot; user_id={plan.before?.user_id || "-"}</div>
                  <div className="text-emerald-300">AFTER:&nbsp; parent_id={plan.after?.parent_id} &middot; user_id={plan.after?.user_id} ({plan.new_parent_name || plan.after?.parent_email})</div>
                </div>
                {plan.note && <div className="pt-2 text-yellow-300">{plan.note}</div>}
              </div>
            </Card>
          )}
          <div className="flex gap-2 pt-2">
            <Button
              onClick={onPreview}
              disabled={busy || !form.txn_id || !form.new_parent_id}
              variant="outline"
              className="border-slate-600 text-slate-200 hover:bg-slate-800"
              data-testid="relink-preview-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Preview (dry-run)
            </Button>
            <Button
              onClick={onExecute}
              disabled={busy || !plan || !plan.dry_run || !form.reason || form.reason.trim().length < 10}
              className="bg-cyan-600 hover:bg-cyan-700 text-white"
              data-testid="relink-execute-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Execute & Audit
            </Button>
            <Button onClick={onClose} variant="ghost" className="text-slate-400" data-testid="relink-cancel-btn">
              Close
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

function BackfillRegIdModal({ loading, data, selections, setSelections, busy, result, onClose, onExecute }) {
  const rows = data?.rows || [];
  const autoFixable = rows.filter((r) => r.is_auto_fixable);
  const ambiguous = rows.filter((r) => !r.is_auto_fixable && (r.candidates || []).filter((c) => c.confidence >= 5).length >= 2);
  const noMatch = rows.filter((r) => !r.is_auto_fixable && (r.candidates || []).length === 0);
  const other = rows.filter((r) => !r.is_auto_fixable && !ambiguous.includes(r) && !noMatch.includes(r));

  const ambigChoice = selections.ambiguousChoice || {};
  const legacyTicks = selections.noMatchMarkLegacy || {};
  const forcedCount = Object.values(ambigChoice).filter(Boolean).length;
  const legacyCount = Object.values(legacyTicks).filter(Boolean).length;

  const setAmbig = (txnId, regId) =>
    setSelections({ ...selections, ambiguousChoice: { ...ambigChoice, [txnId]: regId } });
  const setLegacy = (txnId, on) =>
    setSelections({ ...selections, noMatchMarkLegacy: { ...legacyTicks, [txnId]: on } });
  const setAllLegacy = (on) => {
    const next = {};
    for (const r of noMatch) next[r.txn_id] = on;
    setSelections({ ...selections, noMatchMarkLegacy: next });
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      data-testid="backfill-regid-modal"
      onClick={onClose}
    >
      <Card
        className="bg-slate-900 border-fuchsia-500/30 w-full max-w-6xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <Wrench className="w-5 h-5 text-fuchsia-400" />
            <h2 className="text-lg font-semibold text-white">Backfill Missing registration_id</h2>
            {data && (
              <span className="text-xs text-slate-400 ml-2">
                {rows.length} eligible &middot; {autoFixable.length} auto-fix &middot; {ambiguous.length} ambiguous &middot; {noMatch.length} no-match{other.length > 0 ? ` · ${other.length} other` : ""}
              </span>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="backfill-close-btn">
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          {loading && (
            <div className="py-12 text-center">
              <Loader2 className="w-6 h-6 animate-spin text-fuchsia-400 mx-auto" />
              <p className="text-slate-400 mt-2 text-sm">Inspecting transactions...</p>
            </div>
          )}

          {!loading && data?.error && (
            <Card className="bg-red-900/20 border-red-500/40 p-4 text-red-300 text-sm">
              {data.error}
            </Card>
          )}

          {!loading && data && !data.error && (
            <>
              <section data-testid="backfill-section-auto">
                <h3 className="text-emerald-300 font-semibold text-sm mb-2 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" /> Auto-fixable ({autoFixable.length})
                </h3>
                <p className="text-xs text-slate-400 mb-2">
                  Single high-confidence candidate (parent + term + amount match). These will be patched automatically.
                </p>
                {autoFixable.length === 0 ? (
                  <div className="text-xs text-slate-500 italic">None.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-950/60 text-slate-400 uppercase">
                        <tr><th className="p-2 text-left">Txn / Ref</th><th className="p-2 text-left">Parent</th><th className="p-2 text-right">Amount</th><th className="p-2 text-left">to Registration</th></tr>
                      </thead>
                      <tbody>
                        {autoFixable.map((r) => (
                          <tr key={r.txn_id} className="border-t border-slate-800" data-testid={`backfill-auto-row-${r.txn_id}`}>
                            <td className="p-2 font-mono text-slate-300">{r.payment_reference || r.txn_id?.slice(0, 8)}</td>
                            <td className="p-2 text-slate-300">{r.parent_email || "-"}</td>
                            <td className="p-2 text-right text-slate-300">${Number(r.amount || 0).toFixed(2)}</td>
                            <td className="p-2 font-mono text-emerald-300 text-[11px]">{r.suggested_registration_id?.slice(0, 12)}...</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              <section data-testid="backfill-section-ambig">
                <h3 className="text-amber-300 font-semibold text-sm mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" /> Ambiguous - admin must pick ({ambiguous.length})
                </h3>
                <p className="text-xs text-slate-400 mb-2">
                  Two or more registrations match equally. The earliest is pre-selected as default - change if needed.
                </p>
                {ambiguous.length === 0 ? (
                  <div className="text-xs text-slate-500 italic">None.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-950/60 text-slate-400 uppercase">
                        <tr><th className="p-2 text-left">Txn / Ref</th><th className="p-2 text-left">Parent</th><th className="p-2 text-right">Amount</th><th className="p-2 text-left">Pick registration</th></tr>
                      </thead>
                      <tbody>
                        {ambiguous.map((r) => {
                          const choices = (r.candidates || []).filter((c) => c.confidence >= 5);
                          const sortedChoices = [...choices].sort((a, b) => (a.reg_created_at || "").localeCompare(b.reg_created_at || ""));
                          return (
                            <tr key={r.txn_id} className="border-t border-slate-800" data-testid={`backfill-ambig-row-${r.txn_id}`}>
                              <td className="p-2 font-mono text-slate-300">{r.payment_reference || r.txn_id?.slice(0, 8)}</td>
                              <td className="p-2 text-slate-300">{r.parent_email || "-"}</td>
                              <td className="p-2 text-right text-slate-300">${Number(r.amount || 0).toFixed(2)}</td>
                              <td className="p-2">
                                <select
                                  value={ambigChoice[r.txn_id] || ""}
                                  onChange={(e) => setAmbig(r.txn_id, e.target.value)}
                                  className="bg-slate-800 border border-slate-700 text-white text-xs rounded px-2 py-1 w-full font-mono"
                                  data-testid={`backfill-ambig-select-${r.txn_id}`}
                                >
                                  <option value="">-- skip this row --</option>
                                  {sortedChoices.map((c) => (
                                    <option key={c.registration_id} value={c.registration_id}>
                                      {c.registration_id.slice(0, 12)}... &middot; ${Number(c.reg_total_amount || 0).toFixed(2)} &middot; {(c.reg_created_at || "").slice(0, 10)} &middot; {c.reg_status}
                                    </option>
                                  ))}
                                </select>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              <section data-testid="backfill-section-nomatch">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-slate-300 font-semibold text-sm flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" /> No-match - historical residue ({noMatch.length})
                  </h3>
                  {noMatch.length > 0 && (
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => setAllLegacy(true)} className="text-xs border-slate-600" data-testid="backfill-legacy-all-on">Mark all</Button>
                      <Button size="sm" variant="outline" onClick={() => setAllLegacy(false)} className="text-xs border-slate-600" data-testid="backfill-legacy-all-off">Clear all</Button>
                    </div>
                  )}
                </div>
                <p className="text-xs text-slate-400 mb-2">
                  No candidate registration exists - typically Term 1 legacy records. Marking them as <code className="text-fuchsia-300">legacy_unlinked</code> hides them from future integrity reports.
                </p>
                {noMatch.length === 0 ? (
                  <div className="text-xs text-slate-500 italic">None.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-950/60 text-slate-400 uppercase">
                        <tr><th className="p-2 text-center w-12">Mark</th><th className="p-2 text-left">Txn / Ref</th><th className="p-2 text-left">Parent</th><th className="p-2 text-right">Amount</th><th className="p-2 text-left">Created</th></tr>
                      </thead>
                      <tbody>
                        {noMatch.map((r) => (
                          <tr key={r.txn_id} className="border-t border-slate-800" data-testid={`backfill-nomatch-row-${r.txn_id}`}>
                            <td className="p-2 text-center">
                              <input
                                type="checkbox"
                                checked={!!legacyTicks[r.txn_id]}
                                onChange={(e) => setLegacy(r.txn_id, e.target.checked)}
                                className="rounded border-slate-600 bg-slate-800"
                                data-testid={`backfill-legacy-${r.txn_id}`}
                              />
                            </td>
                            <td className="p-2 font-mono text-slate-300">{r.payment_reference || r.txn_id?.slice(0, 8)}</td>
                            <td className="p-2 text-slate-300">{r.parent_email || "-"}</td>
                            <td className="p-2 text-right text-slate-300">${Number(r.amount || 0).toFixed(2)}</td>
                            <td className="p-2 text-slate-500">{(r.created_at || "").slice(0, 10)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              {other.length > 0 && (
                <section data-testid="backfill-section-other">
                  <h3 className="text-slate-400 font-semibold text-sm mb-2">Other (low-confidence single match) ({other.length})</h3>
                  <p className="text-xs text-slate-500 italic">These have one weak candidate (e.g. amount close but term does not match). Not auto-patched. Review individually if needed.</p>
                </section>
              )}

              {result && (
                <Card className="bg-slate-950/60 border-fuchsia-500/30 p-3" data-testid="backfill-result-card">
                  {result.error ? (
                    <div className="text-red-300 text-xs">{result.error}</div>
                  ) : (
                    <div className="text-xs space-y-1 font-mono text-slate-300">
                      <div className="text-fuchsia-300 font-semibold mb-1">EXECUTED</div>
                      <div>auto-patched: <span className="text-emerald-300">{result.patched_count}</span></div>
                      <div>admin-resolved: <span className="text-amber-300">{result.forced_patched_count}</span></div>
                      <div>marked legacy_unlinked: <span className="text-slate-300">{result.legacy_stamped_count}</span></div>
                      <div>skipped ambiguous: {result.skipped_ambiguous_count}</div>
                      <div>skipped no-match: {result.skipped_no_match_count}</div>
                      {(result.forced_invalid || []).length > 0 && (
                        <div className="text-red-300 pt-1">forced_invalid: {JSON.stringify(result.forced_invalid)}</div>
                      )}
                    </div>
                  )}
                </Card>
              )}
            </>
          )}
        </div>

        <div className="border-t border-slate-800 p-4 flex items-center justify-between bg-slate-950/40">
          <div className="text-xs text-slate-400">
            Will patch <span className="text-emerald-300 font-semibold">{autoFixable.length}</span> auto + <span className="text-amber-300 font-semibold">{forcedCount}</span> admin-resolved &middot; Mark <span className="text-fuchsia-300 font-semibold">{legacyCount}</span> as legacy
          </div>
          <div className="flex gap-2">
            <Button onClick={onClose} variant="ghost" className="text-slate-400" data-testid="backfill-cancel-btn">
              Close
            </Button>
            <Button
              onClick={onExecute}
              disabled={busy || loading || !data || data.error || (autoFixable.length + forcedCount + legacyCount === 0) || !!result}
              className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
              data-testid="backfill-execute-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Execute & Audit
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}


function HygieneAuditModal({ loading, data, onClose, onRerun, onBackfill, onRunAllConfirmed }) {
  const downloadJson = () => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `data-hygiene-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const s = data?.summary;
  const isBundleResult = data?._is_bundle_result;
  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      data-testid="hygiene-audit-modal"
      onClick={onClose}
    >
      <Card
        className="bg-slate-900 border-cyan-500/30 w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <Stethoscope className="w-5 h-5 text-cyan-400" />
            <h2 className="text-lg font-semibold text-white">Data Hygiene Audit</h2>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onRerun} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-1" />} Re-audit
            </Button>
            {onBackfill && !data?._is_backfill_result && !isBundleResult && (
              <>
                <Button
                  variant="outline" size="sm"
                  onClick={() => onBackfill(true)}
                  disabled={loading}
                  className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                  data-testid="hygiene-backfill-dryrun-btn"
                  title="Preview the backfill plan without writing"
                >
                  Preview backfill
                </Button>
                <Button
                  size="sm"
                  onClick={() => onBackfill(false)}
                  disabled={loading}
                  className="bg-emerald-600 hover:bg-emerald-500 text-white"
                  data-testid="hygiene-backfill-run-btn"
                  title="Run the backfill (idempotent — re-runs are skipped)"
                >
                  Run backfill
                </Button>
              </>
            )}
            {onRunAllConfirmed && !data?._is_backfill_result && (
              <>
                <Button
                  variant="outline" size="sm"
                  onClick={() => onRunAllConfirmed(true)}
                  disabled={loading}
                  className="border-fuchsia-500/40 text-fuchsia-300 hover:bg-fuchsia-500/10"
                  data-testid="bundle-dryrun-btn"
                  title="Preview all 3 confirmed cleanups combined"
                >
                  Preview all cleanups
                </Button>
                <Button
                  size="sm"
                  onClick={() => onRunAllConfirmed(false)}
                  disabled={loading || !isBundleResult}
                  className="bg-fuchsia-600 hover:bg-fuchsia-500 text-white disabled:opacity-40"
                  data-testid="bundle-execute-btn"
                  title={isBundleResult ? "Run hygiene + Sarah cascade + abandoned Stripe cleanup in sequence" : "Run dry-run first"}
                >
                  Run all confirmed
                </Button>
              </>
            )}
            {data && !loading && (
              <Button variant="outline" size="sm" onClick={downloadJson} data-testid="hygiene-download-btn">
                <Download className="w-4 h-4 mr-1" /> JSON
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={onClose}><X className="w-4 h-4" /></Button>
          </div>
        </div>

        <div className="overflow-auto p-4 text-sm">
          {loading && (
            <div className="flex items-center gap-2 text-slate-400">
              <Loader2 className="w-4 h-4 animate-spin" /> Running hygiene audit…
            </div>
          )}

          {!loading && data?.error && (
            <div className="bg-red-500/10 border border-red-500/30 p-3 rounded text-red-300">{data.error}</div>
          )}

          {/* ─── Bundle result render ─── */}
          {!loading && isBundleResult && (
            <div className="space-y-3" data-testid="bundle-result">
              <div className={`p-3 rounded border ${data.dry_run ? "bg-fuchsia-500/10 border-fuchsia-500/40" : "bg-emerald-500/10 border-emerald-500/40"}`}>
                <p className={`font-medium ${data.dry_run ? "text-fuchsia-200" : "text-emerald-200"}`}>
                  {data.dry_run
                    ? `⚠ DRY-RUN — nothing was written. Preview of hygiene + Sarah cascade + abandoned Stripe combined.`
                    : `✓ Bundle executed. Total writes: ${data.summary?.total_writes ?? 0} · Errors: ${data.summary?.errors ?? 0}`}
                </p>
                <p className="text-xs mt-1 text-slate-400">bundle_version: {data.bundle_version}</p>
              </div>
              {(data.steps || []).map((step, i) => (
                <Card key={i} className={`p-3 border ${step.ok ? "bg-slate-800/40 border-slate-700" : "bg-red-500/10 border-red-500/40"}`}>
                  <div className="flex items-center justify-between">
                    <p className="text-cyan-300 font-medium uppercase text-xs">
                      {step.ok ? "✓" : "✗"} Step {i + 1}: {step.step.replaceAll("_", " ")}
                    </p>
                    {!step.ok && <span className="text-red-300 text-xs">FAILED</span>}
                  </div>
                  {step.error && (
                    <p className="mt-1 text-xs text-red-300 italic">{step.error}</p>
                  )}
                  {step.result && (
                    <details className="mt-2">
                      <summary className="text-[11px] text-slate-400 cursor-pointer hover:text-white">View step payload</summary>
                      <pre className="mt-1 text-[10px] text-slate-400 whitespace-pre-wrap max-h-60 overflow-auto">{JSON.stringify(step.result, null, 2)}</pre>
                    </details>
                  )}
                </Card>
              ))}
              {data.dry_run && (
                <div className="bg-amber-500/10 border border-amber-500/40 p-3 rounded text-amber-200 text-xs">
                  Review each step above. When ready, click <strong>Run all confirmed</strong> to execute in sequence.
                </div>
              )}
            </div>
          )}

          {/* ─── Backfill result render ─── */}
          {!loading && data?._is_backfill_result && data?.actions && (
            <div className="space-y-3" data-testid="hygiene-backfill-result">
              {data.skipped && (
                <div className="bg-amber-500/10 border border-amber-500/40 p-3 rounded">
                  <p className="text-amber-200 font-medium">Backfill already run — skipped.</p>
                  <p className="text-amber-300/80 text-xs mt-1">{data.reason}</p>
                  <pre className="mt-2 text-[10px] text-amber-300/60 whitespace-pre-wrap">{JSON.stringify(data.previous_run, null, 2)}</pre>
                </div>
              )}
              {data.dry_run && (
                <div className="bg-amber-500/10 border border-amber-500/40 p-3 rounded text-amber-200 text-xs">
                  ⚠ DRY-RUN — nothing was written. Review the plan and click "Run backfill" to execute.
                </div>
              )}
              {data.actions.map((act, i) => (
                <Card key={i} className="p-3 bg-slate-800/40 border-slate-700">
                  <p className="text-cyan-300 font-medium uppercase text-xs">{act.step.replaceAll("_", " ")}</p>
                  {act.step === "section_a_safe_amount_invoiced_backfills" && (
                    <div className="mt-2 text-sm">
                      <p>Filled: <strong className="text-emerald-300">{act.filled}</strong> · Overrides: <strong className="text-fuchsia-300">{act.overridden_with_confirmed_values}</strong> · Skipped (no value): <strong className="text-slate-400">{act.skipped_no_value}</strong></p>
                    </div>
                  )}
                  {act.step === "section_c_confirmed_test_records_being_deleted" && (
                    <div className="mt-2 text-sm">
                      {act.policy_note && (
                        <p className="text-[11px] text-slate-400 italic mb-2">📋 {act.policy_note}</p>
                      )}
                      <p className="text-red-300">Deleted: <strong>{act.deleted_count}</strong> test row{act.deleted_count !== 1 ? "s" : ""}</p>
                      <ul className="text-[11px] text-slate-400 mt-1 space-y-0.5">
                        {(act.deleted_rows || []).map((d) => <li key={d.txn_id}>{d.payment_reference} · {d.parent_email} · ${d.amount} · {d.parent_name}</li>)}
                      </ul>
                      {act.skipped_for_safety_count > 0 && (
                        <div className="mt-3 bg-amber-500/10 border border-amber-500/40 p-2 rounded">
                          <p className="text-amber-200 text-[11px] font-medium uppercase mb-1">
                            ⚠ {act.skipped_for_safety_count} row{act.skipped_for_safety_count > 1 ? "s" : ""} skipped for safety
                          </p>
                          <ul className="text-[11px] space-y-1">
                            {(act.skipped_for_safety_rows || []).map((s) => (
                              <li key={s.txn_id} className="text-amber-300/90">
                                <strong>{s.payment_reference}</strong> · {s.parent_name || "—"} · ${s.amount} · <em>{s.payment_status}</em>
                                <p className="text-amber-300/60 italic ml-2">↳ {s.skip_reason}</p>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                  {act.step === "section_b_orphans_being_preserved" && (
                    <div className="mt-2 text-sm space-y-3">
                      {act.policy_note && (
                        <p className="text-[11px] text-slate-400 italic">📋 {act.policy_note}</p>
                      )}
                      {act.preserve_as_financial_record_count > 0 && (
                        <div className="bg-emerald-500/10 border border-emerald-500/40 p-3 rounded">
                          <p className="text-emerald-300 font-medium uppercase text-[11px] mb-2">
                            💰 Preserved as financial record ({act.preserve_as_financial_record_count})
                          </p>
                          <div className="space-y-1 max-h-48 overflow-auto">
                            {(act.preserve_as_financial_record || []).map((r) => (
                              <div key={r.txn_id} className="text-[11px] grid grid-cols-12 gap-2 text-emerald-200">
                                <span className="col-span-3 font-mono">{r.payment_reference}</span>
                                <span className="col-span-3 truncate">{r.parent_name || r.parent_email || "—"}</span>
                                <span className="col-span-2 font-mono">${r.amount?.toFixed?.(2) ?? "?"}</span>
                                <span className="col-span-2">{r.payment_status}</span>
                                <span className="col-span-2 text-emerald-200/60">{r.preservation_token_match}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {act.flagged_for_manual_review_count > 0 && (
                        <div className="bg-red-500/10 border border-red-500/40 p-3 rounded">
                          <p className="text-red-300 font-medium uppercase text-[11px] mb-1">⚠ Flagged for manual review ({act.flagged_for_manual_review_count})</p>
                          {(act.flagged_for_manual_review || []).map((r) => (
                            <div key={r.txn_id} className="text-xs text-red-200">
                              <p><strong>{r.payment_reference}</strong> · {r.parent_name} · ${r.amount} · {r.payment_status}</p>
                              <p className="text-red-300/70 italic">{r.manual_review_reason}</p>
                            </div>
                          ))}
                        </div>
                      )}
                      <p className="text-amber-300 text-[11px]">Remaining orphans ({act.remaining_orphans_count}) — paid/real-name rows highlighted, no action taken:</p>
                      <div className="space-y-1 max-h-72 overflow-auto">
                        {(act.remaining_orphans || []).map((r) => (
                          <div key={r.txn_id} className={`text-[11px] p-2 rounded grid grid-cols-12 gap-2 ${r.is_paid_real_money ? "bg-emerald-500/10 border border-emerald-500/30" : "bg-slate-800/30"}`}>
                            <span className="col-span-3 font-mono text-white">{r.payment_reference}</span>
                            <span className="col-span-3 text-slate-300 truncate">{r.parent_name || r.parent_email || "—"}</span>
                            <span className="col-span-2 font-mono text-emerald-300">${r.amount?.toFixed?.(2) ?? "?"}</span>
                            <span className="col-span-2">{r.payment_status}{r.is_paid_real_money && " 💰"}</span>
                            <span className="col-span-2 text-slate-500">{r.created_at?.slice(0, 10)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {act.step === "section_d_flagged_for_admin_review" && (
                    <div className="mt-2 text-sm space-y-3">
                      {act.policy_note && (
                        <p className="text-[11px] text-slate-400 italic">📋 {act.policy_note}</p>
                      )}
                      {(act.records || []).map((rec) => (
                        <div key={rec.email} className="bg-fuchsia-500/10 border border-fuchsia-500/30 p-2 rounded text-[11px]">
                          <p className="text-fuchsia-300 font-medium">
                            {rec.email} · matches: <strong>{rec.match_count}</strong>
                            {rec.expected_amount != null && <span className="text-slate-400"> · expected ~${rec.expected_amount}</span>}
                          </p>
                          <p className="text-slate-300 italic mt-0.5">↳ {rec.review_question}</p>
                          {(rec.matches || []).length > 0 && (
                            <ul className="mt-1 ml-3 space-y-0.5 text-slate-400">
                              {rec.matches.map((m) => (
                                <li key={m.id}>{m.payment_reference} · ${m.amount} · {m.payment_status} · {m.created_at?.slice(0, 10)}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {act.step === "section_e_unbackfillable_missing_emails_for_manual_lookup" && (
                    <div className="mt-2 text-sm">
                      <p className="text-amber-300 mb-2">Cannot backfill from any source — manual lookup needed for these <strong>{act.count}</strong>:</p>
                      <div className="space-y-1">
                        {(act.rows || []).map((r) => (
                          <div key={r.txn_id} className="text-[11px] p-2 rounded grid grid-cols-12 gap-2 bg-slate-800/30">
                            <span className="col-span-3 font-mono text-white">{r.payment_reference}</span>
                            <span className="col-span-4 text-slate-200">{r.parent_name}</span>
                            <span className="col-span-2 font-mono text-emerald-300">${r.amount?.toFixed?.(2) ?? "?"}</span>
                            <span className="col-span-3 text-slate-500">{r.created_at?.slice(0, 10)} · {r.payment_status}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}

          {!loading && s && !data?._is_backfill_result && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                <Card className="p-3 bg-amber-500/10 border-amber-500/40">
                  <p className="text-[11px] uppercase text-amber-300">Missing amount_invoiced</p>
                  <p className="text-xl font-mono text-white">{s.missing_amount_invoiced}</p>
                  <p className="text-[10px] text-emerald-300">{s.missing_amount_invoiced_backfillable} backfillable from fee_breakdown</p>
                </Card>
                <Card className="p-3 bg-amber-500/10 border-amber-500/40">
                  <p className="text-[11px] uppercase text-amber-300">Missing parent_email</p>
                  <p className="text-xl font-mono text-white">{s.missing_parent_email}</p>
                  <p className="text-[10px] text-emerald-300">{s.missing_parent_email_backfillable} backfillable from users/registrations</p>
                </Card>
                <Card className="p-3 bg-red-500/10 border-red-500/40">
                  <p className="text-[11px] uppercase text-red-300">Orphans</p>
                  <p className="text-xl font-mono text-white">{s.orphan_count}</p>
                  <p className="text-[10px] text-slate-400">no parent + no registration</p>
                </Card>
              </div>

              {data.orphans?.length > 0 && (
                <details open className="mb-4">
                  <summary className="cursor-pointer text-red-300 font-medium mb-2">
                    Orphan transactions ({data.orphans.length})
                  </summary>
                  <div className="space-y-2" data-testid="hygiene-orphans">
                    {data.orphans.map((r) => (
                      <Card key={r.txn_id} className="p-3 bg-red-500/5 border-red-500/40">
                        <div className="flex justify-between mb-1">
                          <p className="text-white font-medium">{r.payment_reference || r.txn_id}</p>
                          <p className="text-[11px] text-slate-400 font-mono">{r.created_at?.slice(0, 19) || "?"}</p>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-[11px]">
                          <div><span className="text-slate-500">Type</span> <span className="text-white">{r.payment_type || "—"}</span></div>
                          <div><span className="text-slate-500">Amount</span> <span className="text-emerald-300">${r.amount?.toFixed?.(2) ?? r.amount ?? "?"}</span></div>
                          <div><span className="text-slate-500">Status</span> <span className="text-white">{r.payment_status || "—"}</span></div>
                          <div><span className="text-slate-500">Parent</span> <span className="text-white">{r.parent_name || r.parent_email || "—"}</span></div>
                          <div><span className="text-slate-500">parent_id</span> <span className="text-white font-mono">{r.parent_id || "—"}{r.parent_id && !r.parent_id_resolves_to_user && " ❌"}</span></div>
                          <div><span className="text-slate-500">registration_id</span> <span className="text-white font-mono">{r.registration_id || "—"}{r.registration_id && !r.registration_id_resolves_to_reg && " ❌"}</span></div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </details>
              )}

              {data.missing_amount_invoiced?.length > 0 && (
                <details className="mb-4">
                  <summary className="cursor-pointer text-amber-300 font-medium mb-2">
                    Missing amount_invoiced ({data.missing_amount_invoiced.length})
                  </summary>
                  <div className="space-y-1 text-[11px]">
                    {data.missing_amount_invoiced.slice(0, 50).map((r) => (
                      <div key={r.txn_id} className="grid grid-cols-12 gap-2 p-2 bg-slate-800/30 rounded">
                        <span className="col-span-3 text-white font-mono truncate">{r.payment_reference}</span>
                        <span className="col-span-3 text-slate-300 truncate">{r.parent_email || "—"}</span>
                        <span className="col-span-2 text-emerald-300 font-mono">${r.amount?.toFixed?.(2) ?? "?"}</span>
                        <span className="col-span-2 text-cyan-300 font-mono" title="proposed backfill">
                          → ${r.proposed_backfill_value?.toFixed?.(2) ?? "—"}
                        </span>
                        <span className="col-span-2 text-slate-400 truncate">{r.payment_status}</span>
                      </div>
                    ))}
                    {data.missing_amount_invoiced.length > 50 && (
                      <p className="text-slate-500 text-center py-2">… {data.missing_amount_invoiced.length - 50} more (download JSON for full list)</p>
                    )}
                  </div>
                </details>
              )}

              {data.missing_parent_email?.length > 0 && (
                <details className="mb-4">
                  <summary className="cursor-pointer text-amber-300 font-medium mb-2">
                    Missing parent_email ({data.missing_parent_email.length})
                  </summary>
                  <div className="space-y-1 text-[11px]">
                    {data.missing_parent_email.slice(0, 50).map((r) => (
                      <div key={r.txn_id} className="grid grid-cols-12 gap-2 p-2 bg-slate-800/30 rounded">
                        <span className="col-span-3 text-white font-mono truncate">{r.payment_reference}</span>
                        <span className="col-span-2 text-slate-300 truncate">{r.parent_name || "—"}</span>
                        <span className="col-span-4 text-cyan-300 truncate" title={`source: ${r.proposed_backfill_source || "none"}`}>
                          → {r.proposed_backfill_email || "—"}
                        </span>
                        <span className="col-span-3 text-slate-500 text-[10px] truncate">{r.proposed_backfill_source || "no source"}</span>
                      </div>
                    ))}
                    {data.missing_parent_email.length > 50 && (
                      <p className="text-slate-500 text-center py-2">… {data.missing_parent_email.length - 50} more (download JSON for full list)</p>
                    )}
                  </div>
                </details>
              )}

              {s.missing_amount_invoiced === 0 && s.missing_parent_email === 0 && s.orphan_count === 0 && (
                <p className="text-emerald-400 text-center py-8">✨ All clear — no hygiene issues found.</p>
              )}
            </>
          )}
        </div>
      </Card>
    </div>
  );
}

function SiblingAuditModal({ loading, data, onClose, onRerun }) {
  const downloadJson = () => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sibling-audit-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      data-testid="sibling-audit-modal"
      onClick={onClose}
    >
      <Card
        className="bg-slate-900 border-fuchsia-500/30 w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <Stethoscope className="w-5 h-5 text-fuchsia-400" />
            <h2 className="text-lg font-semibold text-white">Sibling-Discount Audit</h2>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onRerun} disabled={loading} data-testid="audit-rerun-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-1" />} Re-run
            </Button>
            {data && !loading && (
              <Button variant="outline" size="sm" onClick={downloadJson} data-testid="audit-download-btn">
                <Download className="w-4 h-4 mr-1" /> JSON
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={onClose}><X className="w-4 h-4" /></Button>
          </div>
        </div>

        <div className="overflow-auto p-4 text-sm">
          {loading && (
            <div className="flex items-center gap-2 text-slate-400">
              <Loader2 className="w-4 h-4 animate-spin" /> Auditing transactions…
            </div>
          )}

          {!loading && data?.error && (
            <div className="bg-red-500/10 border border-red-500/30 p-3 rounded text-red-300">
              {data.error}
            </div>
          )}

          {!loading && data?.summary && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <Card className="p-3 bg-slate-800/40 border-slate-700">
                  <p className="text-[11px] uppercase text-slate-500">Scanned</p>
                  <p className="text-xl font-mono text-white">{data.summary.scanned}</p>
                  <p className="text-[10px] text-slate-500">last {data.summary.window_days}d</p>
                </Card>
                <Card className="p-3 bg-amber-500/10 border-amber-500/40">
                  <p className="text-[11px] uppercase text-amber-300">Has sibling discount</p>
                  <p className="text-xl font-mono text-white">{data.summary.with_sibling_discount}</p>
                </Card>
                <Card className="p-3 bg-red-500/10 border-red-500/40">
                  <p className="text-[11px] uppercase text-red-300">Suspicious (1 child only)</p>
                  <p className="text-xl font-mono text-white">{data.summary.suspicious_one_child}</p>
                </Card>
                <Card className="p-3 bg-fuchsia-500/10 border-fuchsia-500/40">
                  <p className="text-[11px] uppercase text-fuchsia-300">Total $ stored as sibling</p>
                  <p className="text-xl font-mono text-white">${data.summary.total_sibling_dollars_stored?.toFixed?.(2) ?? data.summary.total_sibling_dollars_stored}</p>
                </Card>
              </div>

              <p className="text-xs text-slate-400 mb-2">
                Showing {data.rows?.length ?? 0} row{(data.rows?.length ?? 0) === 1 ? "" : "s"}.
                Suspicious rows are highlighted red. Read-only — no changes have been written.
              </p>

              <div className="space-y-2" data-testid="audit-rows">
                {(data.rows || []).map((r) => (
                  <Card
                    key={r.txn_id}
                    className={`p-3 ${r.is_suspicious ? "bg-red-500/5 border-red-500/40" : "bg-slate-800/30 border-slate-700"}`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div>
                        <p className="text-white font-medium">{r.parent_name || r.parent_email}</p>
                        <p className="text-[11px] text-slate-500 font-mono">{r.payment_reference} · {r.parent_email}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-emerald-300 font-mono">${r.stored_amount?.toFixed(2)}</p>
                        <p className="text-[11px] text-amber-300 font-mono">−${r.stored_sibling_discount?.toFixed(2)} sibling</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-[11px]">
                      <div>
                        <p className="text-slate-500 uppercase">In cart</p>
                        <p className="text-white">{r.registration_children_in_cart} child{r.registration_children_in_cart === 1 ? "" : "ren"}</p>
                        <p className="text-slate-400">{(r.registration_children_names || []).join(", ") || "—"}</p>
                      </div>
                      <div>
                        <p className="text-slate-500 uppercase">Active players</p>
                        <p className="text-white">{r.active_academy_players_under_parent?.length || 0}</p>
                        <p className="text-slate-400 truncate">{(r.active_academy_players_under_parent || []).join(", ") || "—"}</p>
                      </div>
                      <div>
                        <p className="text-slate-500 uppercase">Legacy players</p>
                        <p className="text-white">{r.legacy_academy_players_under_parent?.length || 0}</p>
                        <p className="text-slate-400 truncate">
                          {(r.legacy_academy_players_under_parent || []).map((p) => p.name).join(", ") || "—"}
                        </p>
                      </div>
                    </div>
                    {r.is_suspicious && (
                      <p className="text-[11px] text-red-300 mt-2">⚠ {r.diagnosis}</p>
                    )}
                  </Card>
                ))}
                {(!data.rows || data.rows.length === 0) && (
                  <p className="text-slate-500 text-center py-8">No rows in window.</p>
                )}
              </div>
            </>
          )}
        </div>
      </Card>
    </div>
  );
}

function QueueRow({ row, onOpen }) {
  const diffAmber = row.diff !== null && row.diff !== undefined && row.diff !== 0;

  return (
    <tr
      className={`border-b border-slate-800/50 hover:bg-slate-800/30 cursor-pointer ${row.sla === "breach" ? "bg-red-500/5" : row.sla === "warn" ? "bg-amber-500/5" : ""}`}
      onClick={onOpen}
      data-testid={`queue-row-${row.id}`}
    >
      <td className="p-3">
        <p className="text-white font-medium">{row.parent?.name || row.parent?.display_name || <span className="text-slate-500 italic">(no name)</span>}</p>
        <p className="text-xs text-slate-400">{row.parent?.email || "—"}</p>
        {/* Event-registration subtitle — surfaces the camp / tournament
            name directly on the queue row so admin can see what the
            payment is for without opening the drawer. */}
        {row.type === "event_registration" && row.event_name && (
          <p className="text-xs mt-1" data-testid={`queue-row-${row.id}-event-name`}>
            <span className={`inline-block mr-1 px-1.5 py-0 rounded text-[10px] uppercase tracking-wider ${
              row.event_category === "tournament"
                ? "bg-purple-500/20 text-purple-300 border border-purple-500/40"
                : "bg-orange-500/20 text-orange-300 border border-orange-500/40"
            }`}>{row.event_category || "event"}</span>
            <span className="text-slate-300">{row.event_name}</span>
          </p>
        )}
        {row.parent?.is_scc_member && <Badge className="mt-1 text-[10px] bg-emerald-500/20 text-emerald-300 border-emerald-500/40">SCC member</Badge>}
        {row.fee_mismatch_detected && <Badge className="mt-1 ml-1 text-[10px] bg-red-500/20 text-red-300 border-red-500/40">Fee mismatch ${row.fee_mismatch_delta}</Badge>}
        {row.needs_reconcile && (
          <Badge
            className="mt-1 ml-1 text-[10px] bg-amber-500/20 text-amber-300 border-amber-500/40"
            title={`The line items on this invoice sum $${Math.abs(row.reconcile_gap).toFixed(2)} ${row.reconcile_gap > 0 ? "more" : "less"} than the $${row.amount_invoiced.toFixed(2)} charged — often a credit that wasn't itemised. Open the invoice to repair it.`}
            data-testid={`queue-row-${row.id}-reconcile-badge`}
          >
            ⚠ Doesn&apos;t reconcile ${Math.abs(row.reconcile_gap).toFixed(2)}
          </Badge>
        )}
        {row.invoice_send_error && (
          <Badge
            className="mt-1 ml-1 text-[10px] bg-red-600/30 text-red-200 border-red-500/60"
            title={`Invoice email failed: ${row.invoice_send_error}`}
            data-testid={`queue-row-${row.id}-invoice-error-badge`}
          >
            ✉ Invoice email failed
          </Badge>
        )}
      </td>
      <td className="p-3 text-center font-mono text-white">{row.children_count}</td>
      <td className="p-3 text-right font-mono text-white">${row.amount_invoiced.toFixed(2)}</td>
      <td className={`p-3 text-right font-mono ${diffAmber ? (row.diff > 0 ? "text-teal-300" : "text-amber-300") : "text-slate-400"}`}>
        {row.amount_received !== null && row.amount_received !== undefined ? `$${row.amount_received.toFixed(2)}` : "—"}
        {diffAmber && <div className="text-[10px]">{row.diff > 0 ? "+" : ""}${row.diff.toFixed(2)}</div>}
      </td>
      <td className="p-3 text-center">{statusBadge(row.status)}</td>
      <td className="p-3">
        {(row.status === "claimed" || row.status === "disputed") ? (
          <div>
            <p className="text-slate-300 text-xs">{ageToHumanReadable(row.age_seconds)}</p>
            {slaBadge(row.sla)}
            {row.status === "disputed" && row.dispute_reason && (
              <p className="text-[10px] text-red-300/70 mt-1 line-clamp-1 max-w-[220px]" title={row.dispute_reason}>
                {row.dispute_reason}
              </p>
            )}
          </div>
        ) : (
          <p className="text-slate-500 text-xs">—</p>
        )}
      </td>
      <td className="p-3 text-right text-slate-500 text-xs italic">
        Open →
      </td>
    </tr>
  );
}



function TestAccountsModal({ loading, data, selected, setSelected, onClose, onRerun, onCleanup }) {
  const isCleanupResult = data?._is_cleanup_result;
  const candidates = data?.candidates || [];
  const summary = data?.summary;

  const toggle = (uid) => {
    const next = new Set(selected);
    if (next.has(uid)) next.delete(uid); else next.add(uid);
    setSelected(next);
  };
  const selectAll = (which) => {
    if (which === "deletable") {
      setSelected(new Set(candidates.filter((c) => c.deletable && !c.force_required).map((c) => c.user_id)));
    } else if (which === "all") {
      setSelected(new Set(candidates.map((c) => c.user_id)));
    } else {
      setSelected(new Set());
    }
  };

  const downloadJson = () => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `test-accounts-${new Date().toISOString().slice(0, 10)}.json`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      data-testid="test-accounts-modal"
      onClick={onClose}
    >
      <Card
        className="bg-slate-900 border-rose-500/30 w-full max-w-6xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <Stethoscope className="w-5 h-5 text-rose-400" />
            <h2 className="text-lg font-semibold text-white">Test/Dummy Account Cleanup</h2>
          </div>
          <div className="flex gap-2 items-center">
            <Button variant="outline" size="sm" onClick={onRerun} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-1" />} Re-audit
            </Button>
            {data && !loading && (
              <Button variant="outline" size="sm" onClick={downloadJson}>
                <Download className="w-4 h-4 mr-1" /> JSON
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={onClose}><X className="w-4 h-4" /></Button>
          </div>
        </div>

        <div className="overflow-auto p-4 text-sm">
          {loading && (
            <div className="flex items-center gap-2 text-slate-400">
              <Loader2 className="w-4 h-4 animate-spin" /> Auditing user accounts…
            </div>
          )}

          {!loading && data?.error && (
            <div className="bg-red-500/10 border border-red-500/30 p-3 rounded text-red-300">{data.error}</div>
          )}

          {!loading && isCleanupResult && (
            <div className="space-y-3" data-testid="cleanup-result">
              <div className={`p-3 rounded ${data.dry_run ? "bg-amber-500/10 border border-amber-500/40" : "bg-emerald-500/10 border border-emerald-500/40"}`}>
                <p className={`font-medium ${data.dry_run ? "text-amber-200" : "text-emerald-200"}`}>
                  {data.dry_run ? "⚠ DRY-RUN — no data was written" : "✓ Cleanup committed"}
                </p>
                <p className="text-xs mt-1">
                  {data.executed} executed · {data.skipped} skipped · force={String(data.force)} · reason: {data.reason}
                </p>
              </div>
              <div className="space-y-2 max-h-[60vh] overflow-auto">
                {(data.results || []).map((r) => (
                  <div key={r.user_id} className={`p-2 rounded text-xs ${r.skipped ? "bg-amber-500/10 border border-amber-500/30" : "bg-slate-800/40"}`}>
                    <div className="flex justify-between mb-1">
                      <span className="text-white font-medium">{r.name || "(unnamed)"} <span className="text-slate-500">· {r.email}</span></span>
                      {r.skipped ? <span className="text-amber-300">SKIPPED</span> : <span className="text-emerald-300">OK</span>}
                    </div>
                    {r.skipped && <p className="text-amber-300/80 italic">{r.reason}</p>}
                    {r.deletions && (
                      <div className="text-[10px] text-slate-400 grid grid-cols-7 gap-1 mt-1">
                        {Object.entries(r.deletions).map(([coll, n]) => (
                          <span key={coll} className={n > 0 ? "text-emerald-300" : ""}>{coll}: {n}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {!loading && summary && !isCleanupResult && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <Card className="p-3 bg-slate-800/40 border-slate-700">
                  <p className="text-[11px] uppercase text-slate-500">Total candidates</p>
                  <p className="text-2xl font-mono text-white">{summary.total_candidates}</p>
                </Card>
                <Card className="p-3 bg-emerald-500/10 border-emerald-500/40">
                  <p className="text-[11px] uppercase text-emerald-300">Already tagged</p>
                  <p className="text-2xl font-mono text-white">{summary.already_tagged}</p>
                </Card>
                <Card className="p-3 bg-cyan-500/10 border-cyan-500/40">
                  <p className="text-[11px] uppercase text-cyan-300">Safe to delete</p>
                  <p className="text-2xl font-mono text-white">{summary.deletable_no_force}</p>
                </Card>
                <Card className="p-3 bg-red-500/10 border-red-500/40">
                  <p className="text-[11px] uppercase text-red-300">Need force flag</p>
                  <p className="text-2xl font-mono text-white">{summary.force_required}</p>
                </Card>
              </div>

              <div className="flex flex-wrap gap-2 items-center mb-3">
                <p className="text-slate-400 text-xs mr-2">Selection: <strong className="text-white">{selected.size}</strong></p>
                <Button size="sm" variant="outline" onClick={() => selectAll("deletable")}>Select safe-to-delete</Button>
                <Button size="sm" variant="outline" onClick={() => selectAll("all")}>Select all</Button>
                <Button size="sm" variant="ghost" onClick={() => selectAll("none")}>Clear</Button>
                <div className="flex-grow" />
                <Button size="sm" variant="outline" onClick={() => onCleanup(true, false)} disabled={selected.size === 0} className="border-amber-500/40 text-amber-300">
                  Preview cleanup
                </Button>
                <Button size="sm" onClick={() => onCleanup(false, false)} disabled={selected.size === 0} className="bg-rose-600 hover:bg-rose-500" data-testid="cleanup-execute-btn">
                  Delete selected ({selected.size})
                </Button>
                <Button size="sm" variant="outline" onClick={() => onCleanup(false, true)} disabled={selected.size === 0} className="border-red-500/40 text-red-300" title="Bypass safety guards (paid >$10, active enrollment, verified SCC)">
                  Force delete
                </Button>
              </div>

              <div className="space-y-1 max-h-[55vh] overflow-auto" data-testid="test-account-rows">
                {candidates.map((c) => {
                  const cs = c.cascade_summary;
                  const isSelected = selected.has(c.user_id);
                  return (
                    <div
                      key={c.user_id}
                      className={`p-2 rounded cursor-pointer border transition-colors ${
                        isSelected ? "bg-rose-500/10 border-rose-500/40" :
                        c.force_required ? "bg-red-500/5 border-red-500/30" :
                        c.is_test_account_tag ? "bg-emerald-500/5 border-emerald-500/30" :
                        "bg-slate-800/30 border-slate-700"
                      }`}
                      onClick={() => toggle(c.user_id)}
                    >
                      <div className="flex items-start gap-2 text-xs">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggle(c.user_id)}
                          onClick={(e) => e.stopPropagation()}
                          className="mt-0.5"
                        />
                        <div className="flex-grow">
                          <div className="flex justify-between items-baseline">
                            <div>
                              <span className="text-white font-medium">{c.name}</span>
                              <span className="text-slate-500 ml-2 font-mono text-[10px]">{c.email}</span>
                              {c.is_test_account_tag && <span className="ml-2 px-1.5 py-0.5 text-[9px] bg-emerald-600 text-white rounded">TAGGED</span>}
                              {c.force_required && <span className="ml-2 px-1.5 py-0.5 text-[9px] bg-red-600 text-white rounded">FORCE</span>}
                              {c.is_scc_member && <span className="ml-2 px-1.5 py-0.5 text-[9px] bg-blue-600 text-white rounded">SCC</span>}
                            </div>
                            <span className="text-slate-500 text-[10px]">{c.created_at?.slice(0, 10)}</span>
                          </div>
                          <p className="text-slate-400 mt-0.5 text-[10px]">
                            cascade: {cs.payment_transactions} txn · {cs.academy_players} kid · {cs.academy_enrollments} enrl · {cs.academy_parent_registrations} reg · {cs.academy_credits} cred · {cs.payment_documents} doc · {cs.user_sessions} sess
                          </p>
                          {c.blocking_guards?.length > 0 && (
                            <ul className="text-red-300 mt-1 list-disc list-inside text-[10px]">
                              {c.blocking_guards.map((g, i) => <li key={i}>{g}</li>)}
                            </ul>
                          )}
                          <p className="text-slate-500 mt-0.5 text-[10px] italic">{c.match_reasons.join(" · ")}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {candidates.length === 0 && (
                  <p className="text-emerald-400 text-center py-8">✨ No test accounts found.</p>
                )}
              </div>
            </>
          )}
        </div>
      </Card>
    </div>
  );
}


function TriageModal({ loading, data, onClose }) {
  const downloadJson = () => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `triage-${new Date().toISOString().slice(0, 10)}.json`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      data-testid="triage-modal"
      onClick={onClose}
    >
      <Card
        className="bg-slate-900 border-violet-500/30 w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <Stethoscope className="w-5 h-5 text-violet-400" />
            <h2 className="text-lg font-semibold text-white">Triage — Sarah Gah · Unbackfillable Emails · SCC Flag Audit</h2>
          </div>
          <div className="flex gap-2">
            {data && !loading && (
              <Button variant="outline" size="sm" onClick={downloadJson} data-testid="triage-download-btn">
                <Download className="w-4 h-4 mr-1" /> JSON
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={onClose}><X className="w-4 h-4" /></Button>
          </div>
        </div>

        <div className="overflow-auto p-4 text-sm space-y-6">
          {loading && (
            <div className="flex items-center gap-2 text-slate-400">
              <Loader2 className="w-4 h-4 animate-spin" /> Running triage diagnostics…
            </div>
          )}

          {!loading && data?.error && (
            <div className="bg-red-500/10 border border-red-500/30 p-3 rounded text-red-300">{data.error}</div>
          )}

          {/* ── 1. Sarah Gah ── */}
          {!loading && data?.sarah && (
            <Card className="p-4 bg-slate-800/40 border-slate-700">
              <h3 className="text-violet-300 font-semibold mb-2">1. ADHOC-20260104-6183CE — Sarah Gah</h3>
              {!data.sarah.found && <p className="text-slate-400">Transaction not found in database.</p>}
              {data.sarah.found && (
                <div className="space-y-2 text-xs">
                  <div className="grid grid-cols-2 gap-2">
                    <div><span className="text-slate-500">Amount:</span> <span className="text-emerald-300">${data.sarah.transaction?.amount}</span></div>
                    <div><span className="text-slate-500">Status:</span> <span className="text-white">{data.sarah.transaction?.payment_status}</span></div>
                    <div><span className="text-slate-500">Parent name:</span> <span className="text-white">{data.sarah.parent_name || "—"}</span></div>
                    <div><span className="text-slate-500">Parent email:</span> <span className="text-white">{data.sarah.parent_email || "—"}</span></div>
                    <div><span className="text-slate-500">Parent ID on txn:</span> <span className="text-white font-mono">{data.sarah.parent_id_in_txn || "—"}</span></div>
                    <div><span className="text-slate-500">Created:</span> <span className="text-white">{data.sarah.transaction?.created_at?.slice(0, 19)}</span></div>
                  </div>
                  <details>
                    <summary className="cursor-pointer text-violet-300">Matching users ({data.sarah.matching_users?.length || 0})</summary>
                    <pre className="text-[10px] mt-2 bg-slate-900 p-2 rounded overflow-auto">{JSON.stringify(data.sarah.matching_users, null, 2)}</pre>
                  </details>
                  <details>
                    <summary className="cursor-pointer text-violet-300">Sibling transactions same parent ({data.sarah.sibling_transactions_same_parent?.length || 0})</summary>
                    <pre className="text-[10px] mt-2 bg-slate-900 p-2 rounded overflow-auto">{JSON.stringify(data.sarah.sibling_transactions_same_parent, null, 2)}</pre>
                  </details>
                  <details>
                    <summary className="cursor-pointer text-violet-300">Linked adhoc bookings ({data.sarah.linked_adhoc_bookings?.length || 0})</summary>
                    <pre className="text-[10px] mt-2 bg-slate-900 p-2 rounded overflow-auto">{JSON.stringify(data.sarah.linked_adhoc_bookings, null, 2)}</pre>
                  </details>
                  <details>
                    <summary className="cursor-pointer text-violet-300">Attendance records ({data.sarah.attendance_records?.length || 0})</summary>
                    <pre className="text-[10px] mt-2 bg-slate-900 p-2 rounded overflow-auto">{JSON.stringify(data.sarah.attendance_records, null, 2)}</pre>
                  </details>
                  <div className="bg-slate-900/50 p-2 rounded text-[11px]">
                    <p className="text-violet-200 uppercase">Diagnosis hints</p>
                    <ul className="list-disc list-inside text-slate-400 mt-1 space-y-0.5">
                      {(data.sarah.diagnosis_hints || []).map((h, i) => <li key={i}>{h}</li>)}
                    </ul>
                  </div>
                  {/* Quick-action: attach the guest-checkout retention note */}
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-2 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                    data-testid="attach-guest-checkout-note-btn"
                    onClick={async () => {
                      if (!window.confirm(
                        "Attach internal admin note to ADHOC-20260104-6183CE:\n\n" +
                        "  kind: guest_checkout_retained\n" +
                        "  note: 'Guest checkout — no linked user account, retained for financial records.'\n\n" +
                        "Idempotent (re-clicking does nothing). Confirm?"
                      )) return;
                      try {
                        const res = await axios.post(
                          `${API}/payments-hub/admin/txn/ADHOC-20260104-6183CE/attach-note`,
                          {
                            kind: "guest_checkout_retained",
                            note: "Guest checkout — no linked user account, retained for financial records.",
                            metadata: { policy: "do_not_delete_paid_orphans", session: "13p" },
                          },
                          { withCredentials: true },
                        );
                        if (res.data.already_attached) {
                          toast.success("Note already attached (no change).");
                        } else {
                          toast.success("Internal note attached to ADHOC-20260104-6183CE.");
                        }
                      } catch (err) {
                        { const __e = formatAdminError(err, "Failed to attach note"); toast.error(__e.message, __e.opts); };
                      }
                    }}
                  >
                    Attach "guest checkout retained" note
                  </Button>

                  <SarahCascadeCleanup />
                </div>
              )}
            </Card>
          )}

          {/* ── 2. Unbackfillable emails ── */}
          {!loading && data?.emails && (
            <Card className="p-4 bg-slate-800/40 border-slate-700">
              <h3 className="text-violet-300 font-semibold mb-2">2. Unbackfillable parent_email rows ({data.emails.count})</h3>
              <p className="text-xs text-slate-400 mb-2">No matching user/registration source — needs manual lookup in Stripe Dashboard.</p>
              {data.emails.count === 0 && <p className="text-emerald-400 py-4 text-center">✨ All clear — no unbackfillable rows.</p>}
              {data.emails.count > 0 && <AbandonedStripeCleanup />}
              <div className="space-y-2">
                {(data.emails.rows || []).map((r) => (
                  <div key={r.txn_id} className="bg-slate-900 p-2 rounded text-[11px]">
                    <div className="grid grid-cols-12 gap-2">
                      <span className="col-span-3 font-mono text-white">{r.payment_reference}</span>
                      <span className="col-span-3 text-slate-200">{r.parent_name}</span>
                      <span className="col-span-2 font-mono text-emerald-300">${r.amount?.toFixed?.(2) ?? "?"}</span>
                      <span className="col-span-2">{r.payment_status}</span>
                      <span className="col-span-2 text-slate-500">{r.created_at?.slice(0, 10)}</span>
                    </div>
                    <p className="text-slate-500 mt-1">{r.manual_lookup_hint}</p>
                    {r.stripe_session_id && (
                      <p className="text-cyan-300/80 font-mono mt-0.5 text-[10px]">stripe session: {r.stripe_session_id}</p>
                    )}
                    {r.metadata_email_if_any && (
                      <p className="text-emerald-400/80 mt-0.5">📧 Found in metadata: {r.metadata_email_if_any}</p>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* ── 3. SCC member flag audit ── */}
          {!loading && data?.scc && (
            <Card className="p-4 bg-slate-800/40 border-slate-700">
              <h3 className="text-violet-300 font-semibold mb-2">3. SCC member flag desync audit</h3>
              <div className="grid grid-cols-4 gap-2 mb-3 text-xs">
                <div className="p-2 bg-slate-900 rounded"><p className="text-slate-500">Total desync</p><p className="text-white text-lg font-mono">{data.scc.summary?.total_desync || 0}</p></div>
                <div className="p-2 bg-red-500/10 border border-red-500/30 rounded"><p className="text-red-300">High</p><p className="text-white text-lg font-mono">{data.scc.summary?.high || 0}</p></div>
                <div className="p-2 bg-amber-500/10 border border-amber-500/30 rounded"><p className="text-amber-300">Medium</p><p className="text-white text-lg font-mono">{data.scc.summary?.medium || 0}</p></div>
                <div className="p-2 bg-slate-700/30 rounded"><p className="text-slate-400">Low</p><p className="text-white text-lg font-mono">{data.scc.summary?.low || 0}</p></div>
              </div>
              {data.scc.summary?.total_desync === 0 && <p className="text-emerald-400 py-4 text-center">✨ All clear — flags consistent across all users.</p>}
              {data.scc.summary?.total_desync > 0 && <SccFlagSync />}
              <div className="space-y-1 max-h-96 overflow-auto">
                {(data.scc.rows || []).slice(0, 100).map((r) => (
                  <div
                    key={r.user_id}
                    className={`p-2 rounded text-[11px] ${r.severity === "high" ? "bg-red-500/10 border border-red-500/30" : r.severity === "medium" ? "bg-amber-500/10 border border-amber-500/30" : "bg-slate-900"}`}
                  >
                    <div className="flex justify-between mb-0.5">
                      <span className="font-medium text-white">{r.name}</span>
                      <span className="text-slate-400">{r.email}</span>
                    </div>
                    <div className="text-slate-400 grid grid-cols-4 gap-1">
                      <span>is_scc: <strong className={r.is_scc_member ? "text-emerald-300" : "text-rose-300"}>{String(r.is_scc_member)}</strong></span>
                      <span>type: <strong className="text-white">{r.member_type || "—"}</strong></span>
                      <span>verified: <strong>{String(r.scc_member_verified)}</strong></span>
                      <span>mn: <strong>{r.membership_number || "—"}</strong></span>
                    </div>
                    <ul className="mt-1 text-slate-300 list-disc list-inside">
                      {(r.flags || []).map((f, i) => <li key={i}>{f}</li>)}
                    </ul>
                  </div>
                ))}
                {(data.scc.rows || []).length > 100 && (
                  <p className="text-slate-500 text-center py-2">… {data.scc.rows.length - 100} more (download JSON for full list)</p>
                )}
              </div>
            </Card>
          )}
        </div>
      </Card>
    </div>
  );
}


function DoctorModal({ loading, data, query, onQueryChange, onSearch, onClose }) {
  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      data-testid="doctor-modal"
      onClick={onClose}
    >
      <Card
        className="bg-slate-900 border-rose-500/30 w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <Stethoscope className="w-5 h-5 text-rose-400" />
            <h2 className="text-lg font-semibold text-white">Registration Doctor</h2>
          </div>
          <div className="flex gap-2 items-center">
            <input
              type="text"
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") onSearch(query); }}
              placeholder="name / email / membership #"
              className="h-9 w-64 px-2 text-sm bg-slate-800 border border-rose-500/30 text-white placeholder:text-slate-500 rounded focus:outline-none focus:border-rose-400"
              data-testid="doctor-modal-query-input"
            />
            <Button variant="outline" size="sm" onClick={() => onSearch(query)} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Search"}
            </Button>
            <Button variant="ghost" size="sm" onClick={onClose}><X className="w-4 h-4" /></Button>
          </div>
        </div>

        <div className="overflow-auto p-4 text-sm">
          {loading && (
            <div className="flex items-center gap-2 text-slate-400">
              <Loader2 className="w-4 h-4 animate-spin" /> Diagnosing…
            </div>
          )}

          {!loading && data?.error && (
            <div className="bg-red-500/10 border border-red-500/30 p-3 rounded text-red-300">{data.error}</div>
          )}

          {!loading && data && !data.error && (
            <div className="space-y-4">
              <div className="text-xs text-slate-400">
                Search: <strong className="text-rose-300">{data.query}</strong> · matched {data.user_match_count} user{data.user_match_count === 1 ? "" : "s"}
                {data.active_term && (
                  <span className="ml-3">· Active term: <strong className="text-emerald-300">{data.active_term.name}</strong></span>
                )}
              </div>

              {(!data.results || data.results.length === 0) && (
                <p className="text-amber-400 text-center py-8">No matching user found.</p>
              )}

              {(data.results || []).map((r) => (
                <Card key={r.user.id} className="p-4 bg-slate-800/40 border-slate-700">
                  <div className="flex justify-between mb-2">
                    <div>
                      <p className="text-white font-semibold text-base">{r.user.name || `${r.user.given_name || ""} ${r.user.last_name || ""}`}</p>
                      <p className="text-[11px] text-slate-400 font-mono">{r.user.email} · id: {r.user.id?.slice(0, 12)}…</p>
                    </div>
                    <div className="flex flex-wrap gap-1 justify-end max-w-md">
                      {r.user.is_blocked && <span className="text-[10px] bg-red-600 text-white px-2 py-0.5 rounded">BLOCKED</span>}
                      {r.user.is_active === false && <span className="text-[10px] bg-red-600 text-white px-2 py-0.5 rounded">INACTIVE</span>}
                      {r.user.is_scc_member && r.user.scc_member_verified && <span className="text-[10px] bg-emerald-600 text-white px-2 py-0.5 rounded">SCC ✓</span>}
                      {r.user.is_scc_member && !r.user.scc_member_verified && <span className="text-[10px] bg-amber-600 text-white px-2 py-0.5 rounded">SCC unverified</span>}
                      {r.user.is_digital_member && <span className="text-[10px] bg-teal-600 text-white px-2 py-0.5 rounded" title="Active Digital Membership">DM</span>}
                      {r.user.member_type === "section" && <span className="text-[10px] bg-blue-600 text-white px-2 py-0.5 rounded">Section</span>}
                      {!r.user.has_password_hash && <span className="text-[10px] bg-slate-600 text-white px-2 py-0.5 rounded">No password</span>}
                    </div>
                  </div>

                  {r.warnings?.length > 0 && (
                    <div className="mb-3 space-y-1" data-testid="doctor-warnings">
                      {r.warnings.map((w, i) => (
                        <div key={i} className={`text-xs p-2 rounded ${w.severity === "high" ? "bg-red-500/10 border border-red-500/30 text-red-300" : w.severity === "medium" ? "bg-amber-500/10 border border-amber-500/30 text-amber-300" : "bg-slate-700/30 text-slate-300"}`}>
                          {w.severity === "high" ? "🔴" : w.severity === "medium" ? "🟡" : "ℹ️"} {w.message}
                        </div>
                      ))}
                    </div>
                  )}

                  {r.membership_check && (
                    <div className="text-xs mb-3">
                      <span className="text-slate-400">Membership check:</span>{" "}
                      {r.membership_check.valid === true && <span className="text-emerald-300">✓ valid · {r.membership_check.parsed?.category} · running #{r.membership_check.parsed?.running_number}</span>}
                      {r.membership_check.valid === false && <span className="text-red-300">✗ {r.membership_check.error}</span>}
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[11px]">
                    <div>
                      <p className="text-slate-400 uppercase mb-1">Recent registrations ({r.recent_registrations?.length || 0})</p>
                      <div className="space-y-1 max-h-40 overflow-auto">
                        {(r.recent_registrations || []).slice(0, 5).map((reg) => (
                          <div key={reg.id} className="bg-slate-900 p-2 rounded">
                            <div className="flex justify-between"><span className="text-white">{(reg.children_names || []).join(", ") || "(no children)"}</span><span className="text-slate-500">{reg.created_at?.slice(0, 10)}</span></div>
                            <div className="text-slate-400">status: <span className="text-amber-300">{reg.status}</span> · pay: <span className={reg.payment_status === "paid" ? "text-emerald-300" : "text-rose-300"}>{reg.payment_status || "—"}</span> · ${reg.total_amount || reg.total_fee || "?"}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-slate-400 uppercase mb-1">Recent transactions ({r.recent_transactions?.length || 0})</p>
                      <div className="space-y-1 max-h-40 overflow-auto">
                        {(r.recent_transactions || []).slice(0, 5).map((t) => (
                          <div key={t.id} className="bg-slate-900 p-2 rounded">
                            <div className="flex justify-between"><span className="font-mono text-white">{t.payment_reference}</span><span className="text-slate-500">{t.created_at?.slice(0, 10)}</span></div>
                            <div className="text-slate-400">type: {t.payment_type} · status: <span className={t.payment_status === "paid" ? "text-emerald-300" : t.payment_status === "initiated" ? "text-amber-300" : "text-rose-300"}>{t.payment_status}</span> · ${t.amount}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {r.stuck_registrations?.length > 0 && (
                    <div className="mt-3 bg-red-500/10 border border-red-500/30 p-2 rounded">
                      <p className="text-red-300 text-[11px] uppercase">⚠ {r.stuck_registrations.length} stuck registration{r.stuck_registrations.length > 1 ? "s" : ""}</p>
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-2 text-[11px] h-7 border-red-500/40 text-red-300 hover:bg-red-500/20"
                        data-testid="dedupe-pending-btn"
                        onClick={async (e) => {
                          e.stopPropagation();
                          const dryRun = !window.confirm(
                            `Found ${r.stuck_registrations.length} pending duplicate registration(s) for ${r.user.name || r.user.email}.\n\n` +
                            `Click OK to DELETE all but the most recent of each (parent+term+children) group.\n` +
                            `Click Cancel to see a dry-run plan first (recommended).`
                          );
                          try {
                            const url = `${API}/payments-hub/admin/dedupe-pending-registrations?parent_id=${encodeURIComponent(r.user.id)}&dry_run=${dryRun ? "true" : "false"}`;
                            const res = await axios.post(url, {}, { withCredentials: true });
                            const msg = dryRun
                              ? `DRY-RUN: would delete ${res.data.would_delete_count}, keep ${res.data.kept?.length || 0} (no changes made)`
                              : `Deleted ${res.data.deleted_count} duplicate row(s). Kept ${res.data.kept?.length || 0}.`;
                            toast.success(msg);
                            console.log("dedupe result:", res.data);
                          } catch (err) {
                            { const __e = formatAdminError(err, "Dedupe failed"); toast.error(__e.message, __e.opts); };
                          }
                        }}
                      >
                        Clean up duplicates
                      </Button>
                    </div>
                  )}
                </Card>
              ))}

              {data.guidance?.length > 0 && (
                <details className="bg-slate-800/30 p-3 rounded text-xs">
                  <summary className="cursor-pointer text-slate-300 font-medium">Triage guidance</summary>
                  <ul className="space-y-1 mt-2 text-slate-400 list-disc list-inside">
                    {data.guidance.map((g, i) => <li key={i}>{g}</li>)}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}



function DetailDrawer({ txnId, onClose, onActionComplete }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [dupArchiving, setDupArchiving] = useState(null);  // sibling id being archived
  const [reconciling, setReconciling] = useState(false);   // drift-repair in progress
  const [repairPreview, setRepairPreview] = useState(null); // per-invoice reconcile preview
  const [bulkSweep, setBulkSweep] = useState(null);         // optional one-time sweep result

  const refreshDetail = async () => {
    const r = await axios.get(`${API}/payments-hub/detail/${txnId}`, { withCredentials: true });
    setDetail(r.data);
  };

  // Per-invoice repair — preview first, then apply. Explains the existing gap
  // only: never re-runs the fee calc, never changes the charged amount.
  const previewRepair = async () => {
    setReconciling(true);
    try {
      const res = await axios.post(
        `${API}/payments-hub/txn/${txnId}/reconcile`, {}, { withCredentials: true }
      );
      setRepairPreview(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not build a repair preview");
    } finally {
      setReconciling(false);
    }
  };

  const applyRepair = async () => {
    setReconciling(true);
    try {
      const res = await axios.post(
        `${API}/payments-hub/txn/${txnId}/reconcile`, { confirm: true }, { withCredentials: true }
      );
      toast.success(
        res.data?.method === "credit_applied"
          ? "Credit itemised — the breakdown now matches the amount charged"
          : "Reconciled"
      );
      setRepairPreview(null);
      await refreshDetail();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Repair failed");
    } finally {
      setReconciling(false);
    }
  };

  // Optional secondary: one-time sweep across every invoice with the same
  // un-itemised-credit shape. Dry-run → apply → revert.
  const runBulkSweep = async (apply = false) => {
    setReconciling(true);
    try {
      const res = await axios.post(
        `${API}/academy/admin/backfill-credit-lines?dry_run=${apply ? "false" : "true"}`,
        {}, { withCredentials: true }
      );
      setBulkSweep(res.data);
      if (apply) {
        toast.success(`Fixed ${res.data.applied} invoice(s)`);
        await refreshDetail();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sweep failed");
    } finally {
      setReconciling(false);
    }
  };

  const revertBulkSweep = async (runId) => {
    setReconciling(true);
    try {
      const res = await axios.post(
        `${API}/academy/admin/backfill-credit-lines/revert?run_id=${runId}`,
        {}, { withCredentials: true }
      );
      toast.success(`Reverted ${res.data.reverted} invoice(s)`);
      setBulkSweep(null);
      await refreshDetail();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Revert failed");
    } finally {
      setReconciling(false);
    }
  };

  const handleReconcile = async () => {
    const gap = Math.abs(Number(detail?.fee_breakdown?.synth_unexplained_gap || 0));
    const charged = Number(detail?.amount_invoiced ?? detail?.amount ?? detail?.fee_breakdown?.total ?? 0);
    const ok = window.confirm(
      `Reconcile this transaction's fee breakdown?\n\n` +
      `The charged amount ($${charged.toFixed(2)}) will NOT change. ` +
      `We'll first try to rebuild the real term-fee and discount lines; if that isn't possible we add a single ` +
      `"Legacy reconciliation" line of $${gap.toFixed(2)} so the breakdown adds up. This is reversible.`
    );
    if (!ok) return;
    setReconciling(true);
    try {
      const res = await axios.post(
        `${API}/payments-hub/txn/${txnId}/reconcile`,
        { confirm: true },
        { withCredentials: true }
      );
      const method = res.data?.method === "canonical_rebuild" ? "rebuilt real lines" : "added a reconciliation line";
      toast.success(`Reconciled — ${method}`);
      // Refresh the drawer in place (do NOT call onActionComplete — that would
      // close the drawer before the admin can see the reconciled banner/Undo).
      const r = await axios.get(`${API}/payments-hub/detail/${txnId}`, { withCredentials: true });
      setDetail(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reconcile failed");
    } finally {
      setReconciling(false);
    }
  };

  const handleUndoReconcile = async () => {    if (!window.confirm("Undo the reconciliation and restore the original stored breakdown?")) return;
    setReconciling(true);
    try {
      await axios.post(`${API}/payments-hub/txn/${txnId}/reconcile/undo`, {}, { withCredentials: true });
      toast.success("Reconciliation undone");
      const r = await axios.get(`${API}/payments-hub/detail/${txnId}`, { withCredentials: true });
      setDetail(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Undo failed");
    } finally {
      setReconciling(false);
    }
  };

  // Part 2 — confirm-cancel an unpaid duplicate sibling from the paid keeper.
  const archiveDuplicateSibling = async (sib) => {
    if (!window.confirm(`Cancel duplicate transaction ${sib.payment_reference || sib.id.slice(0, 8)} ($${Number(sib.amount).toFixed(2)}, ${sib.status})?\n\nThe kept (paid/approved) transaction stays. The duplicate is archived (recoverable), not deleted.`)) return;
    setDupArchiving(sib.id);
    try {
      const res = await axios.post(
        `${API}/payments-hub/admin/archive-duplicate-transactions`,
        { keeper_id: txnId, loser_ids: [sib.id], reason: "duplicate_registration" },
        { withCredentials: true }
      );
      const r0 = res.data?.results?.[0];
      if (r0?.action === "archived") {
        toast.success("Duplicate cancelled (archived)");
        const r = await axios.get(`${API}/payments-hub/detail/${txnId}`, { withCredentials: true });
        setDetail(r.data);
        onActionComplete && onActionComplete();
      } else {
        toast.error(`Not cancelled: ${r0?.reason || "refused"}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cancel failed");
    } finally {
      setDupArchiving(null);
    }
  };

  // Action form state
  const [receivedAmount, setReceivedAmount] = useState("");
  const [disputeReason, setDisputeReason] = useState("");
  const [adminNote, setAdminNote] = useState("");
  // Optional verification details — used by Approve / Approve Anyway /
  // Approve+Credit / Resolve. Always-available inputs in the drawer's
  // Payment Status card so admins can record the bank reference + payee they
  // verified against without having to use the Mark-Paid-Offline form.
  const [verifyBankRef, setVerifyBankRef] = useState("");
  const [verifyPayee, setVerifyPayee] = useState("");
  const [verifyNote, setVerifyNote] = useState("");
  // Mark Paid Offline form state (Unpaid / Disputed / Partial drawer)
  const [showOfflineForm, setShowOfflineForm] = useState(false);
  const [offlineMethod, setOfflineMethod] = useState("bank_transfer");
  const [offlineBankRef, setOfflineBankRef] = useState("");
  const [offlinePayee, setOfflinePayee] = useState("");
  const [offlineNote, setOfflineNote] = useState("");
  const [offlineAmount, setOfflineAmount] = useState("");
  // Cancel Registration form state (unpaid registrations only — Feb 2026)
  const [showCancelForm, setShowCancelForm] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  // Generic "Cancel transaction" (soft-delete/archive) — Aug 2026. Works for
  // any status; the backend gates paid/approved behind an extra confirmation.
  const [showTxnCancel, setShowTxnCancel] = useState(false);
  const [txnCancelReason, setTxnCancelReason] = useState("");

  // Bank suggestions — proactively fetched when drawer opens for a non-final txn
  const [bankSuggestions, setBankSuggestions] = useState([]);
  const [bankSuggestionsLoaded, setBankSuggestionsLoaded] = useState(false);

  // Manual line-item override state (Jun 2026) — opened from the fee
  // breakdown section when admin needs to manually adjust a row.
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideRows, setOverrideRows] = useState([{ label: "", amount: "" }]);
  const [overrideNote, setOverrideNote] = useState("");
  const [overrideSendEmail, setOverrideSendEmail] = useState(false);
  const [overrideSaving, setOverrideSaving] = useState(false);

  // Payment method selector (canonical billing) — drives the card fee.
  // credit_card → gross-up card fee applies & shows as a line; paynow/cash/others → no fee.
  const [paymentMethod, setPaymentMethod] = useState("paynow");
  const [pmRemarks, setPmRemarks] = useState("");
  const [pmBankName, setPmBankName] = useState("");
  const [pmSaving, setPmSaving] = useState(false);

  const openOverrideEditor = () => {
    // Seed with existing line items so the admin edits, not rewrites from scratch
    const existing = detail?.fee_breakdown?.line_items || [];
    if (existing.length > 0) {
      setOverrideRows(existing.map((li) => ({
        label: li.label || li.description || li.child_name || "Item",
        amount: String(li.amount ?? li.child_total ?? 0),
      })));
    } else if (typeof detail?.fee_breakdown?.total === "number") {
      setOverrideRows([{ label: "Total fee", amount: String(detail.fee_breakdown.total) }]);
    } else {
      setOverrideRows([{ label: "", amount: "" }]);
    }
    setOverrideNote("");
    setOverrideSendEmail(false);
    setOverrideOpen(true);
  };

  const handleOverrideRowChange = (idx, field, value) => {
    setOverrideRows((rows) => rows.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };
  const addOverrideRow = () => setOverrideRows((rows) => [...rows, { label: "", amount: "" }]);
  const removeOverrideRow = (idx) => setOverrideRows((rows) => rows.length > 1 ? rows.filter((_, i) => i !== idx) : rows);

  const overrideTotal = overrideRows.reduce((acc, r) => acc + (parseFloat(r.amount) || 0), 0);

  const handleOverrideSave = async () => {
    const cleaned = overrideRows
      .map((r) => ({ label: r.label.trim(), amount: parseFloat(r.amount) || 0 }))
      .filter((r) => r.label !== "");
    if (cleaned.length === 0) {
      toast.error("Add at least one line with a label");
      return;
    }
    if (!overrideNote.trim()) {
      toast.error("Please add a reason note");
      return;
    }
    const confirmMsg = overrideSendEmail
      ? `Save override and email the new invoice to ${detail?.parent_email || "the parent"}?\n\nNew total: $${overrideTotal.toFixed(2)}`
      : `Save override?\n\nNew total: $${overrideTotal.toFixed(2)}\n(no email will be sent)`;
    if (!window.confirm(confirmMsg)) return;

    setOverrideSaving(true);
    try {
      const resp = await axios.post(
        `${API}/payments-hub/txn/${txnId}/override-line-items`,
        { line_items: cleaned, note: overrideNote.trim(), send_invoice_email: overrideSendEmail },
        { withCredentials: true },
      );
      const data = resp.data;
      if (data.email_status === "sent") {
        toast.success(`Override saved + invoice emailed (new total $${data.new_amount.toFixed(2)})`);
      } else if (data.email_status === "failed") {
        toast.warning(`Override saved but email failed: ${data.email_error}`);
      } else {
        toast.success(`Override saved (new total $${data.new_amount.toFixed(2)})`);
      }
      setOverrideOpen(false);
      // Re-fetch drawer so the new lines render immediately
      const r = await axios.get(`${API}/payments-hub/detail/${txnId}`, { withCredentials: true });
      setDetail(r.data);
      if (onActionComplete) onActionComplete();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Override failed");
    } finally {
      setOverrideSaving(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/payments-hub/detail/${txnId}`, { withCredentials: true })
      .then((r) => {
        setDetail(r.data);
        setReceivedAmount(r.data.amount_received?.toString() || r.data.amount_invoiced?.toString() || "");
        // Pre-populate verification fields from existing txn data so admins
        // see what was already recorded without having to re-type.
        setVerifyBankRef(r.data.reference_bank || "");
        setVerifyPayee(r.data.bank_account_holder_name || "");
        setVerifyNote("");
        // Canonical payment method (drives card fee). Default from stored breakdown.
        setPaymentMethod((r.data.fee_breakdown?.payment_method) || r.data.payment_method || "paynow");
        setPmBankName(r.data.bank_account_name || r.data.bank_account_holder_name || "");
        setPmRemarks(r.data.payment_method_remarks || "");
        // Fire bank-suggest in parallel — don't block the drawer load on it
        if (["claimed", "disputed", "partial", "unpaid"].includes(r.data.status)) {
          axios.get(`${API}/payments-hub/bank-suggest/${txnId}`, { withCredentials: true })
            .then((sr) => {
              setBankSuggestions(sr.data.suggestions || []);
              setBankSuggestionsLoaded(true);
            })
            .catch(() => setBankSuggestionsLoaded(true));
        } else {
          setBankSuggestionsLoaded(true);
        }
      })
      .catch((err) => { const __e = formatAdminError(err, "Failed to load detail"); toast.error(__e.message, __e.opts); })
      .finally(() => setLoading(false));
  }, [txnId]);

  // Set the payment method → recompute the card fee via the canonical engine.
  const applyPaymentMethod = async (method) => {
    setPaymentMethod(method);
    setPmSaving(true);
    try {
      const res = await axios.post(
        `${API}/payments-hub/txn/${txnId}/set-payment-method`,
        { payment_method: method, bank_account_name: pmBankName, remarks: pmRemarks },
        { withCredentials: true },
      );
      toast.success(res.data.message || "Payment method updated");
      // Refresh the drawer so line items + total reflect the new card fee.
      const r = await axios.get(`${API}/payments-hub/detail/${txnId}`, { withCredentials: true });
      setDetail(r.data);
      if (onActionComplete) onActionComplete();
    } catch (err) {
      const __e = formatAdminError(err, "Failed to set payment method");
      toast.error(__e.message, __e.opts);
    } finally {
      setPmSaving(false);
    }
  };

  // Apply a bank suggestion → fills verification fields with one click
  const applyBankSuggestion = (sug) => {
    setVerifyBankRef(sug.bank_transaction_id || sug.reference_raw || "");
    setVerifyPayee(sug.source_name || "");
    const note = `Verified against bank entry ${sug.bank_transaction_id || ""} (${sug.confidence} match)`;
    setVerifyNote(note);
    toast.success("Bank entry applied — review and click Approve");
  };

  // Build the optional body for canonical approve calls. Empty fields are
  // omitted so we don't overwrite existing values with blanks server-side.
  const buildApproveBody = () => {
    const body = {};
    const ref = verifyBankRef.trim();
    const payee = verifyPayee.trim();
    const note = verifyNote.trim();
    if (ref) body.payment_reference_number = ref;
    if (payee) body.payee_bank_account_name = payee;
    if (note) body.notes = note;
    return body;
  };

  const amountInvoiced = detail?.amount_invoiced || 0;
  const amountReceived = parseFloat(receivedAmount) || 0;
  const diff = parseFloat((amountReceived - amountInvoiced).toFixed(2));
  const isShortpay = diff < 0;
  const isOverpay = diff > 0;
  const isMatch = Math.abs(diff) < 0.01;

  const approve = async () => {
    if (!detail) return;
    setActionLoading("approve");
    try {
      await axios.post(`${API}/payments/admin/approve/${txnId}`, buildApproveBody(), { withCredentials: true });
      toast.success("Payment approved");
      onActionComplete();
    } catch (err) {
      { const __e = formatAdminError(err, "Approve failed"); toast.error(__e.message, __e.opts); };
    } finally {
      setActionLoading(null);
    }
  };

  const requestDifference = async () => {
    if (!detail || !isShortpay) return;
    setActionLoading("shortfall");
    try {
      await axios.post(
        `${API}/payments-hub/txn/${txnId}/request-difference`,
        { shortfall_amount: Math.abs(diff), amount_received: amountReceived, note: adminNote },
        { withCredentials: true }
      );
      toast.success(`Shortfall notice sent for $${Math.abs(diff).toFixed(2)}`);
      onActionComplete();
    } catch (err) {
      { const __e = formatAdminError(err, "Failed"); toast.error(__e.message, __e.opts); };
    } finally {
      setActionLoading(null);
    }
  };

  const approveWithCredit = async () => {
    if (!detail || !isOverpay) return;
    setActionLoading("credit");
    try {
      await axios.post(
        `${API}/payments-hub/txn/${txnId}/approve-with-credit`,
        { amount_received: amountReceived, credit_amount: diff, note: adminNote },
        { withCredentials: true }
      );
      toast.success(`Approved · $${diff.toFixed(2)} credited to parent`);
      onActionComplete();
    } catch (err) {
      { const __e = formatAdminError(err, "Failed"); toast.error(__e.message, __e.opts); };
    } finally {
      setActionLoading(null);
    }
  };

  const dispute = async () => {
    if (!detail || !disputeReason.trim()) {
      toast.error("Enter a dispute reason");
      return;
    }
    setActionLoading("dispute");
    try {
      await axios.post(
        `${API}/payments/admin/dispute/${txnId}`,
        { dispute_reason: disputeReason, amount_received: amountReceived || null, send_email: true },
        { withCredentials: true }
      );
      toast.success("Dispute raised");
      onActionComplete();
    } catch (err) {
      { const __e = formatAdminError(err, "Failed"); toast.error(__e.message, __e.opts); };
    } finally {
      setActionLoading(null);
    }
  };

  const sendInvoice = async () => {
    if (!detail) return;
    setActionLoading("sendinv");
    try {
      const res = await axios.post(
        `${API}/payments/admin/resend-invoice/${txnId}`,
        {},
        { withCredentials: true }
      );
      toast.success(res.data?.message || "Invoice emailed to parent");
    } catch (err) {
      { const __e = formatAdminError(err, "Send invoice failed"); toast.error(__e.message, __e.opts); };
    } finally {
      setActionLoading(null);
    }
  };

  const sendReminder = async () => {
    if (!detail) return;
    setActionLoading("reminder");
    try {
      const res = await axios.post(
        `${API}/payments/admin/send-reminder/${txnId}`,
        {},
        { withCredentials: true }
      );
      toast.success(res.data?.message || "Reminder emailed to parent");
    } catch (err) {
      { const __e = formatAdminError(err, "Send reminder failed"); toast.error(__e.message, __e.opts); };
    } finally {
      setActionLoading(null);
    }
  };

  const markPaidOffline = async () => {
    if (!detail) return;
    if (!offlinePayee.trim() && !offlineBankRef.trim() && !offlineNote.trim()) {
      if (!window.confirm(
        "You're marking this invoice paid offline without a bank reference, payee name, or note.\n\n" +
        "This will move the payment straight to PAID and trigger enrollment + email.\n\n" +
        "Continue anyway?"
      )) {
        return;
      }
    }    if (!window.confirm(
      `Mark ${detail.reference_internal || "this invoice"} as PAID OFFLINE?\n\n` +
      `This will:\n` +
      `  • Move the txn from Unpaid → Paid\n` +
      `  • Activate the parent's roster (children become active)\n` +
      `  • Send the standard payment confirmation email\n\n` +
      `Use this only when the parent has paid you outside the app and provided proof.`
    )) {
      return;
    }
    setActionLoading("offline");
    try {
      const body = {
        payment_method: offlineMethod,
        bank_reference: offlineBankRef.trim() || null,
        payee_name: offlinePayee.trim() || null,
        note: offlineNote.trim() || null,
        amount_received: offlineAmount ? parseFloat(offlineAmount) : null,
      };
      const res = await axios.post(
        `${API}/payments-hub/txn/${txnId}/mark-paid-offline`,
        body,
        { withCredentials: true }
      );
      toast.success(res.data?.approve_result?.message || "Marked paid offline — parent notified");
      // Reset form + close drawer
      setShowOfflineForm(false);
      setOfflineBankRef("");
      setOfflinePayee("");
      setOfflineNote("");
      setOfflineAmount("");
      onActionComplete();
    } catch (err) {
      { const __e = formatAdminError(err, "Mark paid offline failed"); toast.error(__e.message, __e.opts); };
    } finally {
      setActionLoading(null);
    }
  };

  // Generic soft-delete cancel. First attempt sends confirm_paid=false; if the
  // backend returns 409 requires_paid_confirmation, we surface the extra
  // "this was paid — are you sure?" gate and retry with confirm_paid=true.
  const cancelTransaction = async () => {
    if (!detail) return;
    const reason = txnCancelReason.trim();
    if (reason.length < 5) {
      toast.error("Enter a reason (at least 5 characters) — it's logged to the audit trail");
      return;
    }
    const doCancel = async (confirmPaid) =>
      axios.post(`${API}/payments-hub/txn/${txnId}/cancel`,
        { reason, confirm_paid: confirmPaid }, { withCredentials: true });
    setActionLoading("canceltxn");
    try {
      await doCancel(false);
      toast.success("Transaction cancelled (archived, recoverable)");
      setShowTxnCancel(false); setTxnCancelReason("");
      onActionComplete();
    } catch (err) {
      const d = err.response?.data?.detail;
      const code = typeof d === "object" ? d?.code : null;
      if (err.response?.status === 409 && code === "requires_paid_confirmation") {
        if (window.confirm("⚠️ This transaction was PAID / approved — are you sure you want to cancel it?\n\nIt will be archived (recoverable), not deleted. No money is refunded automatically.")) {
          try {
            await doCancel(true);
            toast.success("Paid transaction cancelled (archived, recoverable)");
            setShowTxnCancel(false); setTxnCancelReason("");
            onActionComplete();
          } catch (e2) {
            toast.error(e2.response?.data?.detail?.message || e2.response?.data?.detail || "Cancel failed");
          }
        }
      } else {
        toast.error((typeof d === "string" ? d : d?.message) || "Cancel failed");
      }
    } finally {
      setActionLoading(null);
    }
  };

  const cancelRegistration = async () => {
    if (!detail) return;
    const reason = cancelReason.trim();
    if (reason.length < 5) {
      toast.error("Enter a reason (at least 5 characters) — it's logged to the audit trail");
      return;
    }
    if (!window.confirm(
      `CANCEL this registration?\n\n` +
      `• Reference: ${detail.reference_internal || txnId}\n` +
      `• Parent: ${detail.parent?.name || detail.parent?.email || "—"}\n` +
      `• Amount: $${(detail.amount_invoiced || 0).toFixed(2)}\n\n` +
      `This will:\n` +
      `  • Set the transaction status to "cancelled"\n` +
      `  • Set the registration status to "cancelled"\n` +
      `  • Add an admin note with your reason\n` +
      `  • NOT touch the children's player records\n` +
      `  • NOT affect any paid transactions or enrollments\n\n` +
      `Reason: "${reason}"\n\nProceed?`
    )) {
      return;
    }
    setActionLoading("cancelreg");
    try {
      const res = await axios.post(
        `${API}/payments-hub/txn/${txnId}/cancel-registration`,
        { reason },
        { withCredentials: true }
      );
      const regCancelled = res.data?.registration_cancelled;
      toast.success(
        regCancelled
          ? "Registration cancelled (txn + registration updated, audit logged)"
          : "Transaction cancelled (no linked registration found)"
      );
      setShowCancelForm(false);
      setCancelReason("");
      onActionComplete();
    } catch (err) {
      { const __e = formatAdminError(err, "Cancel failed"); toast.error(__e.message, __e.opts); };
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50" data-testid="detail-drawer">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full md:w-[640px] bg-slate-950 border-l border-slate-800 overflow-y-auto">
        <div className="sticky top-0 bg-slate-950/95 backdrop-blur border-b border-slate-800 p-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-emerald-400" />
              Payment Detail
            </h2>
            {detail?.type === "event_registration" && (
              <Badge
                className={`text-[10px] uppercase tracking-wider ${
                  detail.event_category === "tournament"
                    ? "bg-purple-500/20 text-purple-300 border border-purple-500/40"
                    : "bg-orange-500/20 text-orange-300 border border-orange-500/40"
                }`}
                data-testid="payment-detail-event-badge"
              >
                {detail.event_category === "tournament" ? "Tournament Registration" : "Event Registration"}
              </Badge>
            )}
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-800" data-testid="close-drawer">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {loading && (
          <div className="py-16 text-center">
            <Loader2 className="w-6 h-6 animate-spin text-emerald-400 mx-auto" />
          </div>
        )}

        {!loading && detail && (
          <div className="p-4 space-y-6">
            {/* Part 2 — duplicate sibling banner. Warns when this parent has
                other transaction(s) for the SAME registration (term + children)
                and offers a confirm-to-cancel for the unpaid one(s). */}
            {Array.isArray(detail.duplicate_siblings) && detail.duplicate_siblings.length > 0 && (
              <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3" data-testid="duplicate-sibling-banner">
                <p className="text-amber-300 text-sm font-semibold flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  This parent has {detail.duplicate_siblings.length} other transaction(s) for the same registration
                </p>
                <div className="mt-2 space-y-1.5">
                  {detail.duplicate_siblings.map((s) => {
                    const unpaid = s.status_bucket === "unpaid" && !s.is_archived;
                    return (
                      <div key={s.id} className="flex items-center justify-between text-xs bg-slate-900/60 rounded px-2 py-1.5" data-testid={`dup-sibling-${s.id}`}>
                        <span className="font-mono text-slate-300">{s.payment_reference || s.id.slice(0, 8)}</span>
                        <span className="text-slate-400">${Number(s.amount).toFixed(2)}</span>
                        <span className={`px-2 py-0.5 rounded ${s.is_archived ? "bg-slate-700 text-slate-400" : s.status_bucket === "paid" ? "bg-emerald-500/20 text-emerald-300" : "bg-rose-500/20 text-rose-300"}`}>
                          {s.is_archived ? "archived" : s.status}
                        </span>
                        {unpaid && detail.payment?.status && ["approved", "paid", "verified", "complete", "waived"].includes(String(detail.payment.status).toLowerCase()) ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-6 text-[11px] border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
                            data-testid={`dup-cancel-btn-${s.id}`}
                            disabled={dupArchiving === s.id}
                            onClick={() => archiveDuplicateSibling(s)}
                          >
                            {dupArchiving === s.id ? "Cancelling…" : "Confirm-cancel"}
                          </Button>
                        ) : (
                          <span className="text-[10px] text-slate-500 italic">{unpaid ? "cancel from paid txn" : ""}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {/* Proof of payment attached by the parent */}
            <PaymentProofsPanel reference={detail?.payment_reference} />

            {/* Cancel transaction (soft-delete / archive) — always available.
                Single confirm for unpaid; the backend gates paid/approved
                behind an extra "this was paid" confirmation. */}
            <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-3" data-testid="drawer-cancel-txn-card">
              {!showTxnCancel ? (
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs text-slate-400">Cancel this transaction — it's archived (recoverable) and drops off the active lists &amp; counts. Never deleted.</p>
                  <Button
                    onClick={() => setShowTxnCancel(true)}
                    size="sm"
                    variant="outline"
                    className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10 shrink-0"
                    data-testid="drawer-cancel-txn-open-btn"
                  >
                    <X className="w-4 h-4 mr-1" /> Cancel transaction
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <label className="block text-[10px] uppercase tracking-wide text-slate-500">Reason (required, min 5 chars — logged to audit trail)</label>
                  <Textarea
                    value={txnCancelReason}
                    onChange={(e) => setTxnCancelReason(e.target.value)}
                    placeholder="e.g. 'Erroneous charge — parent never intended to book'"
                    rows={2}
                    className="bg-slate-950 border-rose-500/30 text-slate-200"
                    data-testid="drawer-cancel-txn-reason-input"
                  />
                  <div className="flex gap-2">
                    <Button
                      onClick={cancelTransaction}
                      disabled={actionLoading === "canceltxn" || txnCancelReason.trim().length < 5}
                      size="sm"
                      className="bg-rose-600 hover:bg-rose-500 text-white"
                      data-testid="drawer-cancel-txn-confirm-btn"
                    >
                      {actionLoading === "canceltxn" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <X className="w-3.5 h-3.5 mr-1" />}
                      Confirm cancel
                    </Button>
                    <Button
                      onClick={() => { setShowTxnCancel(false); setTxnCancelReason(""); }}
                      disabled={actionLoading === "canceltxn"}
                      size="sm"
                      variant="ghost"
                      data-testid="drawer-cancel-txn-abort-btn"
                    >
                      Keep
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Section A — Who & what */}
            <section>
              <h3 className="text-xs uppercase text-slate-400 mb-2">Parent & Children</h3>
              <Card className="p-3 bg-slate-900/50 border-slate-800">
                <p className="text-white font-semibold">{detail.parent?.name || detail.parent?.display_name || "—"}</p>
                <p className="text-sm text-slate-400">{detail.parent?.email}</p>
                {detail.parent?.phone && <p className="text-sm text-slate-400">{detail.parent?.phone}</p>}
                <div className="flex flex-wrap gap-1 mt-2">
                  {detail.parent?.is_scc_member && <Badge className="text-[10px] bg-emerald-500/20 text-emerald-300">SCC member</Badge>}
                  {detail.parent?.is_digital_member && <Badge className="text-[10px] bg-blue-500/20 text-blue-300">Digital</Badge>}
                  {detail.parent?.member_type && detail.parent?.member_type !== "non-member" && <Badge className="text-[10px] bg-slate-700">{detail.parent?.member_type}</Badge>}
                  {!detail.parent?.is_scc_member && !detail.parent?.member_type && <Badge className="text-[10px] bg-slate-700/50 text-slate-400">Non-member</Badge>}
                </div>
              </Card>

              {/* Event registration banner — shown for camp/tournament
                  payments so admin sees exactly which event the payment
                  is for without scrolling. */}
              {detail.type === "event_registration" && detail.event_name && (
                <Card
                  className="p-3 mt-2 bg-slate-900/50 border-slate-800"
                  data-testid="payment-detail-event-panel"
                >
                  <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">
                    {detail.event_category === "tournament" ? "Tournament" : "Camp"}
                  </p>
                  <p className="text-white font-semibold" data-testid="payment-detail-event-name">
                    {detail.event_name}
                  </p>
                  <div className="text-xs text-slate-400 mt-1 flex flex-wrap gap-x-3 gap-y-1">
                    {detail.event_start_date && (
                      <span>{detail.event_start_date}{detail.event_end_date && detail.event_end_date !== detail.event_start_date ? ` → ${detail.event_end_date}` : ""}</span>
                    )}
                    {detail.event_location && <span>· {detail.event_location}</span>}
                    {detail.event_slug && (
                      <a
                        href={`/events/${detail.event_slug}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-400 hover:underline"
                        data-testid="payment-detail-event-link"
                      >
                        /events/{detail.event_slug} ↗
                      </a>
                    )}
                  </div>
                </Card>
              )}

              {detail.children?.length > 0 ? (
                <div className="mt-2 space-y-1">
                  {detail.children.map((c, i) => {
                    const hasAgeGroup = !!c.age_group;
                    return (
                      <Card key={i} className="p-2 bg-slate-900/30 border-slate-800 text-sm" data-testid={`payment-detail-child-${i}`}>
                        <div className="flex justify-between items-start gap-2">
                          <span className="text-white">{c.name || "—"}</span>
                          <span
                            className={`text-xs whitespace-nowrap ${hasAgeGroup ? "text-slate-400" : "text-amber-400"}`}
                            data-testid={`payment-detail-child-${i}-age-group`}
                          >
                            {hasAgeGroup ? c.age_group : "Age group not assigned"}
                          </span>
                        </div>
                        <p className="text-xs text-slate-400 mt-1">
                          {c.sessions_per_week} session{c.sessions_per_week !== 1 ? "s" : ""}/week
                        </p>
                        {!hasAgeGroup && (
                          <p className="text-[10px] text-amber-400/80 mt-1 italic" data-testid={`payment-detail-child-${i}-age-group-hint`}>
                            ⚠ Assign team to resolve age group
                          </p>
                        )}
                      </Card>
                    );
                  })}
                </div>
              ) : (
                <p className="mt-2 text-xs text-slate-500 italic" data-testid="payment-detail-no-children">
                  {detail.type === "event_registration"
                    ? "No child name recorded on this event registration."
                    : "No children linked to this payment (likely a DM-only or standalone invoice)."}
                </p>
              )}
            </section>

            {/* Section A' — Fee breakdown */}
            {detail.fee_breakdown && (detail.fee_breakdown.line_items?.length > 0 || typeof detail.fee_breakdown.total === "number") && (
              <section>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs uppercase text-slate-400">Fee Breakdown</h3>
                  <div className="flex items-center gap-2">
                    {detail.fee_breakdown.term_weeks && (
                      <span className="text-[10px] text-slate-500" data-testid="fee-breakdown-term-weeks">
                        {detail.fee_breakdown.term_weeks} week term
                      </span>
                    )}
                    {detail.registration_week_label && (
                      <span
                        className="text-[10px] text-cyan-300/80 font-medium"
                        title={`Registered on ${detail.term_start_date ? "term starting " + new Date(detail.term_start_date).toLocaleDateString() : "this term"}`}
                        data-testid="fee-breakdown-registration-week"
                      >
                        Reg: {detail.registration_week_label}
                      </span>
                    )}
                    {detail.fee_breakdown.source_fallback && (
                      <span
                        className="text-[10px] text-amber-400/70 italic"
                        title="Limited line-item data on the underlying registration — drawer is showing only what's stored. Run 'Fee-breakdown drift' repair from the toolbar for full detail."
                        data-testid="fee-breakdown-synthesized-tag"
                      >
                        ⚠ synthesized — run Fee-breakdown drift repair for full detail
                      </span>
                    )}
                  </div>
                </div>
                {/* Data-drift hint: surfaces when the stored line items don't
                    reconcile to the stored total. Under the strict synthesis
                    rule we never invent a "Discount applied" line for this
                    gap — instead we show the gap honestly here so the admin
                    knows there's data to repair. */}
                {detail.fee_breakdown.source_fallback
                 && Math.abs(detail.fee_breakdown.synth_unexplained_gap || 0) >= 0.05 && (
                  <div
                    className="mb-2 px-3 py-2 rounded border border-amber-500/40 bg-amber-500/10 text-[11px] text-amber-200"
                    data-testid="fee-breakdown-data-drift-banner"
                  >
                    <div className="font-semibold mb-0.5">
                      ⚠ Stored line items don&apos;t reconcile to total
                      {detail.fee_breakdown.synth_unexplained_gap < 0 && (
                        <span className="ml-1 text-amber-300/90 font-normal">
                          — likely a member/digital discount that wasn&apos;t itemised
                        </span>
                      )}
                    </div>
                    <div className="font-mono text-[10px] text-amber-100/80">
                      Unexplained gap: ${Math.abs(detail.fee_breakdown.synth_unexplained_gap).toFixed(2)}
                      {" "}({detail.fee_breakdown.synth_unexplained_gap > 0 ? "line items sum LESS than total" : "line items sum MORE than total (discount not shown)"})
                    </div>
                    <div className="text-amber-300/80 mt-1">
                      → Click &quot;Fee-breakdown drift&quot; in the toolbar to repair this row&apos;s line_items.
                    </div>
                    <button
                      type="button"
                      onClick={handleReconcile}
                      disabled={reconciling}
                      className="mt-2 inline-flex items-center gap-1.5 rounded bg-amber-500 hover:bg-amber-400 text-black text-[11px] font-semibold px-3 py-1.5 disabled:opacity-60"
                      data-testid="fb-reconcile-button"
                    >
                      {reconciling ? "Reconciling…" : "Reconcile now"}
                    </button>
                  </div>
                )}
                {detail.fee_breakdown.reconciled_at && (
                  <div
                    className="mb-2 px-3 py-2 rounded border border-sky-500/40 bg-sky-500/10 text-[11px] text-sky-200 flex items-center justify-between gap-2"
                    data-testid="fb-reconciled-banner"
                  >
                    <span>
                      ✓ Reconciled by {detail.fee_breakdown.reconciled_by} on {String(detail.fee_breakdown.reconciled_at).split("T")[0]}
                      {" "}({detail.fee_breakdown.reconcile_method === "canonical_rebuild"
                        ? "rebuilt real lines"
                        : detail.fee_breakdown.reconcile_method === "credit_applied"
                          ? "credit itemised"
                          : "legacy adjustment line"})
                    </span>
                    <button
                      type="button"
                      onClick={handleUndoReconcile}
                      disabled={reconciling}
                      className="shrink-0 rounded border border-sky-400/50 hover:bg-sky-500/20 px-2 py-1 text-[10px] disabled:opacity-60"
                      data-testid="fb-reconcile-undo-button"
                    >
                      Undo
                    </button>
                  </div>
                )}
                <Card className="p-3 bg-slate-900/50 border-slate-800 text-sm space-y-2" data-testid="payment-detail-fee-breakdown">
                  {/* Bank-note compact block — click Copy to paste straight into
                      the bank transaction Note field. Terse by design. */}
                  {(() => {
                    const note = buildBankNote(detail.fee_breakdown, detail);
                    if (!note) return null;
                    return (
                      <div className="mb-2 rounded border border-emerald-500/30 bg-emerald-500/5 p-2" data-testid="fb-bank-note-block">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[10px] uppercase tracking-wide text-emerald-300/80 font-semibold">
                            Bank note (copy to transaction)
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              copyToClipboardFallback(note).then(() => {
                                toast.success("Bank note copied");
                              });
                            }}
                            className="flex items-center gap-1 text-[10px] text-emerald-300 hover:text-emerald-200 border border-emerald-500/40 rounded px-2 py-0.5"
                            data-testid="fb-bank-note-copy"
                          >
                            <Copy className="w-3 h-3" /> Copy
                          </button>
                        </div>
                        <pre
                          className="text-[11px] text-emerald-100 font-mono whitespace-pre-wrap leading-snug m-0"
                          data-testid="fb-bank-note-text"
                        >{note}</pre>
                      </div>
                    );
                  })()}
                  {detail.fee_breakdown.line_items?.map((li, i) => {
                    // Digital Membership or other top-level addon line — render flat
                    if (li.kind === "digital_membership" || li.label) {
                      const amt = li.amount ?? li.child_total ?? 0;
                      const isDiscount = amt < 0;
                      // Synthesized "Base term fee" lines carry sessions_total
                      // + rate_per_session — render them as a math sub-line so the
                      // admin can see exactly what was billed at what rate
                      // (Feb 2026 visibility fix). `rate_per_session` comes
                      // from the rate_table (authoritative); falls back to
                      // implied only when the term has no rate_table.
                      const showSessionMath = li.sessions_total && li.rate_per_session;
                      const rateFromImplied = li.rate_source === "implied";
                      return (
                        <div key={i} data-testid={`fb-addon-${i}`}>
                          <div className="flex justify-between">
                            <span className={li.is_implied_discount ? "text-emerald-300/90 italic" : "text-slate-300"}>
                              {li.label || li.description}
                            </span>
                            <span className={`font-mono ${isDiscount ? "text-emerald-300" : "text-white"}`}>
                              {isDiscount ? "−" : ""}${Math.abs(amt).toFixed(2)}
                            </span>
                          </div>
                          {showSessionMath && (
                            <p className="text-[10px] text-slate-500 mt-0.5 ml-1" data-testid={`fb-addon-${i}-math`}>
                              {li.sessions_total} sessions × ${Number(li.rate_per_session).toFixed(2)}/session
                              {rateFromImplied && (
                                <span
                                  className="text-amber-400/80 ml-1"
                                  title="No rate table found for this term — rate computed from the stored subtotal"
                                >
                                  (implied — no rate table linked)
                                </span>
                              )}
                            </p>
                          )}
                        </div>
                      );
                    }
                    // Per-child line_item — expand the breakdown sub-dict
                    const childName = li.child_name || `Child ${li.child_index || i + 1}`;
                    const sessionsPerWeek = li.sessions_per_week || 0;
                    const termWeeks = detail.fee_breakdown.term_weeks || null;
                    const sub = li.breakdown || {};
                    // `session_subtotal` on new rows is NET; `subtotal` on
                    // sub-breakdown is GROSS. Fall back to the flat li.amount
                    // (what legacy records like Andrew Varona have — the un-
                    // discounted term charge).
                    const grossTermFees = Number(
                      sub.subtotal
                        ?? (typeof li.amount === "number" ? li.amount : undefined)
                        ?? li.session_subtotal
                        ?? 0
                    );
                    const memberAmt = Number(sub.membership_discount_amount || 0);
                    const siblingAmt = Number(sub.sibling_discount_amount || 0);
                    const volumeAmt = Number(sub.volume_discount_amount || 0);
                    const regFee = Number(li.registration_fee || 0);
                    const finalTotal = Number(sub.final_total ?? li.child_total ?? 0);
                    const memberPct = Number(li.discount_pct || 0);
                    const siblingPct = Number(li.sibling_discount_pct || 0);
                    const dimensionLabel = sessionsPerWeek > 0
                      ? `(${sessionsPerWeek}×/wk${termWeeks ? ` × ${termWeeks}wks` : ""})`
                      : "Term fees";
                    // Legacy detection: no discount sub-fields stored even though
                    // the top-level charge implies one (final_total < gross).
                    // Derive an "implied discount" for display when the gap
                    // exceeds a rounding cent — this fixes Andrew Varona's row
                    // where the 10% Digital-Member discount was applied to
                    // `amount` but never itemised.
                    const hasFullBreakdown = (
                      "membership_discount_amount" in sub
                      || "sibling_discount_amount" in sub
                      || "volume_discount_amount" in sub
                      || "subtotal" in sub
                    );
                    const impliedDiscount = (
                      !hasFullBreakdown
                      && grossTermFees > 0
                      && finalTotal > 0
                      && grossTermFees - regFee - finalTotal >= 0.05
                    ) ? Math.round((grossTermFees - regFee - finalTotal) * 100) / 100 : 0;
                    const impliedPct = impliedDiscount > 0 && grossTermFees > 0
                      ? Math.round((impliedDiscount / grossTermFees) * 100) : 0;
                    return (
                      <div key={i} className="border-l-2 border-slate-700 pl-2 py-1.5" data-testid={`fb-child-${i}`}>
                        <div className="text-slate-100 font-semibold mb-1">{childName}</div>
                        <div className="text-xs space-y-0.5 text-slate-300">
                          {/* Term fees / dimension line — always shown */}
                          <div className="flex justify-between">
                            <span className="text-slate-400">{dimensionLabel}</span>
                            <span className="font-mono">${grossTermFees.toFixed(2)}</span>
                          </div>
                          {/* Signup fee (if any) — separate line under the child */}
                          {regFee > 0 && (
                            <div className="flex justify-between">
                              <span className="text-slate-400">Sign-up fee</span>
                              <span className="font-mono">+${regFee.toFixed(2)}</span>
                            </div>
                          )}
                          {/* Discounts — real values when stored, derived
                              "implied" line when only the net total was written */}
                          {memberAmt > 0 && (
                            <div className="flex justify-between text-emerald-300" data-testid={`fb-child-${i}-member-discount`}>
                              <span>Member disc{memberPct > 0 ? ` ${memberPct.toFixed(0)}%` : ""}{detail.fee_breakdown.discount_stacking_mode === "highest_only" ? " · highest-only" : ""}</span>
                              <span className="font-mono">−${memberAmt.toFixed(2)}</span>
                            </div>
                          )}
                          {siblingAmt > 0 && (
                            <div className="flex justify-between text-emerald-300" data-testid={`fb-child-${i}-sibling-discount`}>
                              <span>Sibling disc{siblingPct > 0 ? ` ${siblingPct.toFixed(0)}%` : ""}</span>
                              <span className="font-mono">−${siblingAmt.toFixed(2)}</span>
                            </div>
                          )}
                          {volumeAmt > 0 && (
                            <div className="flex justify-between text-emerald-300">
                              <span>Volume disc</span>
                              <span className="font-mono">−${volumeAmt.toFixed(2)}</span>
                            </div>
                          )}
                          {impliedDiscount > 0 && (
                            <div className="flex justify-between text-emerald-300/90 italic" data-testid={`fb-child-${i}-implied-discount`}>
                              <span title="Discount inferred from the gap between the stored line amount and the invoiced total. Run Fee-breakdown drift repair to itemise this properly.">
                                Discount {impliedPct > 0 ? `(${impliedPct}%)` : ""} — implied
                              </span>
                              <span className="font-mono">−${impliedDiscount.toFixed(2)}</span>
                            </div>
                          )}
                          {/* Per-child running total */}
                          <div className="flex justify-between pt-1 border-t border-slate-800 mt-1">
                            <span className="text-slate-200">Total this invoice</span>
                            <span className="font-mono text-slate-100">${finalTotal.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {Array.isArray(detail.fee_breakdown.line_items) && detail.fee_breakdown.line_items.length > 0 && (() => {
                    const items = detail.fee_breakdown.line_items;
                    const subtotal = items.reduce((s, li) => { const a = Number(li.amount ?? li.child_total ?? 0); return a > 0 ? s + a : s; }, 0);
                    const discountTotal = items.reduce((s, li) => { const a = Number(li.amount ?? li.child_total ?? 0); return a < 0 ? s + a : s; }, 0);
                    if (discountTotal >= -0.005) return null; // no discounts → subtotal == total, skip
                    return (
                      <>
                        <div className="flex justify-between pt-2 border-t border-slate-800 mt-1">
                          <span className="text-slate-300 font-medium">Subtotal</span>
                          <span className="font-mono text-slate-200" data-testid="fb-subtotal">${subtotal.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-emerald-300/90">Total discounts</span>
                          <span className="font-mono text-emerald-300" data-testid="fb-discount-total">−${Math.abs(discountTotal).toFixed(2)}</span>
                        </div>
                      </>
                    );
                  })()}
                  {typeof detail.fee_breakdown.total === "number" && (
                    <div className="flex justify-between pt-2 border-t border-slate-800 mt-1">
                      <span className="font-semibold text-emerald-300">Total invoiced</span>
                      <span className="font-mono font-bold text-emerald-300" data-testid="fb-total-invoiced">${detail.fee_breakdown.total.toFixed(2)}</span>
                    </div>
                  )}
                  {detail.fee_breakdown.source === "admin_override" && (
                    <div className="mt-2 pt-2 border-t border-amber-500/30 text-[10px] text-amber-300/90" data-testid="fb-override-stamp">
                      Manually overridden by {detail.fee_breakdown.overridden_by} • {detail.fee_breakdown.overridden_at?.split("T")[0]}
                      {detail.fee_breakdown.override_note && (
                        <div className="text-amber-200/80 italic mt-0.5">"{detail.fee_breakdown.override_note}"</div>
                      )}
                    </div>
                  )}
                </Card>

                {/* Jun 2026 — per-invoice reconcile, right where the problem
                    is visible. Explains the existing gap (e.g. a credit that
                    was deducted but never itemised); never re-runs the fee
                    calc, never changes the charged amount. */}
                {Math.abs(detail.fee_breakdown.reconcile_gap || 0) >= 0.05 && !detail.fee_breakdown.reconciled_at && (
                  <div
                    className="mt-2 rounded border border-amber-500/40 bg-amber-500/10 p-3 text-[11px] text-amber-100"
                    data-testid="fb-repair-panel"
                  >
                    <div className="font-semibold text-amber-200 mb-1">
                      ⚠ This invoice doesn&apos;t reconcile
                    </div>
                    <div className="font-mono text-[10px] text-amber-100/80 mb-2">
                      Breakdown ${Number(detail.fee_breakdown.total || 0).toFixed(2)} vs charged $
                      {Number(detail.amount_invoiced ?? 0).toFixed(2)}
                      {" · "}gap ${Math.abs(detail.fee_breakdown.reconcile_gap).toFixed(2)}
                    </div>

                    {!repairPreview && (
                      <div className="flex flex-wrap items-center gap-2">
                        <Button
                          size="sm"
                          className="h-7 text-xs bg-amber-500 hover:bg-amber-400 text-black font-semibold"
                          onClick={previewRepair}
                          disabled={reconciling}
                          data-testid="fb-repair-preview-button"
                        >
                          {reconciling ? "Checking…" : "Reconcile this invoice"}
                        </Button>
                        <button
                          type="button"
                          onClick={() => runBulkSweep(false)}
                          disabled={reconciling}
                          className="text-[10px] underline text-amber-300/80 hover:text-amber-200 disabled:opacity-60"
                          data-testid="fb-repair-bulk-button"
                        >
                          Fix all invoices like this…
                        </button>
                      </div>
                    )}

                    {repairPreview?.status === "needs_manual_review" && (
                      <div className="rounded border border-amber-400/40 bg-slate-900/40 p-2" data-testid="fb-repair-needs-review">
                        <div className="font-semibold text-amber-200 mb-0.5">Needs manual review</div>
                        <p className="text-amber-100/80">{repairPreview.note}</p>
                        <button
                          type="button"
                          onClick={() => setRepairPreview(null)}
                          className="mt-2 text-[10px] underline text-amber-300/80 hover:text-amber-200"
                          data-testid="fb-repair-dismiss"
                        >
                          Close
                        </button>
                      </div>
                    )}

                    {repairPreview?.status === "preview" && (
                      <div className="rounded border border-emerald-400/40 bg-slate-900/40 p-2" data-testid="fb-repair-preview">
                        <div className="text-[10px] uppercase tracking-wide text-emerald-300/80 font-semibold mb-1">
                          Line{(repairPreview.credit_lines?.length || 1) > 1 ? "s" : ""} to be added
                        </div>
                        {(repairPreview.credit_lines || []).map((li, i) => (
                          <div
                            key={i}
                            className="flex justify-between font-mono text-emerald-200"
                            data-testid={i === 0 ? "fb-repair-preview-line" : `fb-repair-preview-line-${i}`}
                          >
                            <span>{li.label}</span>
                            <span>−${Math.abs(li.amount).toFixed(2)}</span>
                          </div>
                        ))}
                        {(repairPreview.credit_lines?.length || 0) > 1 && (
                          <div className="flex justify-between font-mono text-emerald-300/80 border-t border-emerald-500/20 mt-1 pt-1">
                            <span>Total credit</span>
                            <span>−${Number(repairPreview.gap).toFixed(2)}</span>
                          </div>
                        )}
                        <p className="text-amber-100/70 mt-1">
                          The charged amount (${Number(repairPreview.amount).toFixed(2)}) does not change.
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <Button
                            size="sm"
                            className="h-7 text-xs bg-emerald-500 hover:bg-emerald-400 text-black font-semibold"
                            onClick={applyRepair}
                            disabled={reconciling}
                            data-testid="fb-repair-apply-button"
                          >
                            {reconciling ? "Applying…" : "Apply"}
                          </Button>
                          <button
                            type="button"
                            onClick={() => setRepairPreview(null)}
                            className="text-[10px] underline text-amber-300/80 hover:text-amber-200"
                            data-testid="fb-repair-cancel"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}

                    {bulkSweep && (
                      <div className="mt-2 rounded border border-slate-600/50 bg-slate-900/50 p-2" data-testid="fb-repair-bulk-result">
                        <div className="font-semibold text-slate-200 mb-1">
                          {bulkSweep.dry_run ? "Dry run" : "Applied"} — {bulkSweep.dry_run ? bulkSweep.to_fix_count : bulkSweep.applied} invoice(s)
                          {bulkSweep.needs_manual_review_count > 0 && (
                            <span className="text-amber-300/80 font-normal">
                              {" "}· {bulkSweep.needs_manual_review_count} need manual review
                            </span>
                          )}
                        </div>
                        <ul className="max-h-28 overflow-auto space-y-0.5 font-mono text-[10px] text-slate-300">
                          {(bulkSweep.to_fix || []).slice(0, 25).map((r) => (
                            <li key={r.txn_id}>
                              {r.payment_reference || r.txn_id.slice(0, 8)} · {r.parent_name} · −${r.gap.toFixed(2)}
                            </li>
                          ))}
                        </ul>
                        <div className="flex items-center gap-2 mt-2">
                          {bulkSweep.dry_run && bulkSweep.to_fix_count > 0 && (
                            <Button
                              size="sm"
                              className="h-7 text-xs bg-emerald-500 hover:bg-emerald-400 text-black font-semibold"
                              onClick={() => runBulkSweep(true)}
                              disabled={reconciling}
                              data-testid="fb-repair-bulk-apply"
                            >
                              Apply to all
                            </Button>
                          )}
                          {!bulkSweep.dry_run && bulkSweep.run_id && (
                            <button
                              type="button"
                              onClick={() => revertBulkSweep(bulkSweep.run_id)}
                              disabled={reconciling}
                              className="text-[10px] underline text-sky-300/80 hover:text-sky-200"
                              data-testid="fb-repair-bulk-revert"
                            >
                              Revert this sweep
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => setBulkSweep(null)}
                            className="text-[10px] underline text-slate-400 hover:text-slate-200"
                            data-testid="fb-repair-bulk-close"
                          >
                            Close
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Manual line-item override — for irrecoverable fee-breakdown
                    drift, goodwill credits, retroactive adjustments. */}
                <div className="mt-2">
                  {!overrideOpen ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10 h-7 text-xs"
                      onClick={openOverrideEditor}
                      data-testid="open-line-item-override"
                    >
                      Override line items
                    </Button>
                  ) : (
                    <Card className="mt-2 p-3 bg-amber-500/5 border-amber-500/30" data-testid="line-item-override-editor">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-xs font-semibold text-amber-200 uppercase">Manual override</h4>
                        <button
                          type="button"
                          onClick={() => setOverrideOpen(false)}
                          className="text-amber-300/80 hover:text-amber-200 text-xs"
                          data-testid="cancel-line-item-override"
                        >
                          Cancel
                        </button>
                      </div>
                      <p className="text-[11px] text-amber-200/70 mb-3">
                        Replace the line items below. The new sum becomes the invoiced amount. Use negative amounts for discounts/credits.
                      </p>
                      <div className="space-y-2 mb-3">
                        {overrideRows.map((row, i) => (
                          <div key={i} className="flex items-center gap-2" data-testid={`override-row-${i}`}>
                            <Input
                              type="text"
                              placeholder="Label (e.g. Term fee)"
                              value={row.label}
                              onChange={(e) => handleOverrideRowChange(i, "label", e.target.value)}
                              className="flex-1 bg-slate-800 border-slate-700 text-white h-8 text-xs"
                              data-testid={`override-row-${i}-label`}
                            />
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="0.00"
                              value={row.amount}
                              onChange={(e) => handleOverrideRowChange(i, "amount", e.target.value)}
                              className="w-28 bg-slate-800 border-slate-700 text-white h-8 text-xs font-mono"
                              data-testid={`override-row-${i}-amount`}
                            />
                            <button
                              type="button"
                              onClick={() => removeOverrideRow(i)}
                              disabled={overrideRows.length <= 1}
                              className="text-rose-400 hover:text-rose-300 disabled:opacity-30 px-1 text-xs"
                              data-testid={`override-row-${i}-remove`}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={addOverrideRow}
                          className="text-xs text-amber-300 hover:text-amber-200"
                          data-testid="override-add-row"
                        >
                          + Add line
                        </button>
                      </div>
                      <div className="flex items-center justify-between mb-3 px-1">
                        <span className="text-xs text-amber-200/80">New total</span>
                        <span className="font-mono text-sm font-bold text-amber-200" data-testid="override-new-total">${overrideTotal.toFixed(2)}</span>
                      </div>
                      <Textarea
                        value={overrideNote}
                        onChange={(e) => setOverrideNote(e.target.value)}
                        placeholder="Reason for override (audit trail) — e.g. Refund for 3 missed sessions Apr 2026"
                        rows={2}
                        className="bg-slate-800 border-slate-700 text-white text-xs mb-3"
                        data-testid="override-note"
                        maxLength={500}
                      />
                      <label className="flex items-center gap-2 text-xs text-amber-200/90 mb-3 cursor-pointer" data-testid="override-send-email-label">
                        <input
                          type="checkbox"
                          checked={overrideSendEmail}
                          onChange={(e) => setOverrideSendEmail(e.target.checked)}
                          className="accent-amber-500"
                          data-testid="override-send-email"
                        />
                        Email updated invoice (with PDF) to {detail?.parent_email || "parent"}
                      </label>
                      <Button
                        size="sm"
                        onClick={handleOverrideSave}
                        disabled={overrideSaving}
                        className="bg-amber-600 hover:bg-amber-500 text-white w-full h-8 text-xs"
                        data-testid="save-line-item-override"
                      >
                        {overrideSaving
                          ? "Saving…"
                          : overrideSendEmail
                            ? "Save & email invoice"
                            : "Save (no email)"}
                      </Button>
                    </Card>
                  )}
                </div>
              </section>
            )}


            {/* Section B — Payment status */}
            <section>
              <h3 className="text-xs uppercase text-slate-400 mb-2">Payment Status</h3>
              <Card className="p-3 bg-slate-900/50 border-slate-800">
                <div className="flex items-center justify-between mb-2">
                  <span>{statusBadge(detail.status)}</span>
                  {detail.reference_internal && <code className="text-xs text-slate-400">{detail.reference_internal}</code>}
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-xs text-slate-500">Amount invoiced</p>
                    <p className="font-mono text-white">${amountInvoiced.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Amount received</p>
                    <Input
                      type="number"
                      step="0.01"
                      value={receivedAmount}
                      onChange={(e) => setReceivedAmount(e.target.value)}
                      className="bg-slate-800 border-slate-700 text-white h-8 text-sm font-mono"
                      placeholder={amountInvoiced.toFixed(2)}
                      data-testid="received-amount-input"
                    />
                  </div>
                </div>
                {!isMatch && (
                  <div className={`mt-3 p-2 rounded border text-sm ${isShortpay ? "bg-amber-500/10 border-amber-500/40 text-amber-200" : "bg-teal-500/10 border-teal-500/40 text-teal-200"}`}>
                    <div className="flex items-center gap-2">
                      {isShortpay ? <TrendingDown className="w-4 h-4" /> : <TrendingUp className="w-4 h-4" />}
                      <span className="font-semibold">
                        {isShortpay ? `Shortfall $${Math.abs(diff).toFixed(2)}` : `Overpayment $${diff.toFixed(2)}`}
                      </span>
                    </div>
                  </div>
                )}
                {detail.bank_account_holder_name && (
                  <p className="text-xs text-slate-500 mt-2">Parent declared bank: <strong className="text-slate-300">{detail.bank_account_holder_name}</strong></p>
                )}

                {/* Bank import suggestions — proactive match against
                    bank_upload_entries from imported Wise CSVs */}
                {bankSuggestionsLoaded && bankSuggestions.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-800" data-testid="bank-suggest-card">
                    <p className="text-xs uppercase text-emerald-400 mb-2 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      Matching bank entries found ({bankSuggestions.length})
                    </p>
                    <div className="space-y-2">
                      {bankSuggestions.map((sug) => {
                        const dt = sug.finished_on ? sug.finished_on.split(" ")[0] : "";
                        const conf = sug.confidence || "low";
                        const confColor = {
                          exact: "bg-emerald-500/20 text-emerald-300",
                          high: "bg-blue-500/20 text-blue-300",
                          medium: "bg-amber-500/20 text-amber-300",
                          low: "bg-slate-700/30 text-slate-300",
                        }[conf];
                        return (
                          <Card
                            key={sug.id}
                            className="p-2 bg-slate-900/40 border-slate-700 text-xs"
                            data-testid={`bank-suggest-row-${sug.id}`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${confColor}`}>
                                    {conf.toUpperCase()}
                                  </span>
                                  <span className="text-slate-300">{sug.reason}</span>
                                </div>
                                <p className="text-white mt-1 truncate">
                                  {sug.bank_transaction_id} · <span className="font-mono text-emerald-300">${(sug.source_amount || 0).toFixed(2)}</span>
                                </p>
                                <p className="text-slate-400 truncate">
                                  {sug.source_name || "—"} {dt && `· ${dt}`}
                                </p>
                                {sug.is_already_matched_to_this_txn && (
                                  <p className="text-[10px] text-emerald-400 italic">already linked to this txn</p>
                                )}
                              </div>
                              <Button
                                onClick={() => applyBankSuggestion(sug)}
                                size="sm"
                                variant="outline"
                                className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 h-7 px-2 text-xs flex-shrink-0"
                                data-testid={`bank-suggest-apply-${sug.id}`}
                              >
                                Apply
                              </Button>
                            </div>
                          </Card>
                        );
                      })}
                    </div>
                    <p className="mt-2 text-[10px] text-slate-500 italic">
                      Suggestions are read-only — clicking Apply prefills the form below; Approve still needed to commit.
                    </p>
                  </div>
                )}

                {/* Optional verification details — visible for any state where
                    Approve / Resolve makes sense. Fed into Approve / Approve
                    Anyway / Approve+Credit / Resolve via buildApproveBody(). */}
                {(detail.status === "claimed" || detail.status === "disputed" || detail.status === "partial") && (
                  <div className="mt-3 pt-3 border-t border-slate-800 space-y-2">
                    <p className="text-xs uppercase text-slate-400" data-testid="verify-details-label">
                      Verification details <span className="lowercase text-slate-500 italic">(optional — saved on Approve)</span>
                    </p>
                    {/* Payment method — REQUIRED. Drives the card fee (canonical
                        billing): Credit Card → gross-up fee added; others → none. */}
                    <div>
                      <label className="text-[10px] text-slate-500 block mb-1">
                        Payment method <span className="text-rose-400">*</span>
                      </label>
                      <div className="grid grid-cols-2 gap-1" data-testid="payment-method-selector">
                        {[
                          ["credit_card", "Credit Card"],
                          ["paynow", "PayNow Bank Transfer"],
                          ["cash", "Cash"],
                          ["others", "Others"],
                        ].map(([val, lbl]) => (
                          <button
                            key={val}
                            type="button"
                            disabled={pmSaving}
                            onClick={() => applyPaymentMethod(val)}
                            className={`text-xs h-8 rounded border px-2 transition-colors ${
                              paymentMethod === val
                                ? "bg-sky-600 border-sky-500 text-white"
                                : "bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700"
                            }`}
                            data-testid={`payment-method-${val}`}
                          >
                            {lbl}
                          </button>
                        ))}
                      </div>
                      {paymentMethod === "credit_card" && (
                        <p className="text-[10px] text-amber-400 mt-1" data-testid="pm-card-fee-note">
                          A card processing fee (3.4% + $0.50, grossed up) will be added to the invoice.
                        </p>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[10px] text-slate-500 block">Bank reference</label>
                        <Input
                          value={verifyBankRef}
                          onChange={(e) => setVerifyBankRef(e.target.value)}
                          placeholder="e.g. PNT240424123456"
                          className="bg-slate-800 border-slate-700 text-white text-xs h-8"
                          data-testid="verify-bank-ref-input"
                        />
                      </div>
                      {paymentMethod === "paynow" && (
                        <div>
                          <label className="text-[10px] text-slate-500 block">Bank account name</label>
                          <Input
                            value={pmBankName}
                            onChange={(e) => { setPmBankName(e.target.value); setVerifyPayee(e.target.value); }}
                            placeholder="Name on bank statement"
                            className="bg-slate-800 border-slate-700 text-white text-xs h-8"
                            data-testid="verify-payee-input"
                          />
                        </div>
                      )}
                      {(paymentMethod === "cash" || paymentMethod === "others") && (
                        <div>
                          <label className="text-[10px] text-slate-500 block">Remarks</label>
                          <Input
                            value={pmRemarks}
                            onChange={(e) => setPmRemarks(e.target.value)}
                            placeholder="e.g. paid in cash at office"
                            className="bg-slate-800 border-slate-700 text-white text-xs h-8"
                            data-testid="pm-remarks-input"
                          />
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="text-[10px] text-slate-500 block">Note</label>
                      <Input
                        value={verifyNote}
                        onChange={(e) => setVerifyNote(e.target.value)}
                        placeholder="Optional admin note (saved with the approval)"
                        className="bg-slate-800 border-slate-700 text-white text-xs h-8"
                        data-testid="verify-note-input"
                      />
                    </div>
                  </div>
                )}
              </Card>
            </section>

            {/* Section B' — Timeline */}
            {detail.timeline?.length > 0 && (
              <section>
                <h3 className="text-xs uppercase text-slate-400 mb-2">Timeline</h3>
                {detail.invoice_send_error && (
                  <Card
                    className="p-3 mb-2 bg-red-500/10 border-red-500/40"
                    data-testid="invoice-send-error-callout"
                  >
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 mt-0.5 text-red-300 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-red-200 font-semibold">
                          Invoice email never reached the parent
                        </p>
                        <p className="text-xs text-red-300/80 mt-1 font-mono break-all">
                          {detail.invoice_send_error}
                        </p>
                        {detail.invoice_send_error_at && (
                          <p className="text-[10px] text-red-300/60 mt-1">
                            Last attempt: {new Date(detail.invoice_send_error_at).toLocaleString()}
                          </p>
                        )}
                        <p className="text-[11px] text-amber-300/80 mt-2">
                          → Use the "Resend Invoice" action below (or verify the parent's email is not a test domain like @example.com / @regression.test).
                        </p>
                      </div>
                    </div>
                  </Card>
                )}
                <Card className="p-3 bg-slate-900/50 border-slate-800">
                  <div className="space-y-2 text-sm">
                    {detail.timeline.map((e, i) => (
                      <div key={i} className="flex gap-3" data-testid={`timeline-event-${i}`}>
                        <div className="flex-shrink-0 w-2 h-2 mt-1.5 rounded-full bg-emerald-400" />
                        <div className="flex-1">
                          <p className="text-white capitalize">{e.action.replace(/_/g, " ")}</p>
                          <p className="text-xs text-slate-500">{new Date(e.at).toLocaleString()}{e.by ? ` · ${e.by}` : ""}</p>
                          {e.note && <p className="text-xs text-slate-400 mt-0.5 italic">{e.note}</p>}
                          {e.action === "invoice_created" && (
                            <div className="mt-1">
                              <InvoiceLinkButton txnId={txnId} eventIdx={i} />
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              </section>
            )}

            {/* Section C — Actions */}
            {detail.status === "claimed" && (
              <section>
                <h3 className="text-xs uppercase text-slate-400 mb-2">Actions</h3>
                <div className="space-y-3">
                  {isMatch && (
                    <Button onClick={approve} disabled={actionLoading} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="drawer-approve-btn">
                      {actionLoading === "approve" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                      Approve — amounts match
                    </Button>
                  )}

                  {isShortpay && (
                    <Card className="p-3 bg-amber-500/5 border-amber-500/30 space-y-2">
                      <p className="text-sm text-amber-200">
                        Parent paid <strong>${amountReceived.toFixed(2)}</strong>, invoice is <strong>${amountInvoiced.toFixed(2)}</strong>, shortfall is <strong>${Math.abs(diff).toFixed(2)}</strong>
                      </p>
                      <Textarea
                        value={adminNote}
                        onChange={(e) => setAdminNote(e.target.value)}
                        placeholder="Optional note to include in the email (e.g. 'Please top up via PayNow using the same reference')"
                        rows={2}
                        className="bg-slate-800 border-slate-700 text-white text-sm"
                        data-testid="shortfall-note"
                      />
                      <div className="flex gap-2">
                        <Button onClick={requestDifference} disabled={actionLoading} className="flex-1 bg-amber-600 hover:bg-amber-700" data-testid="drawer-request-diff-btn">
                          {actionLoading === "shortfall" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                          Request Difference
                        </Button>
                        <Button onClick={() => {if (window.confirm("Approve anyway and waive the shortfall?")) approve();}} variant="outline" className="border-slate-600" data-testid="drawer-approve-anyway-btn">
                          Approve Anyway
                        </Button>
                      </div>
                    </Card>
                  )}

                  {isOverpay && (
                    <Card className="p-3 bg-teal-500/5 border-teal-500/30 space-y-2">
                      <p className="text-sm text-teal-200">Overpayment of <strong>${diff.toFixed(2)}</strong> — can be credited to parent's balance for future use.</p>
                      <Textarea
                        value={adminNote}
                        onChange={(e) => setAdminNote(e.target.value)}
                        placeholder="Optional note"
                        rows={2}
                        className="bg-slate-800 border-slate-700 text-white text-sm"
                      />
                      <Button onClick={approveWithCredit} disabled={actionLoading} className="w-full bg-teal-600 hover:bg-teal-700" data-testid="drawer-approve-credit-btn">
                        {actionLoading === "credit" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                        Approve + Credit ${diff.toFixed(2)} Excess
                      </Button>
                    </Card>
                  )}

                  {/* Dispute form — always available on claimed */}
                  <Card className="p-3 bg-red-500/5 border-red-500/30 space-y-2">
                    <p className="text-sm text-red-200">Raise dispute (emails parent)</p>
                    <Textarea
                      value={disputeReason}
                      onChange={(e) => setDisputeReason(e.target.value)}
                      placeholder="Reason — e.g. 'Payment could not be matched against bank statement for the claimed amount'"
                      rows={3}
                      className="bg-slate-800 border-slate-700 text-white text-sm"
                      data-testid="dispute-reason"
                    />
                    <Button onClick={dispute} disabled={actionLoading || !disputeReason.trim()} variant="outline" className="w-full border-red-500/40 text-red-300 hover:bg-red-500/10" data-testid="drawer-dispute-btn">
                      {actionLoading === "dispute" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <AlertTriangle className="w-4 h-4 mr-2" />}
                      Raise Dispute
                    </Button>
                  </Card>

                  {/* Secondary comms — always available while claimed */}
                  <div className="flex gap-2">
                    <Button onClick={sendInvoice} disabled={actionLoading} variant="outline" size="sm" className="flex-1 border-slate-600" data-testid="drawer-send-invoice-btn">
                      {actionLoading === "sendinv" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                      Re-send Invoice
                    </Button>
                    <Button onClick={sendReminder} disabled={actionLoading} variant="outline" size="sm" className="flex-1 border-slate-600" data-testid="drawer-send-reminder-btn">
                      {actionLoading === "reminder" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                      Send Reminder
                    </Button>
                  </div>
                </div>
              </section>
            )}

            {detail.status === "disputed" && (
              <section>
                <Card className="p-3 bg-red-500/10 border-red-500/30">
                  <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                    <p className="text-sm text-red-300 font-semibold">Disputed</p>
                    {detail.disputed_at && (
                      <div className="flex items-center gap-2 text-xs">
                        <span className="text-red-200/70">Open for</span>
                        <span className="font-mono text-red-200">{ageToHumanReadable(detail.age_seconds)}</span>
                        {slaBadge(detail.sla)}
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-red-200">{detail.raw_txn?.dispute_reason || detail.dispute_reason || "(no reason captured)"}</p>
                  <div className="flex gap-2 mt-3 flex-wrap">
                    <Button onClick={approve} disabled={actionLoading} size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid="drawer-resolve-btn">
                      {actionLoading === "approve" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                      Resolve → Approve
                    </Button>
                    <Button onClick={sendReminder} disabled={actionLoading} variant="outline" size="sm" className="border-slate-600" data-testid="drawer-disputed-reminder-btn">
                      {actionLoading === "reminder" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                      Send Reminder
                    </Button>
                  </div>
                </Card>

                {/* Mark Paid Offline — for disputed payments where the parent
                    later paid offline (cash/bank transfer) and provided proof */}
                <Card className="p-3 mt-3 bg-emerald-500/5 border-emerald-500/30 space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-emerald-200 font-semibold">Parent paid offline?</p>
                      <p className="text-xs text-slate-400 mt-0.5">Use this if the dispute was resolved by an offline payment with proof.</p>
                    </div>
                    {!showOfflineForm && (
                      <Button
                        onClick={() => {
                          setShowOfflineForm(true);
                          setOfflineAmount(String(detail.amount_invoiced || ""));
                        }}
                        size="sm"
                        variant="outline"
                        className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                        data-testid="drawer-disputed-mark-paid-offline-btn"
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Mark Paid Offline
                      </Button>
                    )}
                  </div>
                  {showOfflineForm && <OfflinePaymentForm
                    method={offlineMethod} setMethod={setOfflineMethod}
                    amount={offlineAmount} setAmount={setOfflineAmount}
                    bankRef={offlineBankRef} setBankRef={setOfflineBankRef}
                    payee={offlinePayee} setPayee={setOfflinePayee}
                    note={offlineNote} setNote={setOfflineNote}
                    invoiced={detail.amount_invoiced || 0}
                    actionLoading={actionLoading}
                    onConfirm={markPaidOffline}
                    onCancel={() => setShowOfflineForm(false)}
                    testIdPrefix="disputed"
                  />}
                </Card>

                {/* Partial payment received — close the dispute by accepting
                    the partial amount and booking a back-bill for the shortfall.
                    Visible when the parent paid SOMETHING but less than invoiced. */}
                {Number(detail.amount_received || 0) > 0 && Number(detail.amount_received || 0) < Number(detail.amount_invoiced || 0) && (
                  <Card className="p-3 mt-3 bg-amber-500/5 border-amber-500/30 space-y-2" data-testid="drawer-partial-resolve-card">
                    <div>
                      <p className="text-sm text-amber-200 font-semibold">Parent paid partial amount</p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        Received <strong className="text-amber-200">${Number(detail.amount_received).toFixed(2)}</strong> of invoiced <strong>${Number(detail.amount_invoiced).toFixed(2)}</strong> — shortfall <strong className="text-rose-300">${(Number(detail.amount_invoiced) - Number(detail.amount_received)).toFixed(2)}</strong>.
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2 pt-1">
                      <Button
                        onClick={async () => {
                          const shortfall = Number(detail.amount_invoiced) - Number(detail.amount_received);
                          if (!window.confirm(`Approve $${Number(detail.amount_received).toFixed(2)} as paid and create a back-bill for $${shortfall.toFixed(2)}? The back-bill will appear in the queue as a manual invoice.`)) return;
                          setActionLoading("approve-partial");
                          try {
                            const res = await axios.post(
                              `${API}/payments-hub/txn/${txnId}/approve-partial`,
                              { amount_received: Number(detail.amount_received), note: adminNote },
                              { withCredentials: true },
                            );
                            toast.success(`Approved $${Number(detail.amount_received).toFixed(2)}. Back-bill ${res.data.back_bill_reference} created for $${shortfall.toFixed(2)}.`);
                            onActionComplete();
                          } catch (err) {
                            const __e = formatAdminError(err, "Approve partial failed");
                            toast.error(__e.message, __e.opts);
                          } finally {
                            setActionLoading(null);
                          }
                        }}
                        disabled={!!actionLoading}
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700"
                        data-testid="drawer-approve-partial-btn"
                      >
                        {actionLoading === "approve-partial" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />}
                        Approve ${Number(detail.amount_received).toFixed(2)} + back-bill ${(Number(detail.amount_invoiced) - Number(detail.amount_received)).toFixed(2)}
                      </Button>
                      <Button
                        onClick={async () => {
                          const shortfall = Number(detail.amount_invoiced) - Number(detail.amount_received);
                          setActionLoading("shortfall");
                          try {
                            await axios.post(
                              `${API}/payments-hub/txn/${txnId}/request-difference`,
                              { shortfall_amount: shortfall, amount_received: Number(detail.amount_received), note: adminNote },
                              { withCredentials: true },
                            );
                            toast.success(`Shortfall notice sent for $${shortfall.toFixed(2)}`);
                            onActionComplete();
                          } catch (err) {
                            const __e = formatAdminError(err, "Request balance failed");
                            toast.error(__e.message, __e.opts);
                          } finally {
                            setActionLoading(null);
                          }
                        }}
                        disabled={!!actionLoading}
                        size="sm"
                        variant="outline"
                        className="border-amber-500/40 text-amber-200 hover:bg-amber-500/10"
                        data-testid="drawer-request-balance-btn"
                      >
                        {actionLoading === "shortfall" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />}
                        Request remaining balance
                      </Button>
                    </div>
                  </Card>
                )}
              </section>
            )}

            {detail.status === "unpaid" && (
              <section>
                <h3 className="text-xs uppercase text-slate-400 mb-2">Actions</h3>
                <Card className="p-3 bg-slate-900/50 border-slate-800 space-y-3">
                  <p className="text-sm text-slate-300">Parent hasn't paid yet. Choose an action:</p>
                  <div className="flex gap-2">
                    <Button onClick={sendInvoice} disabled={actionLoading} className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="drawer-unpaid-send-invoice-btn">
                      {actionLoading === "sendinv" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                      Send Invoice
                    </Button>
                    <Button onClick={sendReminder} disabled={actionLoading} variant="outline" className="flex-1 border-slate-600" data-testid="drawer-unpaid-reminder-btn">
                      {actionLoading === "reminder" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                      Send Reminder
                    </Button>
                  </div>
                </Card>

                {/* Mark Paid Offline — for parents who paid outside the app and sent proof */}
                <Card className="p-3 mt-3 bg-emerald-500/5 border-emerald-500/30 space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-emerald-200 font-semibold">Parent paid offline?</p>
                      <p className="text-xs text-slate-400 mt-0.5">Use this if they paid by bank transfer/cash and sent you proof.</p>
                    </div>
                    {!showOfflineForm && (
                      <Button
                        onClick={() => {
                          setShowOfflineForm(true);
                          setOfflineAmount(String(detail.amount_invoiced || ""));
                        }}
                        size="sm"
                        variant="outline"
                        className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                        data-testid="drawer-unpaid-mark-paid-offline-btn"
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Mark Paid Offline
                      </Button>
                    )}
                  </div>

                  {showOfflineForm && <OfflinePaymentForm
                    method={offlineMethod} setMethod={setOfflineMethod}
                    amount={offlineAmount} setAmount={setOfflineAmount}
                    bankRef={offlineBankRef} setBankRef={setOfflineBankRef}
                    payee={offlinePayee} setPayee={setOfflinePayee}
                    note={offlineNote} setNote={setOfflineNote}
                    invoiced={detail.amount_invoiced || 0}
                    actionLoading={actionLoading}
                    onConfirm={markPaidOffline}
                    onCancel={() => setShowOfflineForm(false)}
                    testIdPrefix="unpaid"
                  />}
                </Card>

                {/* Cancel Registration — for unpaid duplicate/erroneous registrations */}
                <Card className="p-3 mt-3 bg-rose-500/5 border-rose-500/30 space-y-2" data-testid="drawer-cancel-card">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-rose-200 font-semibold">Cancel this registration?</p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        Use for unpaid duplicates / wrong-term registrations. Sets txn + registration status to <code>cancelled</code>, logs reason to audit trail. Does NOT touch child player records or enrollments.
                      </p>
                    </div>
                    {!showCancelForm && (
                      <Button
                        onClick={() => setShowCancelForm(true)}
                        size="sm"
                        variant="outline"
                        className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
                        data-testid="drawer-open-cancel-btn"
                      >
                        <X className="w-4 h-4 mr-1" />
                        Cancel registration
                      </Button>
                    )}
                  </div>

                  {showCancelForm && (
                    <div className="space-y-2 pt-2 border-t border-rose-500/20">
                      <label className="block text-[10px] uppercase tracking-wide text-slate-500">
                        Reason (required, min 5 chars — logged to audit trail)
                      </label>
                      <Textarea
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        placeholder="e.g. 'Duplicate of SCC-T2-20260513-AB12 — parent confirmed by email 14 Feb'"
                        rows={2}
                        className="bg-slate-950 border-rose-500/30 text-slate-200"
                        data-testid="drawer-cancel-reason-input"
                      />
                      <div className="flex gap-2">
                        <Button
                          onClick={cancelRegistration}
                          disabled={actionLoading === "cancelreg" || cancelReason.trim().length < 5}
                          size="sm"
                          className="bg-rose-600 hover:bg-rose-500 text-white"
                          data-testid="drawer-confirm-cancel-btn"
                        >
                          {actionLoading === "cancelreg"
                            ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                            : <X className="w-3.5 h-3.5 mr-1" />}
                          Confirm cancel
                        </Button>
                        <Button
                          onClick={() => { setShowCancelForm(false); setCancelReason(""); }}
                          disabled={actionLoading === "cancelreg"}
                          size="sm"
                          variant="ghost"
                          data-testid="drawer-cancel-abort-btn"
                        >
                          Keep
                        </Button>
                      </div>
                    </div>
                  )}
                </Card>
              </section>
            )}

            {detail.status === "partial" && (
              <section>
                <h3 className="text-xs uppercase text-slate-400 mb-2">Actions</h3>
                <Card className="p-3 bg-orange-500/5 border-orange-500/30 space-y-2">
                  <p className="text-sm text-orange-200">Partial payment recorded. Awaiting top-up.</p>
                  <div className="flex gap-2">
                    <Button onClick={sendReminder} disabled={actionLoading} variant="outline" className="flex-1 border-amber-500/40 text-amber-200" data-testid="drawer-partial-reminder-btn">
                      {actionLoading === "reminder" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                      Send Reminder
                    </Button>
                    <Button onClick={approve} disabled={actionLoading} size="sm" className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="drawer-partial-approve-btn">
                      {actionLoading === "approve" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                      Approve Now
                    </Button>
                  </div>
                  {/* Undo — the partial was applied in error. Resets invoice to
                      unpaid/pending, clears received/shortfall, deletes any back-bill. */}
                  <div className="pt-2 border-t border-orange-500/20">
                    <Button
                      onClick={async () => {
                        if (!window.confirm("Undo this partial payment? The invoice will be reset to UNPAID (pending), the received/shortfall amounts cleared" + (detail.back_bill_reference ? `, and back-bill ${detail.back_bill_reference} deleted` : "") + ".")) return;
                        setActionLoading("undo-partial");
                        try {
                          const res = await axios.post(
                            `${API}/payments-hub/txn/${txnId}/undo-partial`,
                            { note: adminNote },
                            { withCredentials: true },
                          );
                          toast.success(res.data.message || "Partial payment reversed");
                          onActionComplete();
                        } catch (err) {
                          const __e = formatAdminError(err, "Undo partial failed");
                          toast.error(__e.message, __e.opts);
                        } finally {
                          setActionLoading(null);
                        }
                      }}
                      disabled={!!actionLoading}
                      size="sm"
                      variant="outline"
                      className="w-full border-rose-500/40 text-rose-200 hover:bg-rose-500/10"
                      data-testid="drawer-undo-partial-btn"
                    >
                      {actionLoading === "undo-partial" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <XCircle className="w-4 h-4 mr-1" />}
                      Undo partial payment
                    </Button>
                  </div>
                </Card>
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function OfflinePaymentForm({
  method, setMethod,
  amount, setAmount,
  bankRef, setBankRef,
  payee, setPayee,
  note, setNote,
  invoiced,
  actionLoading,
  onConfirm,
  onCancel,
  testIdPrefix = "unpaid",
}) {
  // Preserve original testids for the unpaid variant (no suffix) so
  // iteration_53 tests still pass; disputed variant gets the suffix.
  const sfx = testIdPrefix === "unpaid" ? "" : `-${testIdPrefix}`;
  return (
    <div className="space-y-2 pt-2 border-t border-emerald-500/20">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs text-slate-400 block mb-1">Payment method</label>
          <select
            value={method}
            onChange={(e) => setMethod(e.target.value)}
            className="w-full bg-slate-800 border border-slate-700 text-white text-sm rounded px-2 py-1.5"
            data-testid={`offline-method-select${sfx}`}
          >
            <option value="bank_transfer">Bank Transfer (PayNow)</option>
            <option value="cash">Cash</option>
            <option value="paynow_offline">PayNow (offline screenshot)</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-slate-400 block mb-1">Amount received</label>
          <Input
            type="number"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder={String(invoiced || "0.00")}
            className="bg-slate-800 border-slate-700 text-white text-sm h-8"
            data-testid={`offline-amount-input${sfx}`}
          />
        </div>
      </div>
      <div>
        <label className="text-xs text-slate-400 block mb-1">Bank reference / screenshot ref (optional)</label>
        <Input
          value={bankRef}
          onChange={(e) => setBankRef(e.target.value)}
          placeholder="e.g. PNT240424123456"
          className="bg-slate-800 border-slate-700 text-white text-sm h-8"
          data-testid={`offline-bank-ref-input${sfx}`}
        />
      </div>
      <div>
        <label className="text-xs text-slate-400 block mb-1">Payee/sender name (optional)</label>
        <Input
          value={payee}
          onChange={(e) => setPayee(e.target.value)}
          placeholder="Parent's bank account holder name"
          className="bg-slate-800 border-slate-700 text-white text-sm h-8"
          data-testid={`offline-payee-input${sfx}`}
        />
      </div>
      <div>
        <label className="text-xs text-slate-400 block mb-1">Admin note (optional)</label>
        <Textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. Parent sent screenshot via WhatsApp 24-Apr"
          rows={2}
          className="bg-slate-800 border-slate-700 text-white text-sm"
          data-testid={`offline-note-input${sfx}`}
        />
      </div>
      <div className="flex gap-2 pt-1">
        <Button
          onClick={onConfirm}
          disabled={actionLoading}
          className="flex-1 bg-emerald-600 hover:bg-emerald-700"
          data-testid={`drawer-${testIdPrefix}-confirm-paid-offline-btn`}
        >
          {actionLoading === "offline" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
          Confirm — Mark Paid
        </Button>
        <Button
          onClick={onCancel}
          variant="outline"
          className="border-slate-600"
          disabled={actionLoading}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────────────
// Sarah Gah cascade-cleanup widget — embedded in TriageModal
// ────────────────────────────────────────────────────────────────────────────
function SarahCascadeCleanup() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const downloadJson = (data, suffix) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `sarah-cascade-${suffix}-${new Date().toISOString().slice(0, 10)}.json`;
    a.click(); URL.revokeObjectURL(url);
  };

  const runPreview = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/payments-hub/admin/diagnostic/sarah-gah-cascade-cleanup?dry_run=true`,
        {},
        { withCredentials: true },
      );
      setPreview(res.data);
      toast.success("Dry-run complete — review the plan below.");
    } catch (err) {
      { const __e = formatAdminError(err, "Preview failed"); toast.error(__e.message, __e.opts); };
    } finally { setLoading(false); }
  };

  const runExecute = async () => {
    if (!preview) {
      toast.error("Run dry-run preview first");
      return;
    }
    if (!window.confirm(
      "PERMANENTLY DELETE the following:\n\n" +
      `  • ${preview.totals?.payment_transactions || 0} payment transactions\n` +
      `  • ${preview.totals?.academy_players_this_parent || 0} academy_players (this parent)\n` +
      `  • ${preview.totals?.academy_enrollments || 0} enrollments\n` +
      `  • ${preview.totals?.academy_parent_registrations || 0} parent registrations\n` +
      `  • ${preview.totals?.payment_documents || 0} payment documents\n` +
      `  • ${preview.totals?.academy_credits || 0} academy credits\n` +
      `  • Olivia Pereira player: ${preview.totals?.olivia_pereira_will_delete ? "YES" : "NO (preserved)"}\n` +
      `  • users row: ${preview.totals?.users_row || 0}\n\n` +
      "This is logged to admin_audit_log but NOT REVERSIBLE. Confirm?"
    )) return;

    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/payments-hub/admin/diagnostic/sarah-gah-cascade-cleanup?dry_run=false`,
        { confirm_token: "DELETE_SARAH_GAH_TEST_DATA" },
        { withCredentials: true },
      );
      setPreview({ ...res.data, _executed: true });
      toast.success("Sarah Gah test data deleted.");
    } catch (err) {
      { const __e = formatAdminError(err, "Cleanup failed"); toast.error(__e.message, __e.opts); };
    } finally { setLoading(false); }
  };

  return (
    <div className="mt-3 p-3 border border-rose-500/30 bg-rose-500/5 rounded" data-testid="sarah-cascade-section">
      <p className="text-rose-300 font-semibold text-xs mb-1">DESTRUCTIVE — Sarah Gah cascade-delete</p>
      <p className="text-[11px] text-slate-400 mb-2">
        Wipes <span className="font-mono">parent_id=2cd55a73…</span> across payment_transactions,
        academy_players, enrollments, parent_registrations, payment_documents, academy_credits, and
        the Olivia Pereira player record (if no other parent owns her). Hardcoded scope — cannot be misused.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm" variant="outline" disabled={loading}
          className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
          onClick={runPreview}
          data-testid="sarah-cascade-preview-btn"
        >
          {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin inline" /> : null}
          Preview cascade-delete (dry-run)
        </Button>
        {preview && !preview._executed && (
          <Button
            size="sm" disabled={loading}
            className="bg-rose-600 hover:bg-rose-500"
            onClick={runExecute}
            data-testid="sarah-cascade-execute-btn"
          >
            Execute cascade-delete
          </Button>
        )}
        {preview && (
          <Button
            size="sm" variant="outline" disabled={loading}
            onClick={() => downloadJson(preview, preview._executed ? "executed" : "dryrun")}
            data-testid="sarah-cascade-download-btn"
          >
            <Download className="w-3 h-3 mr-1" /> JSON
          </Button>
        )}
      </div>
      {preview && (
        <div className="mt-3 text-[11px] space-y-2" data-testid="sarah-cascade-preview-result">
          <div className={`px-2 py-1 rounded font-medium ${preview._executed ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/30" : "bg-amber-500/10 text-amber-300 border border-amber-500/30"}`}>
            {preview._executed ? "✓ Executed" : "⚠ DRY-RUN — no data was written"}
          </div>
          <div className="grid grid-cols-2 gap-1 font-mono text-slate-300">
            <span>payment_transactions: <strong className="text-rose-300">{preview.totals?.payment_transactions ?? 0}</strong></span>
            <span>academy_players (this parent): <strong className="text-rose-300">{preview.totals?.academy_players_this_parent ?? 0}</strong></span>
            <span>academy_enrollments: <strong className="text-rose-300">{preview.totals?.academy_enrollments ?? 0}</strong></span>
            <span>academy_parent_registrations: <strong className="text-rose-300">{preview.totals?.academy_parent_registrations ?? 0}</strong></span>
            <span>payment_documents: <strong className="text-rose-300">{preview.totals?.payment_documents ?? 0}</strong></span>
            <span>academy_credits: <strong className="text-rose-300">{preview.totals?.academy_credits ?? 0}</strong></span>
            <span>users row: <strong className="text-rose-300">{preview.totals?.users_row ?? 0}</strong></span>
            <span>Olivia Pereira: <strong className={preview.totals?.olivia_pereira_will_delete ? "text-rose-300" : "text-emerald-300"}>
              {preview.totals?.olivia_pereira_will_delete ? "DELETE" : "preserved"}
            </strong></span>
          </div>
          {(preview.warnings || []).length > 0 && (
            <div className="text-amber-300">⚠ {preview.warnings.join(" · ")}</div>
          )}
          <details>
            <summary className="cursor-pointer text-violet-300">Full plan JSON</summary>
            <pre className="text-[10px] mt-1 bg-slate-900 p-2 rounded overflow-auto max-h-64">{JSON.stringify(preview, null, 2)}</pre>
          </details>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Abandoned Stripe DM sessions cleanup — embedded in TriageModal
// ────────────────────────────────────────────────────────────────────────────
function AbandonedStripeCleanup() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const runPreview = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/payments-hub/admin/diagnostic/cleanup-abandoned-stripe-sessions?dry_run=true`,
        {},
        { withCredentials: true },
      );
      setPreview(res.data);
      toast.success("Dry-run complete.");
    } catch (err) {
      { const __e = formatAdminError(err, "Preview failed"); toast.error(__e.message, __e.opts); };
    } finally { setLoading(false); }
  };

  const runExecute = async () => {
    if (!preview) { toast.error("Run dry-run preview first"); return; }
    if (!window.confirm(
      "PERMANENTLY DELETE the 2 abandoned Stripe digital_membership sessions?\n\n" +
      "Both rows must still satisfy: status='initiated', no parent_id, type='digital_membership'.\n" +
      "Logged to admin_audit_log. Not reversible."
    )) return;

    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/payments-hub/admin/diagnostic/cleanup-abandoned-stripe-sessions?dry_run=false`,
        { confirm_token: "DELETE_ABANDONED_STRIPE_DM_SESSIONS" },
        { withCredentials: true },
      );
      setPreview({ ...res.data, _executed: true });
      toast.success(`Deleted ${res.data.totals?.deleted ?? 0} abandoned Stripe session(s).`);
    } catch (err) {
      { const __e = formatAdminError(err, "Cleanup failed"); toast.error(__e.message, __e.opts); };
    } finally { setLoading(false); }
  };

  return (
    <div className="mb-3 p-3 border border-rose-500/30 bg-rose-500/5 rounded" data-testid="abandoned-stripe-section">
      <p className="text-rose-300 font-semibold text-xs mb-1">DESTRUCTIVE — Delete the 2 abandoned Stripe DM sessions (Dec 2025)</p>
      <p className="text-[11px] text-slate-400 mb-2">
        Hardcoded txn_ids: <span className="font-mono">4d8b81b1…</span> · <span className="font-mono">e609c41f…</span>.
        Each row is re-validated as <span className="font-mono">status='initiated'</span>, no parent_id,
        <span className="font-mono"> type='digital_membership'</span> before deletion.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm" variant="outline" disabled={loading}
          className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
          onClick={runPreview}
          data-testid="abandoned-stripe-preview-btn"
        >
          {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin inline" /> : null}
          Preview (dry-run)
        </Button>
        {preview && !preview._executed && (
          <Button
            size="sm" disabled={loading}
            className="bg-rose-600 hover:bg-rose-500"
            onClick={runExecute}
            data-testid="abandoned-stripe-execute-btn"
          >
            Execute delete
          </Button>
        )}
      </div>
      {preview && (
        <div className="mt-3 text-[11px]" data-testid="abandoned-stripe-result">
          <div className={`px-2 py-1 rounded font-medium mb-2 ${preview._executed ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/30" : "bg-amber-500/10 text-amber-300 border border-amber-500/30"}`}>
            {preview._executed
              ? `✓ Deleted ${preview.totals?.deleted ?? 0} · Blocked ${preview.totals?.blocked ?? 0}`
              : `⚠ DRY-RUN — would delete ${preview.totals?.would_delete_in_dry_run ?? 0} · blocked ${preview.totals?.blocked ?? 0}`}
          </div>
          <details>
            <summary className="cursor-pointer text-violet-300">Per-row outcomes</summary>
            <pre className="text-[10px] mt-1 bg-slate-900 p-2 rounded overflow-auto max-h-64">{JSON.stringify(preview.rows, null, 2)}</pre>
          </details>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// SCC member-flag sync widget — embedded in TriageModal SCC desync card
// ────────────────────────────────────────────────────────────────────────────
function SccFlagSync() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const downloadJson = (data, suffix) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `scc-flag-sync-${suffix}-${new Date().toISOString().slice(0, 10)}.json`;
    a.click(); URL.revokeObjectURL(url);
  };

  const runPreview = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/payments-hub/admin/diagnostic/sync-scc-member-flags?dry_run=true`,
        {},
        { withCredentials: true },
      );
      setPreview(res.data);
      toast.success("Dry-run complete — review the plan below.");
    } catch (err) {
      { const __e = formatAdminError(err, "Preview failed"); toast.error(__e.message, __e.opts); };
    } finally { setLoading(false); }
  };

  const runExecute = async () => {
    if (!preview) { toast.error("Run dry-run preview first"); return; }
    if (preview.skipped) { toast.error("Already executed previously."); return; }
    if (!window.confirm(
      "Apply SCC flag sync to:\n\n" +
      `  • ${preview.totals?.high_synced || 0} HIGH-severity users → is_scc_member=true + verified=true\n` +
      `  • ${preview.totals?.medium_synced || 0} MEDIUM-severity users → is_scc_member=true only (eyeball flagged)\n` +
      `  • Skip already-synced: ${preview.totals?.skipped_already_synced || 0}\n` +
      `  • Skip user-not-found: ${preview.totals?.skipped_user_not_found || 0}\n` +
      `  • Excluded list violations caught (must be 0): ${preview.totals?.exclusion_list_violations_caught || 0}\n\n` +
      "Audit-logged + idempotency marker set. Confirm?"
    )) return;
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/payments-hub/admin/diagnostic/sync-scc-member-flags?dry_run=false`,
        { confirm_token: "SYNC_SCC_FLAGS_APR2026" },
        { withCredentials: true },
      );
      setPreview({ ...res.data, _executed: true });
      toast.success(`Synced ${res.data.totals?.high_synced ?? 0} high + ${res.data.totals?.medium_synced ?? 0} medium.`);
    } catch (err) {
      { const __e = formatAdminError(err, "Sync failed"); toast.error(__e.message, __e.opts); };
    } finally { setLoading(false); }
  };

  return (
    <div className="mb-3 p-3 border border-cyan-500/30 bg-cyan-500/5 rounded" data-testid="scc-flag-sync-section">
      <p className="text-cyan-300 font-semibold text-xs mb-1">SCC flag sync — Apr 2026 allowlist</p>
      <p className="text-[11px] text-slate-400 mb-2">
        Hardcoded scope: <strong className="text-cyan-200">281 candidates</strong> (254 high → fully verified;
        27 medium → flag only, eyeball-pending).
        <strong className="text-rose-200"> 117 excluded</strong> (academy team-codes, SCC-prefixed, unrecognised) cannot be touched.
        Skips users whose <span className="font-mono">is_scc_member</span> is already true. Idempotent.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm" variant="outline" disabled={loading}
          className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
          onClick={runPreview}
          data-testid="scc-flag-sync-preview-btn"
        >
          {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin inline" /> : null}
          Preview sync (dry-run)
        </Button>
        {preview && !preview._executed && !preview.skipped && (
          <Button
            size="sm" disabled={loading}
            className="bg-cyan-600 hover:bg-cyan-500"
            onClick={runExecute}
            data-testid="scc-flag-sync-execute-btn"
          >
            Execute sync
          </Button>
        )}
        {preview && (
          <Button
            size="sm" variant="outline" disabled={loading}
            onClick={() => downloadJson(preview, preview._executed ? "executed" : "dryrun")}
            data-testid="scc-flag-sync-download-btn"
          >
            <Download className="w-3 h-3 mr-1" /> JSON
          </Button>
        )}
      </div>
      {preview && (
        <div className="mt-3 text-[11px]" data-testid="scc-flag-sync-result">
          {preview.skipped ? (
            <div className="px-2 py-1 rounded font-medium bg-slate-700/30 text-slate-300 border border-slate-600">
              ⓘ Already executed — {JSON.stringify(preview.previous_run?.totals || {})}
            </div>
          ) : (
            <div className={`px-2 py-1 rounded font-medium mb-2 ${preview._executed ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/30" : "bg-amber-500/10 text-amber-300 border border-amber-500/30"}`}>
              {preview._executed
                ? `✓ Executed — ${preview.totals?.high_synced ?? 0} high + ${preview.totals?.medium_synced ?? 0} medium written`
                : `⚠ DRY-RUN — would write ${preview.totals?.high_synced ?? 0} high + ${preview.totals?.medium_synced ?? 0} medium`}
            </div>
          )}
          {preview.scope && (
            <div className="grid grid-cols-3 gap-1 font-mono text-slate-300 mb-2">
              <span>excluded (locked): <strong className="text-rose-300">{preview.scope.excluded_count}</strong></span>
              <span>high candidates: <strong className="text-cyan-300">{preview.scope.high_severity_count}</strong></span>
              <span>medium candidates: <strong className="text-cyan-300">{preview.scope.medium_severity_count}</strong></span>
            </div>
          )}
          {preview.totals && (
            <div className="grid grid-cols-2 gap-1 font-mono text-slate-300">
              <span>high_synced: <strong className="text-emerald-300">{preview.totals.high_synced}</strong></span>
              <span>medium_synced: <strong className="text-emerald-300">{preview.totals.medium_synced}</strong></span>
              <span>skipped_already_synced: <strong className="text-slate-400">{preview.totals.skipped_already_synced}</strong></span>
              <span>skipped_user_not_found: <strong className="text-slate-400">{preview.totals.skipped_user_not_found}</strong></span>
              <span className="col-span-2">
                exclusion-list violations caught: <strong className={preview.totals.exclusion_list_violations_caught === 0 ? "text-emerald-300" : "text-red-400"}>
                  {preview.totals.exclusion_list_violations_caught}
                </strong> (must be 0)
              </span>
            </div>
          )}
          {(preview.high_severity?.length || preview.medium_severity?.length) ? (
            <details className="mt-2">
              <summary className="cursor-pointer text-violet-300">Per-user diff ({(preview.high_severity?.length || 0) + (preview.medium_severity?.length || 0)})</summary>
              <pre className="text-[10px] mt-1 bg-slate-900 p-2 rounded overflow-auto max-h-64">
{JSON.stringify({ high_severity: preview.high_severity?.slice(0, 10), medium_severity: preview.medium_severity?.slice(0, 10) }, null, 2)}
              </pre>
              <p className="text-slate-500 text-[10px] mt-1">First 10 of each severity shown above. Full data in JSON download.</p>
            </details>
          ) : null}
        </div>
      )}
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────────────
// Discount Leak Audit Modal — read-only diagnostic for non-member parents
// who got the member rate (Direction B of the SCC desync bug).
// ────────────────────────────────────────────────────────────────────────────
function DiscountLeakModal({ loading, data, onClose }) {
  const downloadJson = () => {
    if (!data) return;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `non-member-discount-leak-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-amber-500/30 rounded-lg shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-auto" data-testid="discount-leak-modal">
        <div className="sticky top-0 bg-slate-900 border-b border-slate-700 p-4 flex items-center justify-between z-10">
          <h2 className="text-lg font-semibold text-amber-300 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" /> Non-member discount leak audit
          </h2>
          <div className="flex gap-2">
            {data && !data.error && (
              <Button size="sm" variant="outline" onClick={downloadJson} data-testid="discount-leak-download-btn">
                <Download className="w-3 h-3 mr-1" /> JSON
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={onClose}>Close</Button>
          </div>
        </div>
        <div className="p-4 space-y-3 text-sm">
          {loading && (
            <div className="text-center py-8">
              <Loader2 className="w-6 h-6 animate-spin mx-auto text-amber-400" />
              <p className="text-slate-400 mt-2">Auditing all academy_registration transactions…</p>
            </div>
          )}
          {!loading && data?.error && (
            <div className="bg-red-500/10 border border-red-500/30 p-3 rounded text-red-300">{data.error}</div>
          )}
          {!loading && data && !data.error && (
            <>
              <div className="grid grid-cols-4 gap-3 mb-2">
                <div className="bg-slate-800/40 p-3 rounded">
                  <p className="text-[10px] text-slate-500 uppercase">Scanned txns</p>
                  <p className="text-2xl font-mono text-white">{data.scanned}</p>
                </div>
                <div className="bg-amber-500/10 border border-amber-500/30 p-3 rounded">
                  <p className="text-[10px] text-amber-400 uppercase">Leaked count</p>
                  <p className="text-2xl font-mono text-amber-300">{data.leaked_count}</p>
                </div>
                <div className="bg-rose-500/10 border border-rose-500/30 p-3 rounded">
                  <p className="text-[10px] text-rose-400 uppercase">Estimated shortfall</p>
                  <p className="text-2xl font-mono text-rose-300">${(data.estimated_total_shortfall || 0).toFixed(2)}</p>
                </div>
                <div className="bg-slate-800/40 p-3 rounded">
                  <p className="text-[10px] text-slate-500 uppercase">By tier</p>
                  <p className="text-[11px] font-mono text-slate-300">
                    {Object.entries(data.by_tier || {}).filter(([, n]) => n > 0).map(([t, n]) => `${t}:${n}`).join(" · ") || "—"}
                  </p>
                </div>
              </div>
              {data.leaked_count === 0 && (
                <p className="text-emerald-400 py-6 text-center">✨ No leak detected — all member discounts validated against verified flags.</p>
              )}
              {data.leaked_count > 0 && (
                <div className="overflow-auto max-h-[60vh]">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-800 sticky top-0">
                      <tr className="text-slate-300">
                        <th className="p-2 text-left">Reference</th>
                        <th className="p-2 text-left">Parent</th>
                        <th className="p-2 text-left">Paid</th>
                        <th className="p-2 text-left">Tier charged</th>
                        <th className="p-2 text-left">Correct full price</th>
                        <th className="p-2 text-left">Shortfall</th>
                        <th className="p-2 text-left">User flags</th>
                        <th className="p-2 text-left">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.leaked_rows.map((r) => (
                        <tr key={r.txn_id} className="border-t border-slate-800 hover:bg-slate-800/40">
                          <td className="p-2 font-mono text-white">{r.payment_reference}</td>
                          <td className="p-2">
                            <p className="text-slate-200">{r.parent_name || "—"}</p>
                            <p className="text-slate-500 text-[10px]">{r.parent_email}</p>
                          </td>
                          <td className="p-2 font-mono text-emerald-300">${(r.amount_paid || 0).toFixed(2)}</td>
                          <td className="p-2"><span className="text-amber-300 text-[11px]">{r.membership_tier_charged}</span></td>
                          <td className="p-2 font-mono text-fuchsia-300">${r.estimated_correct_full_price?.toFixed(2) || "?"}</td>
                          <td className="p-2 font-mono text-rose-300">${r.estimated_shortfall?.toFixed(2) || "?"}</td>
                          <td className="p-2 text-[10px]">
                            {r.user_record_found ? (
                              <div className="font-mono">
                                <p>scc:<strong className={r.user_flags.is_scc_member ? "text-emerald-300" : "text-rose-400"}>{String(r.user_flags.is_scc_member)}</strong> ver:<strong className={r.user_flags.scc_member_verified ? "text-emerald-300" : "text-rose-400"}>{String(r.user_flags.scc_member_verified)}</strong></p>
                                <p>mt:<strong className="text-cyan-300">{r.user_flags.member_type ?? "null"}</strong> dm:<strong className={r.user_flags.is_digital_member ? "text-emerald-300" : "text-slate-500"}>{String(r.user_flags.is_digital_member)}</strong></p>
                                <p className="text-slate-500">mn:{r.user_flags.membership_number || "—"}</p>
                              </div>
                            ) : (
                              <span className="text-rose-400 italic">no user record</span>
                            )}
                          </td>
                          <td className="p-2 text-[10px]">{r.payment_status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="text-[10px] text-slate-500 italic mt-3">
                Read-only audit — no records modified. Discount % values are estimated from the rate table snapshot
                (10% for club_member/digital_member, 11% for section_member, 20% for club_digital). True shortfall may
                differ if rate-table values changed historically.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────────────
// Term 1 paid-but-pending desync modal — diagnose first, fix later.
// ────────────────────────────────────────────────────────────────────────────
function Term1DesyncModal({ loading, data, onRefresh, onClose }) {
  const [fixDryRun, setFixDryRun] = useState(null);
  const [fixBusy, setFixBusy] = useState(false);

  const downloadJson = (obj, suffix) => {
    if (!obj) return;
    const blob = new Blob([JSON.stringify(obj, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `term1-desync-${suffix}-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const runFixDryRun = async () => {
    setFixBusy(true);
    try {
      const r = await axios.post(
        `${API}/payments-hub/admin/diagnostic/fix-term1-paid-pending-desync?dry_run=true`,
        {}, { withCredentials: true },
      );
      setFixDryRun(r.data);
      toast.success(`Dry-run complete — would flip ${r.data.totals?.flipped ?? 0} rows.`);
    } catch (e) {
      { const __e = formatAdminError(e, "Dry-run failed"); toast.error(__e.message, __e.opts); };
    } finally { setFixBusy(false); }
  };

  const runFixExecute = async () => {
    if (!fixDryRun) { toast.error("Run dry-run first"); return; }
    if (!window.confirm(
      `Flip payment_status: pending* → paid for ${fixDryRun.totals?.flipped ?? 0} Term 1 rows?\n\n` +
      "This is logged to admin_audit_log + scheduled_task_state (idempotent).\n" +
      "Confirm?"
    )) return;
    setFixBusy(true);
    try {
      const r = await axios.post(
        `${API}/payments-hub/admin/diagnostic/fix-term1-paid-pending-desync?dry_run=false`,
        { confirm_token: "FIX_TERM1_PAID_PENDING_APR2026" },
        { withCredentials: true },
      );
      setFixDryRun({ ...r.data, _executed: true });
      toast.success(`Flipped ${r.data.totals?.flipped ?? 0} rows.`);
      if (onRefresh) onRefresh();
    } catch (e) {
      { const __e = formatAdminError(e, "Fix failed"); toast.error(__e.message, __e.opts); };
    } finally { setFixBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-rose-500/40 rounded-lg shadow-2xl w-full max-w-7xl max-h-[92vh] overflow-auto" data-testid="term1-desync-modal">
        <div className="sticky top-0 bg-slate-900 border-b border-slate-700 p-4 flex items-center justify-between z-10">
          <h2 className="text-lg font-semibold text-rose-300 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" /> Term 1 paid-but-pending desync
          </h2>
          <div className="flex gap-2">
            {data && !data.error && (
              <Button size="sm" variant="outline" onClick={() => downloadJson(data, "diagnostic")} data-testid="t1-download-diag-btn">
                <Download className="w-3 h-3 mr-1" /> Diagnostic JSON
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={onClose}>Close</Button>
          </div>
        </div>
        <div className="p-4 space-y-3 text-sm">
          {loading && (
            <div className="text-center py-8">
              <Loader2 className="w-6 h-6 animate-spin mx-auto text-rose-400" />
              <p className="text-slate-400 mt-2">Scanning Term 1 transactions…</p>
            </div>
          )}
          {!loading && data?.error && (
            <div className="bg-red-500/10 border border-red-500/30 p-3 rounded text-red-300">{data.error}</div>
          )}
          {!loading && data && !data.error && (
            <>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-slate-800/40 p-3 rounded">
                  <p className="text-[10px] text-slate-500 uppercase">Pending Term 1 scanned</p>
                  <p className="text-2xl font-mono text-white">{data.total_pending_term1_scanned}</p>
                </div>
                <div className="bg-rose-500/10 border border-rose-500/30 p-3 rounded">
                  <p className="text-[10px] text-rose-400 uppercase">Desync count</p>
                  <p className="text-2xl font-mono text-rose-300">{data.desync_count}</p>
                </div>
                <div className="bg-slate-800/40 p-3 rounded">
                  <p className="text-[10px] text-slate-500 uppercase">By payment_status</p>
                  <p className="text-[11px] font-mono text-slate-300 break-all">
                    {Object.entries(data.by_payment_status || {}).map(([s, n]) => `${s}:${n}`).join(" · ") || "—"}
                  </p>
                </div>
              </div>

              <div className="bg-amber-500/5 border border-amber-500/30 p-3 rounded">
                <p className="text-[11px] text-amber-200 italic">
                  <strong>Root cause:</strong> {data.root_cause_hypothesis}
                </p>
                <p className="text-[10px] text-slate-400 mt-1">
                  Queue reads from: <span className="font-mono">{data.queue_reads_from}</span> ·
                  Drawer reads from: <span className="font-mono">{data.drawer_reads_from}</span>
                </p>
                <p className="text-[10px] text-slate-400 mt-1">
                  Signals seen: <span className="font-mono">{Object.entries(data.by_paid_signal || {}).map(([s, n]) => `${s}:${n}`).join(" · ") || "—"}</span>
                </p>
              </div>

              {data.desync_count === 0 && (
                <p className="text-emerald-400 py-6 text-center">✨ No desync detected.</p>
              )}

              {data.desync_count > 0 && (
                <div className="space-y-3">
                  <div className="overflow-auto max-h-[50vh] border border-slate-700 rounded">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-800 sticky top-0">
                        <tr className="text-slate-300">
                          <th className="p-2 text-left">Reference</th>
                          <th className="p-2 text-left">Parent</th>
                          <th className="p-2 text-left">Amount</th>
                          <th className="p-2 text-left">Current status</th>
                          <th className="p-2 text-left">Paid signals</th>
                          <th className="p-2 text-left">Created</th>
                          <th className="p-2 text-left">Paid_at</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.rows.map((r) => (
                          <tr key={r.txn_id} className="border-t border-slate-800 hover:bg-slate-800/40">
                            <td className="p-2 font-mono text-white">{r.payment_reference}</td>
                            <td className="p-2">
                              <p className="text-slate-200">{r.parent_name || "—"}</p>
                              <p className="text-slate-500 text-[10px]">{r.parent_email}</p>
                            </td>
                            <td className="p-2 font-mono text-emerald-300">${(r.amount || 0).toFixed(2)}</td>
                            <td className="p-2">
                              <span className="text-rose-300 text-[11px]">{r.txn_payment_status}</span>
                            </td>
                            <td className="p-2 text-[10px]">
                              <ul className="space-y-0.5">
                                {(r.paid_signals || []).map((s, i) => (
                                  <li key={i} className="text-emerald-300">✓ {s}</li>
                                ))}
                              </ul>
                            </td>
                            <td className="p-2 text-slate-500 text-[10px]">{r.created_at?.slice(0, 10)}</td>
                            <td className="p-2 text-slate-500 text-[10px]">{r.paid_at?.slice(0, 10) || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="bg-slate-800/40 border border-slate-700 p-3 rounded">
                    <p className="text-rose-200 font-medium mb-2">Proposed fix — flip payment_status: pending* → paid</p>
                    <p className="text-[11px] text-slate-400 mb-2">
                      Same safety pattern as hygiene backfill. Dry-run preview first; execute requires confirm_token.
                      Idempotent (logged to scheduled_task_state). Records previous status + signals in admin_audit_log.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm" variant="outline" disabled={fixBusy}
                        className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                        onClick={runFixDryRun}
                        data-testid="t1-fix-dryrun-btn"
                      >
                        {fixBusy ? <Loader2 className="w-3 h-3 mr-1 animate-spin inline" /> : null}
                        Preview fix (dry-run)
                      </Button>
                      {fixDryRun && !fixDryRun._executed && (
                        <Button
                          size="sm" disabled={fixBusy}
                          className="bg-rose-600 hover:bg-rose-500"
                          onClick={runFixExecute}
                          data-testid="t1-fix-execute-btn"
                        >
                          Execute fix
                        </Button>
                      )}
                      {fixDryRun && (
                        <Button
                          size="sm" variant="outline"
                          onClick={() => downloadJson(fixDryRun, fixDryRun._executed ? "executed" : "dryrun")}
                          data-testid="t1-fix-download-btn"
                        >
                          <Download className="w-3 h-3 mr-1" /> JSON
                        </Button>
                      )}
                    </div>
                    {fixDryRun && (
                      <div className="mt-3 text-[11px]">
                        <div className={`px-2 py-1 rounded font-medium ${fixDryRun._executed ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/30" : "bg-amber-500/10 text-amber-300 border border-amber-500/30"}`}>
                          {fixDryRun._executed
                            ? `✓ Executed — flipped ${fixDryRun.totals?.flipped ?? 0} rows`
                            : `⚠ DRY-RUN — would flip ${fixDryRun.totals?.flipped ?? 0} rows`}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              <p className="text-[10px] text-slate-500 italic mt-3">
                Read-only audit until you click "Execute fix". The fix mirrors paid_at/verified_at into payment_status and writes a per-row diff to admin_audit_log.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


function DuplicatePlayersModal({
  loading,
  data,
  keepSelection,
  setKeepSelection,
  busy,
  result,
  onClose,
  onMerge,
}) {
  const groups = data?.groups || [];
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="duplicate-players-modal">
      <div className="bg-slate-950 border border-fuchsia-500/30 rounded-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-slate-800">
          <div>
            <h2 className="text-lg font-semibold text-white">Duplicate Players</h2>
            <p className="text-xs text-slate-400 mt-0.5">
              Groups of <code className="text-fuchsia-300">academy_players</code> rows sharing the same parent + given_name + last_name + birthdate. Pick the row to KEEP per group — every other row is soft-deleted with <code>archived_reason: "duplicate_of_&lt;keep_id&gt;"</code> and all references are repointed.
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="dup-players-close-btn">
            <X className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex-1 overflow-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-slate-400">
              <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Scanning academy_players…
            </div>
          ) : data?.error ? (
            <div className="text-red-300 text-sm">Error: {data.error}</div>
          ) : groups.length === 0 ? (
            <div className="text-center py-12 text-emerald-300">
              <CheckCircle className="w-10 h-10 mx-auto mb-2" />
              No duplicate player groups found. Everyone's tidy.
            </div>
          ) : (
            <div className="space-y-4">
              <div className="text-xs text-slate-400">
                Found <strong className="text-fuchsia-300">{groups.length}</strong> duplicate group{groups.length === 1 ? "" : "s"}.
              </div>
              {groups.map((g, idx) => {
                const selectedKeep = keepSelection[idx];
                const archiveIds = (g.rows || []).map((r) => r.player_id).filter((pid) => pid && pid !== selectedKeep);
                const mergedThisGroup = result && result.groupIdx === idx;
                return (
                  <div key={`${g.parent_id}-${g.birthdate}-${idx}`} className="border border-slate-800 rounded-lg overflow-hidden" data-testid={`dup-group-${idx}`}>
                    <div className="px-3 py-2 bg-slate-900/70 border-b border-slate-800 flex items-center justify-between">
                      <div className="text-sm text-slate-200">
                        <strong>{g.given_name} {g.last_name}</strong>
                        <span className="text-slate-500 ml-2">(DOB {g.birthdate})</span>
                        <span className="text-slate-500 mx-2">·</span>
                        <span className="text-slate-300">{g.parent_name || "(unknown parent)"}</span>
                        <span className="text-slate-500 ml-1">&lt;{g.parent_email}&gt;</span>
                      </div>
                      <Badge variant="outline" className="border-fuchsia-500/40 text-fuchsia-300">{g.row_count} rows</Badge>
                    </div>
                    <table className="w-full text-xs">
                      <thead className="bg-slate-900/40">
                        <tr className="text-left text-slate-400">
                          <th className="px-3 py-2">Keep</th>
                          <th className="px-3 py-2">Player ID</th>
                          <th className="px-3 py-2">Membership #</th>
                          <th className="px-3 py-2">Status</th>
                          <th className="px-3 py-2">Paid txns</th>
                          <th className="px-3 py-2">$ paid</th>
                          <th className="px-3 py-2" title="Credits attached to THIS player_id only (parent-level credits shown below the table)">Player credits</th>
                          <th className="px-3 py-2">Created</th>
                          <th className="px-3 py-2">Parent link fields</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(g.rows || []).map((r) => {
                          const isKeep = selectedKeep === r.player_id;
                          const isSuggested = g.suggested_keep_player_id === r.player_id;
                          return (
                            <tr
                              key={r.player_id}
                              className={`border-t border-slate-800 ${isKeep ? "bg-emerald-500/5" : ""}`}
                              data-testid={`dup-row-${r.player_id}`}
                            >
                              <td className="px-3 py-2">
                                <input
                                  type="radio"
                                  name={`keep-${idx}`}
                                  checked={isKeep}
                                  onChange={() => setKeepSelection({ ...keepSelection, [idx]: r.player_id })}
                                  data-testid={`dup-keep-radio-${r.player_id}`}
                                />
                                {isSuggested && <span className="ml-1 text-[10px] text-emerald-400">(suggested)</span>}
                              </td>
                              <td className="px-3 py-2 font-mono text-slate-300 truncate max-w-[180px]" title={r.player_id}>{r.player_id}</td>
                              <td className="px-3 py-2 text-amber-300">{r.membership_number || <span className="text-slate-600 italic">(blank)</span>}</td>
                              <td className="px-3 py-2 text-slate-300">{r.status || "—"}</td>
                              <td className="px-3 py-2 text-slate-300">{r.paid_txn_count}</td>
                              <td className="px-3 py-2 text-emerald-300">{r.paid_total > 0 ? `$${r.paid_total.toFixed(2)}` : "—"}</td>
                              <td className="px-3 py-2 text-cyan-300" data-testid={`dup-credits-${r.player_id}`}>
                                {r.credits_row_count > 0
                                  ? <span title={`${r.credits_row_count} row${r.credits_row_count === 1 ? "" : "s"}`}>${(r.credits_balance || 0).toFixed(2)}</span>
                                  : <span className="text-slate-600">—</span>}
                              </td>
                              <td className="px-3 py-2 text-slate-500">{(r.created_at || "").slice(0, 10)}</td>
                              <td className="px-3 py-2 text-[10px] text-slate-500">
                                {r.parent_id_field ? "parent_id" : ""}
                                {r.parent_id_field && r.parent_user_id_field ? " + " : ""}
                                {r.parent_user_id_field ? "parent_user_id" : ""}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <div className="px-3 py-2 bg-slate-900/40 border-t border-slate-800 flex items-center justify-between gap-3 flex-wrap">
                      <div className="text-xs text-slate-400 flex items-center gap-3">
                        <span>
                          {selectedKeep
                            ? <>Will archive <strong className="text-fuchsia-300">{archiveIds.length}</strong> row{archiveIds.length === 1 ? "" : "s"}.</>
                            : "Select which row to keep."}
                        </span>
                        <span className="text-cyan-300/80" data-testid={`dup-parent-credits-${idx}`}>
                          Parent-level credits: <strong>${(g.parent_credits_balance || 0).toFixed(2)}</strong>
                          <span className="text-slate-500 ml-1">({g.parent_credits_row_count || 0} row{(g.parent_credits_row_count || 0) === 1 ? "" : "s"}, follows parent — unaffected by merge)</span>
                        </span>
                      </div>
                      {mergedThisGroup ? (
                        <span className="text-xs text-emerald-300 flex items-center">
                          <CheckCircle className="w-3.5 h-3.5 mr-1" /> Merged at {new Date(result.merged_at).toLocaleTimeString()}
                        </span>
                      ) : (
                        <Button
                          onClick={() => onMerge(idx, g)}
                          disabled={busy || !selectedKeep || archiveIds.length === 0}
                          size="sm"
                          className="bg-fuchsia-600 hover:bg-fuchsia-500 text-white"
                          data-testid={`dup-merge-btn-${idx}`}
                        >
                          {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Wrench className="w-3.5 h-3.5 mr-1" />}
                          Merge group
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FindPlayerModal({
  loading, data, given, last, setGiven, setLast, onClose, onSearch,
  forceMergeKeep, setForceMergeKeep,
  forceMergeArchive, setForceMergeArchive,
  forceMergeReason, setForceMergeReason,
  forceMergePreview, forceMergeBusy, forceMergeResult,
  onForceMergePreview, onForceMergeExecute,
  onReassignParent,
}) {
  const rows = data?.rows || [];
  const archiveSelectedCount = Object.values(forceMergeArchive || {}).filter(Boolean).length;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="find-player-modal">
      <div className="bg-slate-950 border border-cyan-500/30 rounded-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-slate-800">
          <div>
            <h2 className="text-lg font-semibold text-white">Find player by name</h2>
            <p className="text-xs text-slate-400 mt-0.5">
              Returns every <code className="text-cyan-300">academy_players</code> row matching the name (case-insensitive),
              regardless of birthdate or parent. Use this to surface duplicates the grouper misses — and force-merge them
              when you're certain they're the same child.
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="find-player-close-btn">
            <X className="w-4 h-4" />
          </Button>
        </div>
        <div className="p-4 border-b border-slate-800 bg-slate-900/30 flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-[10px] uppercase tracking-wide text-slate-500 mb-1">Given name</label>
            <Input
              value={given}
              onChange={(e) => setGiven(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") onSearch(); }}
              placeholder="e.g. Jasper"
              className="bg-slate-950 border-cyan-500/30 text-slate-200 w-44"
              data-testid="find-player-given-input"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wide text-slate-500 mb-1">Last name</label>
            <Input
              value={last}
              onChange={(e) => setLast(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") onSearch(); }}
              placeholder="e.g. Liu"
              className="bg-slate-950 border-cyan-500/30 text-slate-200 w-44"
              data-testid="find-player-last-input"
            />
          </div>
          <Button
            onClick={onSearch}
            disabled={loading}
            className="bg-cyan-600 hover:bg-cyan-500 text-white"
            data-testid="find-player-search-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Stethoscope className="w-4 h-4 mr-1" />}
            Search
          </Button>
        </div>
        <div className="flex-1 overflow-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-slate-400">
              <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Looking up…
            </div>
          ) : data?.error ? (
            <div className="text-red-300 text-sm">Error: {data.error}</div>
          ) : !data ? (
            <div className="text-slate-500 text-sm italic">Enter a name and click Search.</div>
          ) : rows.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              No academy_players rows match <strong>{given} {last}</strong>.
            </div>
          ) : (
            <div className="space-y-3">
              <div className="text-xs text-slate-400">
                Found <strong className="text-cyan-300">{rows.length}</strong> row{rows.length === 1 ? "" : "s"} for <strong>{given} {last}</strong>.
                {(() => {
                  const dobs = Array.from(new Set(rows.map((r) => r.birthdate).filter(Boolean)));
                  const parents = Array.from(new Set(rows.map((r) => r.parent_id_field || r.parent_user_id_field).filter(Boolean)));
                  return (
                    <span className="ml-2">
                      Distinct birthdates: <strong>{dobs.length}</strong> ({dobs.join(", ") || "—"}). Distinct parents: <strong>{parents.length}</strong>.
                    </span>
                  );
                })()}
              </div>
              <table className="w-full text-xs border border-slate-800 rounded">
                <thead className="bg-slate-900/70">
                  <tr className="text-left text-slate-400">
                    <th className="px-2 py-2">Keep</th>
                    <th className="px-2 py-2">Archive</th>
                    <th className="px-2 py-2">Player ID</th>
                    <th className="px-2 py-2">Membership #</th>
                    <th className="px-2 py-2">Birthdate</th>
                    <th className="px-2 py-2">Parent</th>
                    <th className="px-2 py-2">Status</th>
                    <th className="px-2 py-2">Paid txns</th>
                    <th className="px-2 py-2">$ paid</th>
                    <th className="px-2 py-2">Player credits</th>
                    <th className="px-2 py-2">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const isKeep = forceMergeKeep === r.player_id;
                    const isArchive = !!forceMergeArchive[r.player_id];
                    return (
                      <tr
                        key={r.player_id}
                        className={`border-t border-slate-800 ${isKeep ? "bg-emerald-500/5" : isArchive ? "bg-rose-500/5" : ""}`}
                        data-testid={`find-player-row-${r.player_id}`}
                      >
                        <td className="px-2 py-2">
                          <input
                            type="radio"
                            name="force-merge-keep"
                            checked={isKeep}
                            onChange={() => {
                              setForceMergeKeep(r.player_id);
                              // If this row was selected as archive, unselect it
                              if (forceMergeArchive[r.player_id]) {
                                setForceMergeArchive({ ...forceMergeArchive, [r.player_id]: false });
                              }
                            }}
                            data-testid={`force-merge-keep-${r.player_id}`}
                          />
                        </td>
                        <td className="px-2 py-2">
                          <input
                            type="checkbox"
                            checked={isArchive}
                            disabled={isKeep}
                            onChange={(e) => setForceMergeArchive({ ...forceMergeArchive, [r.player_id]: e.target.checked })}
                            data-testid={`force-merge-archive-${r.player_id}`}
                          />
                        </td>
                        <td className="px-2 py-2 font-mono text-slate-300 truncate max-w-[140px]" title={r.player_id}>{r.player_id}</td>
                        <td className="px-2 py-2 text-amber-300">{r.membership_number || <span className="text-slate-600 italic">(blank)</span>}</td>
                        <td className="px-2 py-2 text-slate-300">{r.birthdate || "—"}</td>
                        <td className="px-2 py-2 text-slate-300">
                          <div className="flex items-center gap-1">
                            <span>{r.parent_name || <span className="text-amber-400 italic">(placeholder / no parent)</span>}</span>
                            <button
                              type="button"
                              onClick={() => onReassignParent && onReassignParent(r)}
                              className="p-0.5 text-slate-500 hover:text-cyan-300 border border-slate-700 hover:border-cyan-500/40 rounded"
                              title="Reassign this child to a different parent"
                              data-testid={`reassign-parent-btn-${r.player_id}`}
                            >
                              <Users className="w-3 h-3" />
                            </button>
                          </div>
                          <div className="text-[10px] text-slate-500">{r.parent_email}</div>
                        </td>
                        <td className="px-2 py-2 text-slate-300">{r.status || "—"}</td>
                        <td className="px-2 py-2 text-slate-300">{r.paid_txn_count}</td>
                        <td className="px-2 py-2 text-emerald-300">{r.paid_total > 0 ? `$${r.paid_total.toFixed(2)}` : "—"}</td>
                        <td className="px-2 py-2 text-cyan-300">
                          {r.credits_row_count > 0 ? `$${(r.credits_balance || 0).toFixed(2)}` : <span className="text-slate-600">—</span>}
                        </td>
                        <td className="px-2 py-2 text-slate-500">{(r.created_at || "").slice(0, 10)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* ─── Force merge section ─────────────────────────────────────────── */}
              {(forceMergeKeep || archiveSelectedCount > 0 || forceMergePreview) && (
                <div className="mt-4 border border-rose-500/30 bg-rose-500/5 rounded-lg p-3" data-testid="force-merge-panel">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-rose-300 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" /> Force merge (bypasses birthdate / name match)
                    </h3>
                    <span className="text-[10px] text-slate-500">Same parent only · audit-logged · two-step confirm</span>
                  </div>

                  <div className="text-xs text-slate-300 mb-3">
                    Keep: <strong className="text-emerald-300">{forceMergeKeep || "(pick one)"}</strong>
                    <span className="mx-2 text-slate-500">·</span>
                    Archive: <strong className="text-rose-300">{archiveSelectedCount}</strong> row{archiveSelectedCount === 1 ? "" : "s"}
                  </div>

                  {!forceMergePreview && !forceMergeResult && (
                    <Button
                      onClick={onForceMergePreview}
                      disabled={forceMergeBusy || !forceMergeKeep || archiveSelectedCount === 0}
                      size="sm"
                      className="bg-rose-600 hover:bg-rose-500 text-white"
                      data-testid="force-merge-preview-btn"
                    >
                      {forceMergeBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <AlertTriangle className="w-3.5 h-3.5 mr-1" />}
                      Preview force merge
                    </Button>
                  )}

                  {forceMergePreview && !forceMergeResult && (
                    <div className="space-y-3" data-testid="force-merge-preview-result">
                      <div className="text-xs text-slate-300 space-y-1">
                        <div>
                          <strong className="text-rose-300">Differences from keep:</strong>
                          <ul className="ml-4 mt-1 text-[11px] text-slate-400 list-disc list-inside">
                            {Object.entries(forceMergePreview.differences_from_keep || {}).map(([aid, diff]) => (
                              <li key={aid} className="font-mono">
                                {aid.slice(0, 12)}…:
                                {diff.given_name && <span className="text-amber-300 ml-1">given_name</span>}
                                {diff.last_name && <span className="text-amber-300 ml-1">last_name</span>}
                                {diff.birthdate && <span className="text-amber-300 ml-1">birthdate</span>}
                                {!diff.given_name && !diff.last_name && !diff.birthdate && <span className="text-emerald-300 ml-1">(all match)</span>}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <strong className="text-rose-300">Will backfill on keep:</strong>{" "}
                          {(forceMergePreview.would_backfill_fields || []).length === 0
                            ? <span className="text-slate-500">(nothing — keep already complete)</span>
                            : <span className="text-amber-300">{(forceMergePreview.would_backfill_fields || []).join(", ")}</span>}
                        </div>
                        <div>
                          <strong className="text-rose-300">Will repoint:</strong>{" "}
                          {Object.entries(forceMergePreview.would_repoint || {}).map(([k, n]) => (
                            <span key={k} className={n > 0 ? "text-amber-300 mr-2" : "text-slate-500 mr-2"}>
                              {k}={n}
                            </span>
                          ))}
                        </div>
                        <div className="text-[10px] text-slate-500">
                          Archive reason will be: <code className="text-rose-300">{forceMergePreview.would_archive_reason}</code>
                          {"  "}· Confirm token valid for {forceMergePreview.token_expires_in_seconds}s
                        </div>
                      </div>

                      <div>
                        <label className="block text-[10px] uppercase tracking-wide text-slate-500 mb-1">
                          Reason (required — logged to admin_audit_log)
                        </label>
                        <Textarea
                          value={forceMergeReason}
                          onChange={(e) => setForceMergeReason(e.target.value)}
                          placeholder="e.g. 'Birthdate typo on archived row — confirmed with parent via email 14 Feb 2026'"
                          rows={2}
                          className="bg-slate-950 border-rose-500/30 text-slate-200"
                          data-testid="force-merge-reason-input"
                        />
                      </div>

                      <Button
                        onClick={onForceMergeExecute}
                        disabled={forceMergeBusy || !forceMergeReason.trim()}
                        size="sm"
                        className="bg-rose-600 hover:bg-rose-500 text-white"
                        data-testid="force-merge-execute-btn"
                      >
                        {forceMergeBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5 mr-1" />}
                        Execute force merge
                      </Button>
                    </div>
                  )}

                  {forceMergeResult && (
                    <div className="text-xs text-emerald-300 space-y-1" data-testid="force-merge-result">
                      <div className="flex items-center"><CheckCircle className="w-3.5 h-3.5 mr-1" /> Force merge complete.</div>
                      <div className="text-slate-300">
                        Kept: <code>{forceMergeResult.kept_player_id}</code> · Archived: <code>{(forceMergeResult.archived_player_ids || []).join(", ")}</code>
                      </div>
                      <div className="text-slate-400">
                        Repoint: {Object.entries(forceMergeResult.repoint_stats || {}).filter(([, n]) => n > 0).map(([k, n]) => `${k}=${n}`).join(", ") || "(none)"}
                      </div>
                      <div className="text-[10px] text-slate-500">Reason logged: "{forceMergeResult.reason}"</div>
                    </div>
                  )}
                </div>
              )}

              <p className="text-[10px] text-slate-500 italic mt-2">
                Tip: only force-merge when you're confident both rows describe the same child. The archive_reason on archived rows
                is tagged with <code>_force_merged</code> so an audit can tell them apart from a standard merge later.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function ProrataAuditModal({
  loading, data, expanded, setExpanded,
  terms, termsLoading, selectedTermId, setSelectedTermId,
  onRun, onClose, onDownload,
}) {
  const rows = data?.rows || [];
  const term = data?.term || {};
  // Per-row admin input: actual training start week (string, blank by default)
  // Tracked locally — never sent to backend; difference is computed client-side.
  const [actualStartWeek, setActualStartWeek] = useState({});
  // Track which rows are showing the auto-suggested value (not yet manually
  // edited). When admin types, the suggestion-highlight goes away.
  const [autoSuggestApplied, setAutoSuggestApplied] = useState({});

  // When the audit data refreshes, pre-fill any rows the backend flagged with
  // an auto_suggested_training_start_week. Admin can override or clear.
  useEffect(() => {
    if (!data || !Array.isArray(data.rows)) return;
    const nextValues = {};
    const nextApplied = {};
    for (const r of data.rows) {
      if (r.auto_suggested_training_start_week != null) {
        nextValues[r.registration_id] = String(r.auto_suggested_training_start_week);
        nextApplied[r.registration_id] = true;
      }
    }
    setActualStartWeek((prev) => ({ ...nextValues, ...prev }));
    setAutoSuggestApplied(nextApplied);
  }, [data]);

  const computeDifference = (row) => {
    const raw = actualStartWeek[row.registration_id];
    if (raw === undefined || raw === null || String(raw).trim() === "") {
      return { status: "no_action", amount: 0, weeks_diff: 0 };
    }
    const trainingStart = Number(raw);
    if (!Number.isFinite(trainingStart) || trainingStart <= 0) {
      return { status: "no_action", amount: 0, weeks_diff: 0 };
    }
    const regWeek = Number(row.registration_week || 0);
    const weeksDiff = regWeek - trainingStart;
    const weeklyRate = Number(row.sum_discounted_weekly_rate || 0);
    if (weeksDiff > 0) {
      return { status: "back_payment", amount: weeksDiff * weeklyRate, weeks_diff: weeksDiff };
    }
    if (weeksDiff < 0) {
      return { status: "credit", amount: Math.abs(weeksDiff) * weeklyRate, weeks_diff: weeksDiff };
    }
    return { status: "no_action", amount: 0, weeks_diff: 0 };
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="prorata-audit-modal">
      <div className="bg-slate-950 border border-orange-500/30 rounded-xl w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-slate-800">
          <div>
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Calculator className="w-5 h-5 text-orange-400" /> Pro-rata audit
              <Badge variant="outline" className="border-amber-500/40 text-amber-300 text-[10px]">read-only / advisory</Badge>
            </h2>
            <p className="text-xs text-slate-400 mt-0.5">
              <strong className="text-amber-300">This is a read-only advisory tool.</strong> No changes are made automatically — all rows require manual review.
              Fee breakdown is read directly from the stored registration record (no recalculation).
              Enter the <strong>actual training start week</strong> per row only when you know the child began training on a date different from the registration date.
              All follow-up actions (manual invoices for back-payment, credits) must be done through the existing payment tools.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {data && !data.error && rows.length > 0 && (
              <Button onClick={onDownload} variant="outline" size="sm" className="border-orange-500/40 text-orange-300" data-testid="prorata-download-btn">
                <Download className="w-4 h-4 mr-1" /> JSON
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={onClose} data-testid="prorata-close-btn">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Term picker */}
        <div className="p-4 border-b border-slate-800 bg-slate-900/30 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[280px]">
            <label className="block text-[10px] uppercase tracking-wide text-slate-500 mb-1">Term</label>
            <select
              value={selectedTermId}
              onChange={(e) => setSelectedTermId(e.target.value)}
              disabled={termsLoading || loading}
              className="w-full bg-slate-950 border border-orange-500/30 text-slate-200 rounded px-2 py-1.5 text-sm"
              data-testid="prorata-term-select"
            >
              {termsLoading && <option>Loading terms…</option>}
              {!termsLoading && terms.length === 0 && <option value="">(no terms found)</option>}
              {terms.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name || t.id} — {t.start_date} → {t.end_date}{t.__is_active ? " · ACTIVE" : ""}
                </option>
              ))}
            </select>
          </div>
          <Button
            onClick={() => { setActualStartWeek({}); onRun(); }}
            disabled={loading || termsLoading || !selectedTermId}
            className="bg-orange-600 hover:bg-orange-500 text-white"
            data-testid="prorata-run-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Calculator className="w-4 h-4 mr-1" />}
            Run audit
          </Button>
        </div>

        <div className="flex-1 overflow-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-slate-400">
              <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Auditing every paid registration…
            </div>
          ) : data?.error ? (
            <div className="text-red-300 text-sm">Error: {data.error}</div>
          ) : !data ? (
            <div className="text-slate-500 text-sm italic">Pick a term and click Run audit.</div>
          ) : (
            <div className="space-y-4">
              {/* Summary cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <SummaryCard label="Registrations audited" value={data.registration_count || 0} accent="slate" />
                <SummaryCard
                  label="Back-payment flagged"
                  value={rows.filter((r) => computeDifference(r).status === "back_payment").length}
                  sub="rows with manual start week entered"
                  accent="rose"
                />
                <SummaryCard
                  label="Credit flagged"
                  value={rows.filter((r) => computeDifference(r).status === "credit").length}
                  sub="rows with manual start week entered"
                  accent="cyan"
                />
                <SummaryCard
                  label="No action"
                  value={rows.filter((r) => computeDifference(r).status === "no_action").length}
                  sub="start week blank"
                  accent="slate"
                />
              </div>

              {/* Term card */}
              <div className="border border-slate-800 rounded p-3 text-xs text-slate-300 bg-slate-900/30">
                <div className="font-semibold text-slate-200">
                  {term.name || term.id} <span className="text-slate-500 ml-2">{term.start_date} → {term.end_date}</span>
                  <span className="text-slate-500 ml-2">· {term.session_weeks_total} weeks</span>
                  {term.base_session_rate != null && (
                    <span className="text-slate-500 ml-2">· base session rate ${Number(term.base_session_rate).toFixed(2)}</span>
                  )}
                </div>
                <div className="mt-1 text-slate-400">
                  Session weeks: <strong className="text-emerald-300">{term.session_weeks_total}</strong>
                  {" · "}Holiday weeks: <strong className="text-amber-300">{term.holiday_weeks_total}</strong>
                  {(term.term_breaks || []).length > 0 && (
                    <span className="text-slate-500"> · breaks: {(term.term_breaks || []).map((b) => `${b.start_date}→${b.end_date}`).join(", ")}</span>
                  )}
                </div>
              </div>

              {/* Per-registration table */}
              {rows.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  No paid registrations for this term.
                </div>
              ) : (
                <div className="border border-slate-800 rounded overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-900/70">
                      <tr className="text-left text-slate-400">
                        <th className="px-2 py-2 w-8"></th>
                        <th className="px-2 py-2">Parent</th>
                        <th className="px-2 py-2">Child(ren)</th>
                        <th className="px-2 py-2">Registered (week)</th>
                        <th className="px-2 py-2 text-right">Weeks charged</th>
                        <th className="px-2 py-2 text-right">Total paid</th>
                        <th className="px-2 py-2">Actual training start week</th>
                        <th className="px-2 py-2">Earliest attendance</th>
                        <th className="px-2 py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, idx) => {
                        const isOpen = !!expanded[idx];
                        const diff = computeDifference(r);
                        const fb = r.fee_breakdown_stored || {};
                        return (
                          <React.Fragment key={r.registration_id || idx}>
                            <tr
                              className="border-t border-slate-800 hover:bg-slate-900/30"
                              data-testid={`prorata-row-${idx}`}
                            >
                              <td className="px-2 py-2">
                                <button
                                  onClick={() => setExpanded({ ...expanded, [idx]: !isOpen })}
                                  className="text-slate-500 hover:text-slate-200"
                                  data-testid={`prorata-toggle-${idx}`}
                                >
                                  {isOpen ? "▾" : "▸"}
                                </button>
                              </td>
                              <td className="px-2 py-2">
                                <div className="text-slate-200">{r.parent_name || "—"}</div>
                                <div className="text-[10px] text-slate-500">{r.parent_email}</div>
                              </td>
                              <td className="px-2 py-2 text-slate-300">
                                {(r.child_names && r.child_names.length > 0)
                                  ? r.child_names.join(", ")
                                  : (r.children || []).map((c) => c.child_name).join(", ") || "—"}
                              </td>
                              <td className="px-2 py-2 text-slate-300">
                                {r.registration_week_label}
                                <div className="text-[10px] text-slate-500">{(r.registered_at || "").slice(0, 10)}</div>
                              </td>
                              <td className="px-2 py-2 text-right text-slate-200">
                                {Number(r.weeks_charged).toFixed(1)}
                                <div className="text-[10px] text-slate-500">of {r.session_weeks_total}</div>
                              </td>
                              <td className="px-2 py-2 text-right text-slate-200">
                                ${Number(r.actual_fee_paid ?? fb.total_fee_charged ?? 0).toFixed(2)}
                                {Number(fb.dm_fee || 0) > 0 && (
                                  <div className="text-[10px] text-slate-500">incl. DM ${Number(fb.dm_fee).toFixed(2)}</div>
                                )}
                                {r.fee_source && r.fee_source !== "missing" && (
                                  <div className="text-[9px] text-slate-600" title={`Source: ${r.fee_source}`}>
                                    src: {r.fee_source.split(".").pop()}
                                  </div>
                                )}
                                {r.fee_source === "missing" && (
                                  <div className="text-[9px] text-rose-400">⚠ fee missing</div>
                                )}
                              </td>
                              <td className="px-2 py-2">
                                <input
                                  type="number"
                                  min="1"
                                  max={r.session_weeks_total}
                                  step="0.5"
                                  placeholder="leave blank"
                                  value={actualStartWeek[r.registration_id] ?? ""}
                                  onChange={(e) => {
                                    setActualStartWeek({
                                      ...actualStartWeek,
                                      [r.registration_id]: e.target.value,
                                    });
                                    // Admin edited it — drop the auto-suggest visual
                                    if (autoSuggestApplied[r.registration_id]) {
                                      setAutoSuggestApplied({
                                        ...autoSuggestApplied,
                                        [r.registration_id]: false,
                                      });
                                    }
                                  }}
                                  className={
                                    "w-24 rounded px-2 py-1 text-xs " +
                                    (autoSuggestApplied[r.registration_id]
                                      ? "bg-amber-500/10 border border-amber-500/60 text-amber-200"
                                      : "bg-slate-950 border border-slate-700 text-slate-200")
                                  }
                                  data-testid={`prorata-actual-start-week-${idx}`}
                                />
                                {autoSuggestApplied[r.registration_id] && (
                                  <div
                                    className="text-[9px] text-amber-300 mt-1 max-w-[180px] leading-tight"
                                    data-testid={`prorata-autosuggest-label-${idx}`}
                                  >
                                    Auto-suggested from attendance records — review and confirm before using.
                                    {r.auto_suggested_reason && (
                                      <div className="text-[8px] text-amber-400/70 mt-0.5">
                                        {r.auto_suggested_reason}
                                      </div>
                                    )}
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setActualStartWeek({
                                          ...actualStartWeek,
                                          [r.registration_id]: "",
                                        });
                                        setAutoSuggestApplied({
                                          ...autoSuggestApplied,
                                          [r.registration_id]: false,
                                        });
                                      }}
                                      className="text-slate-500 hover:text-slate-200 underline text-[9px] mt-0.5"
                                      data-testid={`prorata-autosuggest-clear-${idx}`}
                                    >
                                      clear
                                    </button>
                                  </div>
                                )}
                              </td>
                              <td className="px-2 py-2 text-amber-300 text-[10px]">
                                {r.earliest_attendance_date || <span className="text-slate-600">—</span>}
                              </td>
                              <td className="px-2 py-2" data-testid={`prorata-status-${idx}`}>
                                {diff.status === "no_action" && (
                                  <Badge variant="outline" className="border-slate-700 text-slate-400 text-[9px]">No action</Badge>
                                )}
                                {diff.status === "back_payment" && (
                                  <Badge variant="outline" className="border-rose-500/40 text-rose-300 text-[9px] font-semibold">
                                    Back-payment owed: ${diff.amount.toFixed(2)}
                                  </Badge>
                                )}
                                {diff.status === "credit" && (
                                  <Badge variant="outline" className="border-cyan-500/40 text-cyan-300 text-[9px] font-semibold">
                                    Credit owed: ${diff.amount.toFixed(2)}
                                  </Badge>
                                )}
                              </td>
                            </tr>
                            {isOpen && (
                              <tr className="bg-slate-900/40">
                                <td colSpan={9} className="px-4 py-3">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[11px]">
                                    {/* Stored fee breakdown */}
                                    <div className="border border-slate-800 rounded p-2">
                                      <div className="text-slate-400 uppercase tracking-wide text-[9px] mb-1 flex items-center gap-1">
                                        Stored fee breakdown (no recalculation)
                                        {fb.legacy_discount_note && (
                                          <Badge variant="outline" className="border-amber-500/40 text-amber-300 text-[8px]" data-testid={`prorata-legacy-note-${idx}`}>
                                            {fb.legacy_discount_note}
                                          </Badge>
                                        )}
                                      </div>
                                      <div className="space-y-0.5 text-slate-300">
                                        <div>Base session rate: <strong>${Number(fb.base_session_rate || 0).toFixed(2)}</strong> per session per week</div>
                                        <div>Member tier: <code className="text-slate-200">{fb.member_tier_label || "—"}</code></div>
                                        <div>Member discount total: <strong>${Number(fb.member_discount_amount || 0).toFixed(2)}</strong></div>
                                        <div>Sibling discount total: <strong>${Number(fb.sibling_discount_amount || 0).toFixed(2)}</strong></div>
                                        <div>Digital Membership fee: <strong>${Number(fb.dm_fee || 0).toFixed(2)}</strong></div>
                                        <div className="pt-1 border-t border-slate-800 mt-1">
                                          Total fee charged: <strong className="text-emerald-300">${Number(fb.total_fee_charged || 0).toFixed(2)}</strong>
                                        </div>
                                        {fb.discount_source && fb.discount_source !== "fee_breakdown.line_items" && (
                                          <div className="text-[9px] text-amber-400/70 pt-0.5">
                                            Discount data source: <code>{fb.discount_source}</code>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    {/* Reverse-engineered weeks math */}
                                    <div className="border border-slate-800 rounded p-2">
                                      <div className="text-slate-400 uppercase tracking-wide text-[9px] mb-1">Reverse-engineered weeks charged</div>
                                      <div className="space-y-0.5 text-slate-300">
                                        <div>Sum discounted weekly rate: <strong>${Number(r.sum_discounted_weekly_rate || 0).toFixed(2)}</strong>/wk</div>
                                        <div className="text-slate-400 italic text-[10px]">
                                          = (total − DM) ÷ (Σ kids: sessions/wk × rate × (1−member%) × (1−sibling%))
                                        </div>
                                        <div>Raw weeks: <code className="text-slate-200">{Number(r.weeks_charged_raw || 0).toFixed(3)}</code></div>
                                        <div>Rounded: <strong className="text-amber-300">{Number(r.weeks_charged || 0).toFixed(1)} weeks</strong></div>
                                      </div>
                                    </div>
                                  </div>
                                  {/* Per-child breakdown */}
                                  <div className="mt-2 border border-slate-800 rounded overflow-hidden">
                                    <table className="w-full text-[10px]">
                                      <thead className="bg-slate-900/70 text-slate-500">
                                        <tr>
                                          <th className="text-left px-2 py-1">Child</th>
                                          <th className="text-right px-2 py-1">Sessions/wk</th>
                                          <th className="text-right px-2 py-1">Per-session rate</th>
                                          <th className="text-right px-2 py-1">Member %</th>
                                          <th className="text-right px-2 py-1">Sibling %</th>
                                          <th className="text-right px-2 py-1">Member discount $</th>
                                          <th className="text-right px-2 py-1">Sibling discount $</th>
                                          <th className="text-right px-2 py-1">Discounted weekly</th>
                                          <th className="text-left px-2 py-1">Earliest attendance</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {(r.children || []).map((c, ci) => (
                                          <tr key={c.player_id || ci} className="border-t border-slate-800/60">
                                            <td className="px-2 py-1 text-slate-200">{c.child_name}</td>
                                            <td className="px-2 py-1 text-right text-slate-300">{c.sessions_per_week}</td>
                                            <td className="px-2 py-1 text-right text-slate-300">${Number(c.per_session_rate || 0).toFixed(2)}</td>
                                            <td className="px-2 py-1 text-right text-slate-300">{Number(c.member_discount_pct || 0).toFixed(1)}%</td>
                                            <td className="px-2 py-1 text-right text-slate-300">{Number(c.sibling_discount_pct || 0).toFixed(1)}%</td>
                                            <td className="px-2 py-1 text-right text-slate-400">${Number(c.member_discount_amount || 0).toFixed(2)}</td>
                                            <td className="px-2 py-1 text-right text-slate-400">${Number(c.sibling_discount_amount || 0).toFixed(2)}</td>
                                            <td className="px-2 py-1 text-right text-emerald-300 font-semibold">${Number(c.discounted_weekly_rate || 0).toFixed(2)}</td>
                                            <td className="px-2 py-1 text-amber-300">{c.earliest_attendance_date || <span className="text-slate-600">—</span>}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                  <div className="mt-2 text-[10px] text-slate-500 italic">
                                    Reference: <code>{r.payment_reference || r.registration_id?.slice(0, 12)}</code> · Status flag updates live as you type the actual training start week. DM fee is excluded from the per-week difference calculation (it's a fixed annual fee, not a per-week charge).
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              <p className="text-[10px] text-slate-500 italic">
                <strong>How weeks_charged is derived:</strong> (total_fee − DM_fee) ÷ (Σ kids: sessions/wk × per-session-rate × (1−member%) × (1−sibling%)), rounded to nearest 0.5. <strong>Difference math:</strong> when you enter an actual training start week N earlier than the registration week R, back-payment owed = (R−N) × sum_discounted_weekly_rate. When N is later, credit owed = (N−R) × sum_discounted_weekly_rate. The audit makes no automatic writes — follow up using the existing manual invoice / credit tools.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}



function InvoiceLinkButton({ txnId, eventIdx }) {
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState(null);
  const openInvoice = async () => {
    setBusy(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments-hub/admin/txn/${txnId}/invoice-html`,
        { withCredentials: true },
      );
      if (res.data?.found && res.data?.html) {
        // Render via a Blob URL instead of document.write — the browser
        // treats the resulting page as a separate origin, so any scripts
        // in the invoice HTML cannot read cookies / localStorage of the
        // hub. We also revoke the URL once the popup releases it.
        const blob = new Blob([res.data.html], { type: "text/html;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const w = window.open(url, "_blank", "noopener,noreferrer,width=820,height=900");
        if (w) {
          // Release the blob URL after the new window has had a chance to
          // load it. Browsers vary on when load fires for blob URLs in a
          // new tab, so we just defer revocation by ~60s.
          setTimeout(() => URL.revokeObjectURL(url), 60000);
          setState({ mode: "open" });
        } else {
          URL.revokeObjectURL(url);
          toast.error("Pop-up blocked — allow pop-ups for this site");
        }
      } else {
        setState({ mode: "resend_only", message: res.data?.message || "Invoice not stored" });
      }
    } catch (err) {
      const detail = err.response?.data?.detail || String(err);
      // 404 with resend_only flag also lands here in some setups — fall through
      // to the Resend button UX.
      if (err.response?.status === 404) {
        setState({ mode: "resend_only", message: detail });
      } else {
        setState({ mode: "error", message: detail });
        toast.error(`Open invoice failed: ${detail}`);
      }
    } finally {
      setBusy(false);
    }
  };
  const resendInvoice = async () => {
    setBusy(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments/admin/resend-invoice/${txnId}`,
        {},
        { withCredentials: true },
      );
      toast.success("Invoice resent to parent");
      setState({ mode: "open", message: "Invoice email resent" });
    } catch (err) {
      const detail = err.response?.data?.detail || String(err);
      toast.error(`Resend failed: ${detail}`);
    } finally {
      setBusy(false);
    }
  };
  if (state?.mode === "resend_only") {
    return (
      <button
        type="button"
        onClick={resendInvoice}
        disabled={busy}
        className="text-[11px] text-amber-300 underline hover:text-amber-200"
        data-testid={`timeline-invoice-resend-${eventIdx}`}
        title="Original invoice HTML wasn't stored — click to generate and email a fresh copy"
      >
        {busy ? "Resending…" : "Invoice not stored — Resend invoice"}
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={openInvoice}
      disabled={busy}
      className="text-[11px] text-cyan-300 underline hover:text-cyan-200"
      data-testid={`timeline-invoice-open-${eventIdx}`}
    >
      {busy ? "Loading…" : "Open invoice"}
    </button>
  );
}


function SummaryCard({ label, value, sub, accent }) {
  const accentMap = {
    slate: "border-slate-700 text-slate-200",
    orange: "border-orange-500/40 text-orange-200",
    cyan: "border-cyan-500/40 text-cyan-200",
    rose: "border-rose-500/40 text-rose-200",
    amber: "border-amber-500/40 text-amber-200",
    emerald: "border-emerald-500/40 text-emerald-200",
  };
  return (
    <div className={`border rounded p-2 ${accentMap[accent] || accentMap.slate}`}>
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-xl font-semibold">{value ?? "—"}</div>
      {sub && <div className="text-[10px] text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function dayName(d) {  // eslint-disable-line no-unused-vars
  if (d == null) return "—";
  const map = { 1: "Mon", 2: "Tue", 3: "Wed", 4: "Thu", 5: "Fri", 6: "Sat", 7: "Sun" };
  return map[d] || String(d);
}



function _csvEscape(v) {
  if (v === null || v === undefined) return "";
  const s = String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function FeeBreakdownDriftModal({ loading, data, repairBusy, repairResult, onRepair, onClose }) {
  const rows = React.useMemo(() => (data && Array.isArray(data.rows)) ? data.rows : [], [data]);
  const isError = !!(data && data.error);
  const hasPayload = !!(data && !data.error && typeof data.drift_count === "number");

  // Selection state: registration_id → boolean. Default ALL selected.
  const [selected, setSelected] = React.useState({});
  // Expand state: registration_id → boolean
  const [expanded, setExpanded] = React.useState({});
  // Inspect cache: registration_id → response from /inspect
  const [inspectCache, setInspectCache] = React.useState({});
  const [inspectLoading, setInspectLoading] = React.useState({});

  // Initialize selection when rows arrive — every REPAIRABLE row checked.
  // Rows whose underlying term has been deleted (`term_exists: false`) are
  // left unchecked because their proposed-rewrite cannot be computed and the
  // repair would error out for them.
  React.useEffect(() => {
    if (rows.length === 0) {
      setSelected({});
      return;
    }
    setSelected((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const r of rows) {
        if (next[r.registration_id] === undefined) {
          next[r.registration_id] = r.repairable !== false;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [rows]);

  const selectedIds = React.useMemo(
    () => rows.filter((r) => selected[r.registration_id]).map((r) => r.registration_id),
    [rows, selected],
  );
  const repairableRows = React.useMemo(() => rows.filter((r) => r.repairable !== false), [rows]);
  const unrepairableCount = rows.length - repairableRows.length;
  const allRepairableSelected = repairableRows.length > 0 && selectedIds.length === repairableRows.length;
  const noneSelected = selectedIds.length === 0;

  const toggleAll = () => {
    const target = !allRepairableSelected;
    const next = {};
    for (const r of rows) {
      next[r.registration_id] = target && r.repairable !== false;
    }
    setSelected(next);
  };

  const toggleRow = (id) => {
    setSelected((p) => ({ ...p, [id]: !p[id] }));
  };

  const loadInspect = async (id) => {
    if (inspectCache[id] || inspectLoading[id]) return;
    setInspectLoading((p) => ({ ...p, [id]: true }));
    try {
      const res = await axios.get(
        `${API}/payments-hub/admin/diagnostic/fee-breakdown-drift/inspect/${id}`,
        { withCredentials: true },
      );
      setInspectCache((p) => ({ ...p, [id]: res.data }));
    } catch (err) {
      const __e = formatAdminError(err, "Inspect failed");
      toast.error(__e.message, __e.opts);
      setInspectCache((p) => ({ ...p, [id]: { error: err.response?.data?.detail || String(err) } }));
    } finally {
      setInspectLoading((p) => ({ ...p, [id]: false }));
    }
  };

  const toggleExpand = (id) => {
    setExpanded((p) => {
      const next = !p[id];
      if (next) loadInspect(id);
      return { ...p, [id]: next };
    });
  };

  const downloadCsv = () => {
    if (!rows.length) return;
    const headers = ["Parent", "Email", "Term", "Total ($)", "Payment status", "Stored shape", "SCC verified", "Digital Member", "Registered", "Repairable"];
    const lines = [headers.map(_csvEscape).join(",")];
    for (const r of rows) {
      lines.push([
        r.parent_name || "",
        r.parent_email || "",
        r.term_name || r.term_id || "",
        Number(r.total_amount || 0).toFixed(2),
        r.payment_status || "",
        r.fee_breakdown_shape || "",
        r.parent_member_flags?.scc_member_verified ? "yes" : "no",
        r.parent_member_flags?.is_digital_member ? "yes" : "no",
        (r.created_at || "").slice(0, 10),
        r.repairable === false ? "no — term deleted" : "yes",
      ].map(_csvEscape).join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `fee-breakdown-drift-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="fb-drift-modal">
      <div className="bg-slate-950 border border-amber-500/30 rounded-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-slate-800">
          <div>
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" /> Fee-breakdown drift
              <Badge variant="outline" className="border-amber-500/40 text-amber-300 text-[10px]">read-only preview</Badge>
            </h2>
            <p className="text-xs text-slate-400 mt-0.5">
              Finds registrations where the parent is a verified member but the stored breakdown is missing the member discount. Click any row to inspect current vs proposed before repairing. <strong>total_amount is always preserved.</strong>
            </p>
          </div>
          <div className="flex items-center gap-2">
            {data && !isError && rows.length > 0 && (
              <Button onClick={downloadCsv} variant="outline" size="sm" className="border-amber-500/40 text-amber-300" data-testid="fb-drift-download-btn">
                <Download className="w-4 h-4 mr-1" /> CSV
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={onClose} data-testid="fb-drift-close-btn">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div className="flex-1 overflow-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-slate-400">
              <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Scanning member-verified parents for drift…
            </div>
          ) : isError ? (
            <div className="text-red-300 text-sm" data-testid="fb-drift-error">
              <div className="font-semibold mb-1">Could not load drift report</div>
              <div className="text-rose-200/80">{String(data.error)}</div>
            </div>
          ) : !hasPayload ? (
            <div className="text-slate-500 text-sm italic">No data returned. Try again.</div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <SummaryCard label="Drift rows" value={data.drift_count ?? 0} accent="amber" />
                <SummaryCard label="Legacy aggregate" value={(data.summary_by_shape && data.summary_by_shape.legacy_aggregate) || 0} sub="have aggregate fb" accent="slate" />
                <SummaryCard
                  label="Cannot repair"
                  value={unrepairableCount}
                  sub="term no longer exists"
                  accent={unrepairableCount > 0 ? "rose" : "slate"}
                />
                <SummaryCard label="Selected" value={selectedIds.length} sub={`of ${repairableRows.length} repairable`} accent="emerald" />
              </div>

              {repairResult && (
                <div className={`border ${repairResult.error ? "border-rose-500/40 bg-rose-500/5" : "border-emerald-500/40 bg-emerald-500/5"} rounded p-3 text-xs`}>
                  {repairResult.error ? (
                    <div className="text-rose-300">Repair failed: {repairResult.error}</div>
                  ) : (
                    <>
                      <div className="text-emerald-300 font-semibold">Repair complete</div>
                      <div className="text-slate-300 mt-1">Repaired: <strong>{repairResult.repaired}</strong> · Errors: <strong>{repairResult.errors?.length || 0}</strong>{(repairResult.skipped_not_in_drift?.length || 0) > 0 && <> · Skipped (already repaired by another tab): <strong>{repairResult.skipped_not_in_drift.length}</strong></>}</div>
                      {(repairResult.errors || []).length > 0 && (
                        <details className="mt-2">
                          <summary className="text-rose-300 cursor-pointer">{repairResult.errors.length} error(s)</summary>
                          <pre className="mt-1 text-[10px] text-rose-300 whitespace-pre-wrap">{JSON.stringify(repairResult.errors, null, 2)}</pre>
                        </details>
                      )}
                    </>
                  )}
                </div>
              )}

              {data.drift_count === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  No drift detected. All member-verified parents have stored breakdown with non-zero member discount.
                </div>
              ) : (
                <>
                  <div className="border border-slate-800 rounded overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-900/70 text-slate-400 text-left">
                        <tr>
                          <th className="px-2 py-2 w-8">
                            <input
                              type="checkbox"
                              checked={allRepairableSelected}
                              onChange={toggleAll}
                              disabled={repairableRows.length === 0}
                              className="accent-amber-500"
                              data-testid="fb-drift-select-all"
                              title={allRepairableSelected ? "Deselect all repairable" : "Select all repairable"}
                            />
                          </th>
                          <th className="px-2 py-2 w-8"></th>
                          <th className="px-2 py-2">Parent</th>
                          <th className="px-2 py-2">Term</th>
                          <th className="px-2 py-2 text-right">Total amount</th>
                          <th className="px-2 py-2">Status</th>
                          <th className="px-2 py-2">Stored shape</th>
                          <th className="px-2 py-2">Member flags</th>
                          <th className="px-2 py-2">Registered</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r, i) => {
                          const id = r.registration_id;
                          const isExpanded = !!expanded[id];
                          const isChecked = !!selected[id];
                          const inspect = inspectCache[id];
                          const isInspecting = !!inspectLoading[id];
                          return (
                            <React.Fragment key={id || i}>
                              <tr
                                className={`border-t border-slate-800 hover:bg-slate-900/30 cursor-pointer ${isExpanded ? "bg-slate-900/30" : ""}`}
                                data-testid={`fb-drift-row-${i}`}
                              >
                                <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => toggleRow(id)}
                                    disabled={r.repairable === false}
                                    className="accent-amber-500 disabled:opacity-30"
                                    data-testid={`fb-drift-checkbox-${i}`}
                                    title={r.repairable === false ? "Cannot repair — term no longer exists" : ""}
                                  />
                                </td>
                                <td className="px-2 py-2" onClick={() => toggleExpand(id)}>
                                  <button
                                    type="button"
                                    className="text-slate-400 hover:text-white"
                                    aria-label={isExpanded ? "Collapse" : "Inspect"}
                                    data-testid={`fb-drift-expand-${i}`}
                                  >
                                    {isExpanded ? "▾" : "▸"}
                                  </button>
                                </td>
                                <td className="px-2 py-2" onClick={() => toggleExpand(id)}>
                                  <div className="text-slate-200">{r.parent_name || "—"}</div>
                                  <div className="text-[10px] text-slate-500">{r.parent_email}</div>
                                </td>
                                <td className="px-2 py-2 text-slate-300" onClick={() => toggleExpand(id)}>
                                  <div>{r.term_name || r.term_id?.slice(0, 8) || "—"}</div>
                                  {r.repairable === false && (
                                    <div className="text-[9px] text-rose-300 mt-0.5" data-testid={`fb-drift-term-missing-${i}`}>
                                      ⚠ {r.unrepairable_reason || "cannot repair"}
                                    </div>
                                  )}
                                </td>
                                <td className="px-2 py-2 text-right text-slate-200" onClick={() => toggleExpand(id)}>${Number(r.total_amount || 0).toFixed(2)}</td>
                                <td className="px-2 py-2" onClick={() => toggleExpand(id)}><Badge variant="outline" className="border-slate-700 text-slate-400 text-[9px]">{r.payment_status}</Badge></td>
                                <td className="px-2 py-2" onClick={() => toggleExpand(id)}>
                                  <Badge variant="outline" className={`text-[9px] ${r.fee_breakdown_shape === "missing" ? "border-rose-500/40 text-rose-300" : r.fee_breakdown_shape === "legacy_aggregate" ? "border-amber-500/40 text-amber-300" : "border-slate-700 text-slate-400"}`}>
                                    {r.fee_breakdown_shape}
                                  </Badge>
                                </td>
                                <td className="px-2 py-2 text-[10px] text-slate-400" onClick={() => toggleExpand(id)}>
                                  {r.parent_member_flags?.scc_member_verified && <span className="mr-1 text-emerald-300">SCC✓</span>}
                                  {r.parent_member_flags?.is_digital_member && <span className="mr-1 text-cyan-300">DM</span>}
                                  {r.parent_member_flags?.is_scc_member && !r.parent_member_flags?.scc_member_verified && <span className="mr-1 text-slate-400">scc(unverified)</span>}
                                </td>
                                <td className="px-2 py-2 text-[10px] text-slate-500" onClick={() => toggleExpand(id)}>{(r.created_at || "").slice(0, 10)}</td>
                              </tr>
                              {isExpanded && (
                                <tr className="bg-slate-900/40 border-t border-slate-800" data-testid={`fb-drift-inspect-${i}`}>
                                  <td colSpan={9} className="p-0">
                                    <DriftInspectPanel inspect={inspect} loading={isInspecting} />
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {!repairResult && data.confirm_token && (
                    <div className="border border-amber-500/40 bg-amber-500/5 rounded p-3 flex items-center justify-between flex-wrap gap-2">
                      <div className="text-xs text-amber-200 flex-1 min-w-[260px]">
                        <strong>{selectedIds.length}</strong> of {rows.length} row{rows.length === 1 ? "" : "s"} selected for repair.
                        Click a row to inspect current vs proposed. <strong>total_amount is preserved</strong> on every selected row — only the breakdown metadata is rewritten.
                        <div className="text-[9px] text-amber-300/70 mt-1">Confirm token TTL: {Math.round(data.confirm_token_ttl_seconds / 60)} min</div>
                      </div>
                      <Button
                        onClick={() => onRepair(selectedIds)}
                        disabled={repairBusy || noneSelected}
                        className="bg-amber-600 hover:bg-amber-500 text-white disabled:opacity-40"
                        data-testid="fb-drift-repair-btn"
                      >
                        {repairBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-1" />}
                        Repair {selectedIds.length} selected
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DriftInspectPanel({ inspect, loading }) {
  if (loading) {
    return (
      <div className="px-6 py-4 text-xs text-slate-400 flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading current vs proposed breakdown…
      </div>
    );
  }
  if (!inspect) return null;
  if (inspect.error) {
    return <div className="px-6 py-3 text-xs text-rose-300">Failed to inspect: {String(inspect.error)}</div>;
  }
  const cur = inspect.current || {};
  const prop = inspect.proposed || {};
  const proposedUnavailable = !!prop.unavailable;
  const fmt = (v) => `$${Number(v || 0).toFixed(2)}`;
  const renderLineItems = (items) => {
    if (!items || items.length === 0) return <p className="text-[11px] text-slate-500 italic">No line items stored.</p>;
    return (
      <ul className="space-y-1">
        {items.map((li, idx) => {
          if (li.kind === "digital_membership") {
            return <li key={idx} className="text-[11px] text-cyan-200">+ {li.label || "Digital Membership"} — {fmt(li.amount)}</li>;
          }
          const sub = li.session_subtotal || li.fee || li.child_total || 0;
          const dpct = Number(li.discount_pct || 0);
          return (
            <li key={idx} className="text-[11px] text-slate-200">
              {li.child_name || `Child ${li.child_index || idx + 1}`} ·
              {" "}<span className="text-slate-400">{li.sessions_per_week ? `${li.sessions_per_week}/wk · ` : ""}</span>
              <span className="font-mono">{fmt(sub)}</span>
              {dpct > 0 && <span className="text-emerald-300"> · −{dpct}% member discount</span>}
              {(li.breakdown?.registration_fee || 0) > 0 && <span className="text-amber-300"> · +{fmt(li.breakdown.registration_fee)} reg fee</span>}
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <div className="px-6 py-4 grid md:grid-cols-2 gap-4 text-xs">
      {/* Current */}
      <div className="border border-rose-500/30 bg-rose-500/5 rounded p-3">
        <div className="text-rose-300 font-semibold mb-2 text-[11px] uppercase tracking-wide">
          Current stored · {cur.shape || "—"}
        </div>
        {renderLineItems(cur.line_items)}
        <div className="mt-2 pt-2 border-t border-rose-500/20 space-y-1 text-[11px]">
          <div className="flex justify-between"><span className="text-slate-400">Subtotal</span><span className="font-mono text-slate-200">{fmt(cur.subtotal)}</span></div>
          <div className="flex justify-between"><span className="text-slate-400">Registration fee</span><span className="font-mono text-slate-200">{fmt(cur.registration_fee)}</span></div>
          <div className="flex justify-between"><span className="text-slate-400">Digital Membership</span><span className="font-mono text-slate-200">{fmt(cur.digital_membership_price)}</span></div>
          <div className="flex justify-between"><span className="text-rose-300">Member discount</span><span className={`font-mono ${cur.member_discount_amount > 0 ? "text-emerald-300" : "text-rose-300"}`}>{cur.member_discount_amount > 0 ? `−${fmt(cur.member_discount_amount)}` : "$0.00 ← missing"}</span></div>
          <div className="flex justify-between text-slate-300 pt-1 border-t border-rose-500/20"><span>Math sum</span><span className="font-mono">{fmt(cur.math_sum)}</span></div>
          <div className="flex justify-between text-white"><span>Stored total</span><span className="font-mono">{fmt(cur.stored_total)}</span></div>
          {Math.abs(cur.math_gap || 0) > 0.01 && (
            <div className="flex justify-between text-rose-300 font-semibold"><span>Unexplained gap</span><span className="font-mono">{fmt(cur.math_gap)}</span></div>
          )}
        </div>
      </div>

      {/* Proposed */}
      <div className={`border rounded p-3 ${proposedUnavailable ? "border-slate-700 bg-slate-900/30" : "border-emerald-500/30 bg-emerald-500/5"}`}>
        {proposedUnavailable ? (
          <>
            <div className="text-slate-300 font-semibold mb-2 text-[11px] uppercase tracking-wide flex items-center gap-1">
              <AlertTriangle className="w-3 h-3 text-rose-400" /> Cannot repair this row
            </div>
            <p className="text-[11px] text-rose-300 mb-2">{prop.reason || "Proposed breakdown could not be computed."}</p>
            <p className="text-[11px] text-slate-400">
              This usually means the term this registration was linked to has been deleted from the system. The registration still carries its stored total <span className="font-mono text-slate-200">{fmt(inspect.total_preserved)}</span>, but a fresh fee calculation cannot run without the term's rate table.
            </p>
            <p className="text-[10px] text-slate-500 mt-2 italic">
              Options: leave as-is (parent already paid based on the original total), or manually correct the breakdown in the DB if you need an audit-clean record.
            </p>
          </>
        ) : (
          <>
            <div className="text-emerald-300 font-semibold mb-2 text-[11px] uppercase tracking-wide flex items-center justify-between">
              <span>Proposed rewrite · line_items</span>
              <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 text-[9px]">total preserved · {fmt(inspect.total_preserved)}</Badge>
            </div>
            {prop.source && (
              <div className="text-[10px] text-emerald-200/70 italic mb-2" data-testid="fb-drift-proposed-source">
                source: {prop.source}
              </div>
            )}
            {renderLineItems(prop.line_items)}
            <div className="mt-2 pt-2 border-t border-emerald-500/20 space-y-1 text-[11px]">
              <div className="flex justify-between"><span className="text-slate-400">Subtotal</span><span className="font-mono text-slate-200">{fmt(prop.subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Registration fee</span><span className="font-mono text-slate-200">{fmt(prop.registration_fee)}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Digital Membership</span><span className="font-mono text-slate-200">{fmt(prop.digital_membership_price)}</span></div>
              <div className="flex justify-between"><span className="text-emerald-300">Member discount</span><span className="font-mono text-emerald-300">{prop.member_discount_amount > 0 ? `−${fmt(prop.member_discount_amount)}` : "$0.00"}</span></div>
              <div className="flex justify-between text-slate-300 pt-1 border-t border-emerald-500/20"><span>Math sum</span><span className="font-mono">{fmt(prop.math_sum)}</span></div>
              <div className="flex justify-between text-white"><span>Stored total (preserved)</span><span className="font-mono">{fmt(prop.stored_total)}</span></div>
              {prop.would_recalculate_total_to !== prop.stored_total && (
                <div className="text-[10px] text-amber-300/80 italic pt-1">
                  ⓘ Fresh calc would have produced {fmt(prop.would_recalculate_total_to)} — the repair keeps your existing total intact.
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function AutoCancelStaleModal({ loading, data, busy, result, onRun, onClose, onDownload }) {
  const eligible = (data && Array.isArray(data.eligible)) ? data.eligible : [];
  const skipped = (data && Array.isArray(data.skipped)) ? data.skipped : [];
  const isError = !!(data && data.error);
  const hasPayload = !!(data && !data.error && typeof data.eligible_count === "number");
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="autocancel-stale-modal">
      <div className="bg-slate-950 border border-rose-500/30 rounded-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-slate-800">
          <div>
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <X className="w-5 h-5 text-rose-400" /> Auto-cancel stale pending registrations
              <Badge variant="outline" className="border-amber-500/40 text-amber-300 text-[10px]">{data?.threshold_days || 14} day threshold</Badge>
              <Badge variant="outline" className="border-slate-700 text-slate-400 text-[10px]">{data?.per_run_cap || 20}/run cap</Badge>
            </h2>
            <p className="text-xs text-slate-400 mt-0.5">
              Cancels pending registrations older than 14 days. Sends a courtesy email with re-register link to each parent.
              Skips rows where the parent has a paid registration in the same term, OR where any payment proof is attached.
              The scheduled task runs daily at 02:00 SGT — admin-button on-demand here for review.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {data && !isError && (
              <Button onClick={onDownload} variant="outline" size="sm" className="border-rose-500/40 text-rose-300" data-testid="autocancel-download-btn">
                <Download className="w-4 h-4 mr-1" /> JSON
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={onClose} data-testid="autocancel-close-btn">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div className="flex-1 overflow-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-slate-400">
              <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Scanning for stale pending registrations…
            </div>
          ) : isError ? (
            <div className="text-red-300 text-sm" data-testid="autocancel-error">
              <div className="font-semibold mb-1">Could not load auto-cancel preview</div>
              <div className="text-rose-200/80">{String(data.error)}</div>
              <div className="text-[10px] text-slate-500 mt-2">
                If you see "Not Found", the production server may be running an older build. Verify the route was deployed.
              </div>
            </div>
          ) : !hasPayload ? (
            <div className="text-slate-500 text-sm italic">No data returned. Try again.</div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <SummaryCard label="Eligible to cancel" value={data.eligible_count ?? 0} accent="rose" />
                <SummaryCard label="Skipped (paid/proof)" value={data.skipped_count ?? 0} accent="slate" sub="manual review" />
                <SummaryCard label="Per-run cap" value={data.per_run_cap ?? 20} accent="slate" />
                <SummaryCard label="Threshold" value={`${data.threshold_days ?? 14}d`} accent="amber" />
              </div>

              {data.autocancel_version && (
                <div className="text-[9px] text-slate-600 italic" data-testid="autocancel-version">autocancel_version: {data.autocancel_version}</div>
              )}

              {data.over_cap && (
                <div className="border border-amber-500/40 bg-amber-500/5 rounded p-3 text-xs text-amber-200">
                  <strong>⚠ Over cap</strong> — {data.over_cap_message} The scheduled task will NOT run. Use "Force over cap" to override here.
                </div>
              )}

              {result && (
                <div className={`border ${result.error ? "border-rose-500/40 bg-rose-500/5" : "border-emerald-500/40 bg-emerald-500/5"} rounded p-3 text-xs`}>
                  {result.error ? (
                    <div className="text-rose-300">Run failed: {result.error}</div>
                  ) : (
                    <>
                      <div className="text-emerald-300 font-semibold">Auto-cancel completed</div>
                      <div className="text-slate-300 mt-1">
                        Cancelled: <strong>{result.cancelled}</strong> of {result.eligible_total} ·
                        Errors: <strong>{result.errors?.length || 0}</strong> · Cap applied: <strong>{result.cap_applied}</strong>
                      </div>
                      {(result.errors || []).length > 0 && (
                        <details className="mt-2">
                          <summary className="text-rose-300 cursor-pointer">{result.errors.length} error(s)/warning(s)</summary>
                          <pre className="mt-1 text-[10px] text-rose-300 whitespace-pre-wrap">{JSON.stringify(result.errors, null, 2)}</pre>
                        </details>
                      )}
                    </>
                  )}
                </div>
              )}

              {eligible.length > 0 && (
                <div className="border border-rose-500/30 rounded overflow-hidden">
                  <div className="bg-rose-500/5 px-3 py-2 text-rose-300 text-xs font-semibold">Eligible ({eligible.length})</div>
                  <table className="w-full text-xs">
                    <thead className="bg-slate-900/70 text-slate-400 text-left">
                      <tr>
                        <th className="px-2 py-2">Parent</th>
                        <th className="px-2 py-2">Term</th>
                        <th className="px-2 py-2 text-right">Total</th>
                        <th className="px-2 py-2 text-right">Pending days</th>
                        <th className="px-2 py-2">Status</th>
                        <th className="px-2 py-2">Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {eligible.map((r, i) => (
                        <tr key={r.registration_id || i} className="border-t border-slate-800 hover:bg-slate-900/30" data-testid={`autocancel-eligible-${i}`}>
                          <td className="px-2 py-2">
                            <div className="text-slate-200">{r.parent_name || "—"}</div>
                            <div className="text-[10px] text-slate-500">{r.parent_email}</div>
                          </td>
                          <td className="px-2 py-2 text-slate-300">{r.term_name || (r.term_id || "").slice(0, 8)}</td>
                          <td className="px-2 py-2 text-right text-slate-200">${Number(r.total_amount || 0).toFixed(2)}</td>
                          <td className="px-2 py-2 text-right text-rose-300 font-semibold">{r.pending_days}</td>
                          <td className="px-2 py-2"><Badge variant="outline" className="border-slate-700 text-slate-400 text-[9px]">{r.payment_status}</Badge></td>
                          <td className="px-2 py-2 text-[10px] text-slate-500">{(r.created_at || "").slice(0, 10)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {skipped.length > 0 && (
                <details className="border border-slate-800 rounded overflow-hidden">
                  <summary className="bg-slate-900/50 px-3 py-2 text-slate-400 text-xs cursor-pointer">Skipped ({skipped.length}) — for manual review</summary>
                  <table className="w-full text-xs">
                    <thead className="bg-slate-900/70 text-slate-400 text-left">
                      <tr>
                        <th className="px-2 py-2">Parent</th>
                        <th className="px-2 py-2">Term</th>
                        <th className="px-2 py-2 text-right">Pending days</th>
                        <th className="px-2 py-2">Skip reasons</th>
                      </tr>
                    </thead>
                    <tbody>
                      {skipped.map((r, i) => (
                        <tr key={r.registration_id || i} className="border-t border-slate-800">
                          <td className="px-2 py-2">
                            <div className="text-slate-200">{r.parent_name || "—"}</div>
                            <div className="text-[10px] text-slate-500">{r.parent_email}</div>
                          </td>
                          <td className="px-2 py-2 text-slate-300">{r.term_name || (r.term_id || "").slice(0, 8)}</td>
                          <td className="px-2 py-2 text-right text-slate-300">{r.pending_days}</td>
                          <td className="px-2 py-2 text-[10px]">
                            {(r.skip_reasons || []).map((reason, ri) => (
                              <Badge key={ri} variant="outline" className="border-amber-500/40 text-amber-300 text-[9px] mr-1">{reason}</Badge>
                            ))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </details>
              )}

              {!result && eligible.length > 0 && (
                <div className="border border-rose-500/40 bg-rose-500/5 rounded p-3 flex items-center justify-between">
                  <div className="text-xs text-rose-200">
                    Will cancel up to {Math.min(eligible.length, data.per_run_cap)} row(s) and send courtesy email per cancellation.
                    {data.over_cap && <span className="text-amber-300 ml-2">⚠ {eligible.length} exceeds cap — use Force.</span>}
                  </div>
                  <div className="flex gap-2 ml-3">
                    {data.over_cap && (
                      <Button onClick={() => onRun(true)} disabled={busy} variant="outline" className="border-amber-500/40 text-amber-200" data-testid="autocancel-force-btn">
                        Force {eligible.length}
                      </Button>
                    )}
                    <Button onClick={() => onRun(false)} disabled={busy || data.over_cap} className="bg-rose-600 hover:bg-rose-500 text-white" data-testid="autocancel-run-btn">
                      {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <X className="w-4 h-4 mr-1" />}
                      Cancel {Math.min(eligible.length, data.per_run_cap)}
                    </Button>
                  </div>
                </div>
              )}

              {eligible.length === 0 && !result && (
                <div className="text-center py-8 text-slate-400">
                  No stale pending registrations to cancel. {skipped.length > 0 ? `(${skipped.length} skipped — see expandable section above.)` : ""}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


